<template>
  <div
    class="coordinates-map-wrapper"
    :class="{ 'coordinates-map-wrapper--popup': layout === 'popup' }"
  >
    <div class="map-container-wrapper">
      <div
        ref="googleMap"
        class="coordinates-google-map"
      />
      <egypt-gis-panel
        ref="egyptGisPanel"
        :blocked="egyptGisInteractionBlocked"
        @load-state="onEgyptGisLoadState"
      />
      <map-float-panel
        v-if="showLayerStatusPanel"
        panel-id="gis-layer-status"
        panel-class="map-layer-status-panel"
      >
        <template #title>
          تحميل طبقات الخريطة
        </template>
        <div class="map-layer-status-panel__list">
          <div
            v-for="item in activeLayerLoadItems"
            :key="item.key"
            class="map-layer-status-item"
            :class="{
              'map-layer-status-item--loading': isLayerLoading(item.key),
              'map-layer-status-item--loaded': isLayerLoaded(item.key),
              'map-layer-status-item--error': isLayerErrored(item.key),
              'map-layer-status-item--clickable': canRequestLayerLoad(item.key),
            }"
            @click="onLayerLoadItemClick(item)"
          >
            <b-spinner
              v-if="isLayerLoading(item.key)"
              small
              variant="primary"
              class="map-layer-status-item__icon"
            />
            <span
              v-else-if="isLayerLoaded(item.key)"
              class="map-layer-status-item__icon map-layer-status-item__icon--success"
              aria-hidden="true"
            >✓</span>
            <span
              v-else-if="isLayerErrored(item.key)"
              class="map-layer-status-item__icon map-layer-status-item__icon--error"
              aria-hidden="true"
            >!</span>
            <span
              v-else
              class="map-layer-status-item__icon map-layer-status-item__icon--pending"
              aria-hidden="true"
            >•</span>
            <span class="map-layer-status-item__label">{{ item.title }}</span>
          </div>
        </div>
      </map-float-panel>
    </div>

    <b-modal
      id="admin-boundaries-upload-google"
      v-model="showAdminBoundariesUploadModal"
      title="رفع الحدود الإدارية"
      hide-footer
      @hide="resetAdminBoundariesUploadForm"
    >
      <b-form @submit.prevent="submitAdminBoundariesUpload">
        <b-form-group
          label="ملف الحدود (ZIP / KMZ / KML)"
          label-for="admin-boundaries-upload-google-file"
        >
          <b-form-file
            id="admin-boundaries-upload-google-file"
            v-model="adminBoundariesUploadForm.file"
            accept=".zip,.kmz,.kml,application/zip,application/vnd.google-earth.kmz,application/vnd.google-earth.kml+xml,application/octet-stream"
            :state="Boolean(adminBoundariesUploadForm.file)"
            placeholder="اختر ملف ZIP أو KMZ أو KML أو اسحبه هنا"
            drop-placeholder="اسحب الملف هنا..."
          />
        </b-form-group>
        <b-form-group
          label="نوع الطبقة"
          label-for="admin-boundaries-upload-google-type"
        >
          <b-form-select
            id="admin-boundaries-upload-google-type"
            v-model="adminBoundariesUploadForm.type"
            :options="adminBoundaryTypeSelectOptions"
            :state="Boolean(adminBoundariesUploadForm.type)"
            required
          />
        </b-form-group>
        <b-form-group
          label="السنة"
          label-for="admin-boundaries-upload-google-year"
        >
          <b-form-input
            id="admin-boundaries-upload-google-year"
            v-model.number="adminBoundariesUploadForm.year"
            type="number"
            min="1900"
            max="2100"
          />
        </b-form-group>
        <div class="d-flex justify-content-end flex-wrap">
          <b-button
            variant="outline-secondary"
            class="mr-50"
            :disabled="adminBoundariesUploading"
            @click="showAdminBoundariesUploadModal = false"
          >
            إلغاء
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="adminBoundariesUploading || !adminBoundariesUploadForm.file || !adminBoundariesUploadForm.type"
          >
            <b-spinner
              v-if="adminBoundariesUploading"
              small
              class="mr-50"
            />
            رفع
          </b-button>
        </div>
      </b-form>
    </b-modal>
    <b-modal
      :id="savePolygonChoiceModalId"
      v-model="showSavePolygonChoiceModal"
      size="sm"
      centered
      static
      hide-header
      hide-footer
      modal-class="save-polygon-choice-modal"
      @shown="bringSavePolygonModalAboveFullscreen"
    >
      <p class="save-polygon-choice-modal__msg">
        تم إكمال رسم المضلع. أين تريد حفظه؟
      </p>
      <p
        v-if="lastDrawnPolygonAreaText"
        class="save-polygon-choice-modal__area"
      >
        {{ lastDrawnPolygonAreaText }}
      </p>
      <div class="save-polygon-choice-modal__actions">
        <b-button
          size="sm"
          variant="primary"
          :disabled="savingCoordinates"
          @click="onSavePolygonToCurrentLocation"
        >
          <b-spinner
            v-if="savingCoordinates"
            small
            class="mr-25"
          />
          القطعة الحالية
        </b-button>
        <b-button
          v-if="enableSaveAsNewLocation"
          size="sm"
          variant="outline-primary"
          :disabled="savingCoordinates"
          @click="onSavePolygonToNewLocation"
        >
          قطعة جديدة
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="savingCoordinates"
          @click="showSavePolygonChoiceModal = false"
        >
          لاحقاً
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>

import html2canvas from 'html2canvas'
import {
  BSpinner,
  BModal,
  BForm,
  BFormGroup,
  BFormFile,
  BFormInput,
  BFormSelect,
  BButton,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import geodesiaApi from '@/api/geodesia/geodesia'
import MapFloatPanel from '@/views/modules/projects/Components/MapFloatPanel.vue'
import EgyptGisPanel from '@/views/modules/gis/EgyptGisPanel.vue'
import { fetchMapAllDataShared } from '@/utils/mapAllDataCache'
import { getSurveyPointGoogleIcon } from '@/utils/surveyPointColors'
import {
  GEODESIA_LAYER_ERROR_MESSAGE,
  GEODESIA_LAYER_SOURCE,
  GEODESIA_LAYER_TRUNCATED_MESSAGE,
  GEODESIA_MAP_LAYER_KEYS,
  createGeodesiaLayerCancelSource,
  geodesiaLayerErrorMessage,
  getGeodesiaLayerFeatures,
  isGeodesiaLayerRequestCanceled,
  isGeodesiaLayerTruncated,
  unwrapGeodesiaLayerPayload,
} from '@/utils/geodesiaMapLayers'
import { importGoogleMapsLibrary, loadGoogleMapsScript } from '@/utils/leafletLoader'
import {
  DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
  ADMINISTRATIVE_BOUNDARY_TYPES,
  MATCHED_ADMINISTRATIVE_BOUNDARY_GOOGLE_STYLE,
  administrativeBoundaryLayerKey,
  createAdministrativeBoundaryGoogleStyleResolver,
  createAdministrativeBoundaryVisibility,
  fetchAdministrativeBoundariesGeoJson,
  fetchAdministrativeBoundaryTypes,
  buildAdministrativeBoundaryPopupHtml,
  flattenAdministrativeBoundaryAttributes,
  getAdministrativeBoundaryLayerLoadItems,
  getBoundaryTypeMeta,
  isAdministrativeBoundaryLayerKey,
  normalizeAdministrativeBoundaryItems,
  ringsFromGeoJsonGeometry,
  typeFromAdministrativeBoundaryLayerKey,
  uploadAdministrativeBoundaries,
} from '@/utils/administrativeBoundaries'

/** Safe iteration — MVCArray may lack Array.prototype.forEach in some load states. */
function iterateLatLngPath(path, callback) {
  if (!path || typeof path.getLength !== 'function') return
  const len = path.getLength()
  for (let i = 0; i < len; i += 1) {
    const latLng = path.getAt(i)
    if (latLng) callback(latLng, i)
  }
}

const PARCEL_POLYGON_STYLE = {
  strokeColor: '#0080FF',
  strokeOpacity: 1,
  strokeWeight: 2,
  fillColor: '#0080FF',
  fillOpacity: 0.35,
}

const OTHER_LOCATION_PALETTE = ['#FF8C00', '#9C27B0', '#2E7D32', '#C62828', '#5D4037', '#00838F']

const MEASURE_DISTANCE_STYLE = {
  strokeColor: '#E65100',
  strokeOpacity: 0.95,
  strokeWeight: 3,
  clickable: false,
}

const MEASURE_AREA_STYLE = {
  strokeColor: '#6A1B9A',
  strokeOpacity: 0.95,
  strokeWeight: 2,
  fillColor: '#6A1B9A',
  fillOpacity: 0.2,
  clickable: false,
}

const MEASURE_MARKER_ICON = {
  fillColor: '#E65100',
  fillOpacity: 1,
  strokeColor: '#fff',
  strokeWeight: 1,
}

const MEASURE_LABEL_OFFSET_METERS = 5
const SNAP_THRESHOLD_PX = 14
const CLOSE_SNAP_THRESHOLD_PX = 28
const CLOSE_SNAP_THRESHOLD_PX_TOUCH = 44
const DRAW_POINT_COLOR = '#00A86B'
const SNAP_POINT_COLOR = '#FFC107'
const SNAP_EFFECT_BOUNCE_MS = 650
const SKETCH_CIRCLE_SIDES = 64
const DRAW_HISTORY_MAX = 40

const MAP_LAYER_LOAD_ITEMS = [
  { key: 'locationPolygons', title: 'المشاريع القريبة', always: true },
  { key: 'projects', title: 'المشاريع' },
  { key: 'mapLibraries', title: 'مكتبات الخرائط' },
  { key: 'fixedPoints', title: 'النقاط الثابتة' },
  ...getAdministrativeBoundaryLayerLoadItems(),
  { key: 'jxlCoordinates', title: 'إحداثيات JXL' },
  { key: 'jxlRw5Coordinates', title: 'إحداثيات RW5' },
  { key: 'jxlRawCoordinates', title: 'إحداثيات RAW' },
  { key: 'jxlPolygons', title: 'مضلعات JXL' },
  { key: 'jxlBaselineLines', title: 'خطوط الأساس (JXL)' },
  { key: 'vrsCoordinates', title: 'إحداثيات VRS' },
  { key: 'egyptGis', title: 'قاعدة بيانات مصر', always: true },
]

const SKETCH_LAYER_ITEMS = [
  { key: 'parcelDimensions', title: 'أبعاد المضلع' },
  { key: 'userLocation', title: 'موقعي الحالي' },
  { key: 'projects', title: 'المشاريع' },
  { key: 'mapLibraries', title: 'مكتبات الخرائط' },
  { key: 'locationPolygons', title: 'المشاريع القريبة' },
  { key: 'fixedPoints', title: 'النقاط الثابتة' },
  ...getAdministrativeBoundaryLayerLoadItems(),
  { key: 'vrsCoordinates', title: 'إحداثيات VRS' },
  { key: 'jxlCoordinates', title: 'إحداثيات JXL' },
  { key: 'jxlRw5Coordinates', title: 'إحداثيات RW5' },
  { key: 'jxlRawCoordinates', title: 'إحداثيات RAW' },
  { key: 'jxlPolygons', title: 'مضلعات JXL' },
  { key: 'jxlBaselineLines', title: 'خطوط الأساس (JXL)' },
]

function createInitialLayerLoadState() {
  return MAP_LAYER_LOAD_ITEMS.reduce((state, item) => {
    state[item.key] = { loading: false, loaded: false, error: null }
    return state
  }, {})
}

function sketchToolSvg(paths) {
  return '<span class="google-sketch-tool__icon" aria-hidden="true">'
    + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
    + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
    + paths
    + '</svg></span>'
}

const SELECT_TOOL_MODES = ['select', 'rectangle-select', 'lasso-select']

/** Calcite 16px paths used by ArcGIS Sketch create tools (pin, line, polygon, rectangle, circle). */
const ESRI_SKETCH_TOOL_ICON_PATHS = {
  select: 'M1.007 1.007v11.313l3.182-3.182 2.475 6.01 2.121-.875-2.475-6.01h4.596z',
  'rectangle-select': 'M2 2h3v1H3v2H2zm9 0h3v1h-2v2h-1zM2 10h1v2h2v1H2zm9 3h3v-1h-2v-2h-1zM5 5h6v6H5z',
  'lasso-select': 'M6.5 1.5c-2.8 0-5 2.1-5 5.1 0 4.2 4.6 7.9 6.5 9.4 1.9-1.5 6.5-5.2 6.5-9.4 0-3-2.2-5.1-5-5.1-1 0-1.8.3-2.5.8A3.7 3.7 0 0 0 6.5 1.5zM8 5.2a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6z',
  point: 'M8 0a5.5 5.5 0 0 0-5.5 5.5C2.5 9.9 8 16 8 16s5.5-6.1 5.5-10.5A5.5 5.5 0 0 0 8 0zm0 7.5a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  polyline: 'M13.207.793a1 1 0 0 1 0 1.414L2.207 13.207a1 1 0 1 1-1.414-1.414L11.793.793a1 1 0 0 1 1.414 0z',
  polygon: 'M12.9 15H3.1L0 6.5 8 0l8 6.5zM3.9 13.5h8.2L14.6 7 8 1.7 1.4 7z',
  rectangle: 'M15 15H1V1h14zm-1-1V2H2v12z',
  circle: 'M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5A5.5 5.5 0 1 1 8 13.5 5.5 5.5 0 0 1 8 2.5z',
}

function esriSketchToolIcon(mode) {
  const pathD = ESRI_SKETCH_TOOL_ICON_PATHS[mode]
  return '<span class="google-sketch-tool__icon" aria-hidden="true">'
    + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">'
    + `<path fill="currentColor" d="${pathD}"/>`
    + '</svg></span>'
}

/** Supported satellite imagery vintages (year shown in map attribution). */
const IMAGERY_YEAR_OPTIONS = [2020, 2023, 2026]
const DEFAULT_IMAGERY_YEAR = 2026
const WAYBACK_CATALOG_URL =
  'https://wayback.maptiles.arcgis.com/arcgis/rest/services/World_Imagery/MapServer?f=json'
/**
 * Wayback fallback release ids ("M" from Esri catalog) when catalog fetch fails.
 * 2020-10-14 → 119; 2023-10-11 → 1034 (closest to ~15/10 targets).
 */
const WAYBACK_RELEASE_FALLBACK = {
  2020: 119,
  2023: 1034,
}
const WAYBACK_TILE_URL_TEMPLATE =
  'https://wayback.maptiles.arcgis.com/arcgis/rest/services/World_Imagery/WMTS/1.0.0/default028mm/MapServer/tile/{releaseId}/{z}/{y}/{x}'
const WAYBACK_TARGET_DATE_BY_YEAR = {
  2020: '2020-10-15',
  2023: '2023-10-15',
}

const waybackReleaseCache = {
  promise: null,
  byYear: {},
}

function buildWaybackTileUrl(releaseId, coord, zoom) {
  if (!releaseId || !coord || zoom < 0 || zoom > 23) return null
  return WAYBACK_TILE_URL_TEMPLATE
    .replace('{releaseId}', String(releaseId))
    .replace('{z}', String(zoom))
    .replace('{y}', String(coord.y))
    .replace('{x}', String(coord.x))
}

function pickWaybackReleaseId(selection, year) {
  const yearPrefix = `World Imagery (Wayback ${year}-`
  const matches = selection.filter(item => String(item?.Name || '').startsWith(yearPrefix))
  if (!matches.length) return null

  const targetDate = WAYBACK_TARGET_DATE_BY_YEAR[year]
  if (targetDate) {
    const targetTs = new Date(targetDate).getTime()
    let bestMatch = matches[0]
    let bestDelta = Infinity
    matches.forEach(item => {
      const dateMatch = String(item?.Name || '').match(/(\d{4}-\d{2}-\d{2})/)
      if (!dateMatch) return
      const delta = Math.abs(new Date(dateMatch[1]).getTime() - targetTs)
      if (delta < bestDelta) {
        bestDelta = delta
        bestMatch = item
      }
    })
    const releaseId = Number(bestMatch?.M)
    return Number.isFinite(releaseId) && releaseId > 0 ? releaseId : null
  }

  const releaseId = Number(matches[0]?.M)
  return Number.isFinite(releaseId) && releaseId > 0 ? releaseId : null
}

async function fetchWaybackReleaseForYear(year) {
  const numericYear = Number(year)
  if (waybackReleaseCache.byYear[numericYear]) {
    return waybackReleaseCache.byYear[numericYear]
  }

  if (!waybackReleaseCache.promise) {
    waybackReleaseCache.promise = fetch(WAYBACK_CATALOG_URL)
      .then(response => (response.ok ? response.json() : null))
      .then(payload => (Array.isArray(payload?.Selection) ? payload.Selection : []))
      .catch(() => [])
  }

  const selection = await waybackReleaseCache.promise
  const releaseId = pickWaybackReleaseId(selection, numericYear)
    || WAYBACK_RELEASE_FALLBACK[numericYear]
    || null

  if (releaseId) {
    waybackReleaseCache.byYear[numericYear] = releaseId
  }
  return releaseId
}

/** html2canvas cannot read WebGL tile layers that use CSS transforms — flatten them first. */
function prepareGoogleMapDomForCapture(container) {
  const restores = []
  if (!container) return () => {}

  const tilePaneSelectors = [
    '.gm-style > div:first-child > div:first-child > div:last-child > div',
    '.gm-style > div:first-child > div:first-child > div:last-child',
  ]

  tilePaneSelectors.forEach(selector => {
    container.querySelectorAll(selector).forEach(el => {
      const computed = window.getComputedStyle(el)
      const transform = computed.transform
      if (!transform || transform === 'none') return

      const matrixMatch = transform.match(/matrix(?:3d)?\(([^)]+)\)/)
      if (!matrixMatch) return

      const parts = matrixMatch[1].split(',').map(value => parseFloat(value.trim()))
      if (parts.length < 6) return

      restores.push({
        el,
        transform: el.style.transform,
        left: el.style.left,
        top: el.style.top,
        position: el.style.position,
      })

      el.style.transform = 'none'
      el.style.left = `${parts[4]}px`
      el.style.top = `${parts[5]}px`
      if (!el.style.position || el.style.position === 'static') {
        el.style.position = 'absolute'
      }
    })
  })

  return () => {
    restores.forEach(({ el, transform, left, top, position }) => {
      el.style.transform = transform
      el.style.left = left
      el.style.top = top
      el.style.position = position
    })
  }
}

function waitForGoogleMapIdle(map) {
  return new Promise(resolve => {
    if (!map || !window.google?.maps?.event) {
      resolve()
      return
    }
    window.google.maps.event.addListenerOnce(map, 'idle', resolve)
    setTimeout(resolve, 3000)
  })
}

/** Upscale to a minimum size while keeping the map centered (no top-left offset). */
function centerCanvasOnMinSize(sourceCanvas, minWidth = 1920, minHeight = 1080) {
  const srcW = sourceCanvas.width
  const srcH = sourceCanvas.height
  if (srcW >= minWidth && srcH >= minHeight) {
    return sourceCanvas
  }

  const targetW = Math.max(minWidth, srcW)
  const targetH = Math.max(minHeight, srcH)
  const output = document.createElement('canvas')
  output.width = targetW
  output.height = targetH

  const ctx = output.getContext('2d')
  if (!ctx) return sourceCanvas

  ctx.fillStyle = '#e5e3df'
  ctx.fillRect(0, 0, targetW, targetH)

  const fitScale = Math.min(targetW / srcW, targetH / srcH)
  const drawW = Math.round(srcW * fitScale)
  const drawH = Math.round(srcH * fitScale)
  const offsetX = Math.round((targetW - drawW) / 2)
  const offsetY = Math.round((targetH - drawH) / 2)

  ctx.drawImage(sourceCanvas, offsetX, offsetY, drawW, drawH)
  return output
}

export default {
  name: 'CoordinatesMapGoogle',
  components: {
    BSpinner,
    BModal,
    BForm,
    BFormGroup,
    BFormFile,
    BFormInput,
    BFormSelect,
    BButton,
    MapFloatPanel,
    EgyptGisPanel,
  },
  props: {
    coordinates: {
      type: Array,
      required: true,
    },
    locationId: {
      type: [Number, String],
      default: null,
    },
    coordinatesType: {
      type: String,
      default: 'technical_office',
    },
    enableAllMapData: {
      type: Boolean,
      default: true,
    },
    otherProjectLocations: {
      type: Array,
      default: () => [],
    },
    layout: {
      type: String,
      default: 'inline',
      validator: v => ['inline', 'popup'].includes(v),
    },
    /** Initial satellite imagery vintage — 2026 = current Google hybrid; 2020/2023 = Wayback archives. */
    defaultImageryYear: {
      type: [Number, String],
      default: DEFAULT_IMAGERY_YEAR,
      validator: v => IMAGERY_YEAR_OPTIONS.includes(Number(v)),
    },
    /** Show map control to save drawn polygon as a new project location (GISTab). */
    enableSaveAsNewLocation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      homeButton: null,
      savePolygonButton: null,
      saveAsNewLocationButton: null,
      adminBoundariesUploadButton: null,
      showAdminBoundariesUploadModal: false,
      adminBoundariesUploading: false,
      adminBoundaryTypes: ADMINISTRATIVE_BOUNDARY_TYPES.map(item => ({ ...item })),
      adminBoundariesUploadForm: {
        file: null,
        type: 'governorate',
        year: DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
      },
      locationLoading: false,
      loadingMapData: false,
      map: null,
      geometrySpherical: null,
      polygonDrawActive: false,
      polygonDrawPoints: [],
      polygonDrawMarkers: [],
      polygonDrawPreviewLine: null,
      polygonDrawCloseLine: null,
      polygonDrawFirstVertexListener: null,
      finishPolygonButton: null,
      mapClickListener: null,
      polygonDrawDblClickListener: null,
      polygonDrawPreviousDoubleClickZoom: null,
      drawPolygonButton: null,
      sketchMode: null,
      selectAnchorPoint: null,
      selectLassoPath: [],
      selectedOverlays: [],
      selectGraphicListeners: [],
      deleteSelectionButton: null,
      undoDrawButton: null,
      redoDrawButton: null,
      drawHistoryPanel: null,
      drawHistoryFab: null,
      drawHistoryPanelOpen: true,
      drawHistoryPanelHidden: false,
      drawHistoryMediaQuery: null,
      drawHistory: [],
      drawHistoryIndex: -1,
      suppressDrawHistory: false,
      pathHistoryTimer: null,
      skipPathHistory: false,
      parcelDragListeners: [],
      moveVertexMarkers: [],
      moveVertexListeners: [],
      moveDragStartCoords: null,
      moveGrabVertexIndex: -1,
      sketchAnchorPoint: null,
      sketchPreviewOverlay: null,
      sketchMoveListener: null,
      sketchToolbar: null,
      sketchLayersPanel: null,
      sketchLayersPanelOpen: false,
      sketchToolButtons: {},
      measureMode: null,
      measurePoints: [],
      measureMarkers: [],
      measurePreviewOverlay: null,
      measureLiveLabelControl: null,
      measureCursorPoint: null,
      measureOverlays: [],
      measureClickListener: null,
      measureDblClickListener: null,
      measureMouseMoveListener: null,
      measurePreviousDoubleClickZoom: null,
      measureDistanceButton: null,
      measureAreaButton: null,
      mapInitPromise: null,
      mapInitAborted: false,
      visibilityCheckInterval: null,
      resizeObserver: null,
      onWindowResize: null,
      parcelPolygon: null,
      committedParcelPolygon: null,
      preservedCommittedParcel: false,
      parcelPathListeners: [],
      parcelClickListener: null,
      pendingPolygonCoordinates: [],
      showSavePolygonChoiceModal: false,
      lastDrawnPolygonAreaText: '',
      savePolygonChoiceModalId: `save-polygon-choice-google-${Math.random().toString(36).slice(2, 10)}`,
      suppressPropRedrawUntil: 0,
      savingCoordinates: false,
      currentCoordinates: {
        lat: 31.2001,
        lng: 29.9187,
      },
      administrativeBoundariesYear: DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
      layerVisibility: {
        parcelDimensions: true,
        projects: false,
        mapLibraries: false,
        locationPolygons: false,
        fixedPoints: false,
        ...createAdministrativeBoundaryVisibility(false),
        vrsCoordinates: false,
        jxlCoordinates: false,
        jxlRw5Coordinates: false,
        jxlRawCoordinates: false,
        jxlPolygons: false,
        jxlBaselineLines: false,
        userLocation: true,
      },
      layerLoadState: createInitialLayerLoadState(),
      geodesiaLayerCancels: {},
      overlayGroups: {
        otherProjectLocations: [],
        locationPolygons: [],
        projects: [],
        mapLibraries: [],
        fixedPoints: [],
        ...ADMINISTRATIVE_BOUNDARY_TYPES.reduce((acc, item) => {
          acc[administrativeBoundaryLayerKey(item.type)] = []
          return acc
        }, {}),
        matchedAdministrativeBoundary: [],
        vrsCoordinates: [],
        jxlCoordinates: [],
        jxlRw5Coordinates: [],
        jxlRawCoordinates: [],
        jxlPolygons: [],
        jxlBaselineLines: [],
        userLocation: [],
        importedPoints: [],
        sketchGraphics: [],
      },
      userLocationMarker: null,
      imageryYear: DEFAULT_IMAGERY_YEAR,
      imageryYearControl: null,
      fullscreenButton: null,
      adminBoundaryLayerControl: null,
      mapTypeChangedListener: null,
      waybackOverlayLayer: null,
      waybackOverlayYear: null,
      syncingImageryMapType: false,
      geodesiaPointInfoWindow: null,
      overlayInfoWindow: null,
      parcelDimensionOverlays: [],
      mapPointClickListener: null,
    }
  },
  computed: {
    resolvedLocationId() {
      const id = this.locationId
      if (id === null || id === undefined || id === '') return null
      const numericId = Number(id)
      return Number.isFinite(numericId) && numericId > 0 ? numericId : null
    },
    canSaveDrawnPolygon() {
      return this.pendingPolygonCoordinates.length >= 3 && !this.savingCoordinates
    },
    canViewEgyptGis() {
      try {
        return typeof this.hasPermission === 'function'
          && this.hasPermission('view gis layers')
      } catch (e) {
        return false
      }
    },
    egyptGisInteractionBlocked() {
      return Boolean(this.polygonDrawActive || this.measureMode || this.sketchMode)
    },
    canViewAdministrativeBoundaries() {
      try {
        return typeof this.hasPermission === 'function'
          && this.hasPermission('view administrative_boundaries')
      } catch (e) {
        return false
      }
    },
    canUploadAdministrativeBoundaries() {
      try {
        return typeof this.hasPermission === 'function'
          && this.hasPermission('upload administrative_boundaries')
      } catch (e) {
        return false
      }
    },
    adminBoundaryTypeSelectOptions() {
      return [
        { value: null, text: 'اختر نوع الطبقة', disabled: true },
        ...this.adminBoundaryTypes.map(item => ({
          value: item.type,
          text: item.title,
        })),
      ]
    },
    layerLoadItems() {
      let items = !this.enableAllMapData
        ? MAP_LAYER_LOAD_ITEMS.filter(item => item.always)
        : MAP_LAYER_LOAD_ITEMS
      if (!this.canViewAdministrativeBoundaries) {
        items = items.filter(item => !isAdministrativeBoundaryLayerKey(item.key))
      }
      if (!this.canViewEgyptGis) {
        items = items.filter(item => item.key !== 'egyptGis')
      }
      return items
    },
    activeLayerLoadItems() {
      return this.layerLoadItems.filter(item => (
        this.isLayerLoading(item.key) || this.isLayerErrored(item.key)
      ))
    },
    showLayerStatusPanel() {
      return this.activeLayerLoadItems.length > 0
    },
    sketchLayerItems() {
      if (this.canViewAdministrativeBoundaries) return SKETCH_LAYER_ITEMS
      return SKETCH_LAYER_ITEMS.filter(item => !isAdministrativeBoundaryLayerKey(item.key))
    },
    projectId() {
      return this.$route.params.id
    },
    ...mapGetters({
      storeLocationPolygons: 'projects/locationPolygons',
    }),
  },
  watch: {
    canViewEgyptGis(value) {
      if (value && this.map) this.mountEgyptGis()
    },
    coordinates: {
      handler(newVal) {
        if (Date.now() < this.suppressPropRedrawUntil) {
          return
        }
        const incoming = this.normalizeCoordinatesList(newVal)
        if (
          incoming.length >= 3
          && this.pendingPolygonCoordinates.length >= 3
          && this.areCoordinatesEqual(incoming, this.pendingPolygonCoordinates)
        ) {
          return
        }
        if (this.preservedCommittedParcel) {
          return
        }
        this.drawPolygon(incoming.length >= 3 ? incoming : null)
      },
      deep: true,
    },
    locationId() {
      this.suppressPropRedrawUntil = 0
      this.teardownCommittedParcelPolygon()
      this.drawPolygon()
    },
    otherProjectLocations: {
      handler() {
        this.drawOtherProjectLocations()
        if (!this.parcelPolygon?.getPath) {
          this.$nextTick(() => this.focusCurrentPolygon())
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.mapInitAborted = false
    this.$nextTick(() => {
      this.initMap()
    })
  },
  beforeDestroy() {
    this.releaseEgyptGis()
    this.mapInitAborted = true
    this.cancelAllGeodesiaLayerRequests()
    this.teardownParcelPolygon()
    this.teardownCommittedParcelPolygon()
    this.clearOverlayGroup('otherProjectLocations')
    this.clearOverlayGroup('locationPolygons')
    this.clearOverlayGroup('projects')
    this.clearOverlayGroup('mapLibraries')
    this.clearOverlayGroup('fixedPoints')
    this.clearOverlayGroup('vrsCoordinates')
    this.clearOverlayGroup('jxlCoordinates')
    this.clearOverlayGroup('jxlRw5Coordinates')
    this.clearOverlayGroup('jxlRawCoordinates')
    this.clearOverlayGroup('jxlPolygons')
    this.clearOverlayGroup('jxlBaselineLines')
    this.clearOverlayGroup('matchedAdministrativeBoundary')
    this.clearImportedPoints()
    this.clearOverlayGroup('sketchGraphics')
    ADMINISTRATIVE_BOUNDARY_TYPES.forEach(item => {
      this.clearOverlayGroup(administrativeBoundaryLayerKey(item.type))
    })
    this.clearUserLocationMarker()

    this.stopSketchTool()
    this.stopPolygonDrawMode()
    this.stopMeasureMode()
    this.clearSelection()
    this.unbindSelectionKeyboard()
    this.clearDrawHistoryTimers()
    this.unbindDrawHistoryMediaQuery()
    this.detachDrawHistoryOverlay()
    this.unbindDomFloatPanels()
    this.disableParcelMoveMode()
    this.clearAllMeasurements()
    this.clearParcelDimensions()
    this.closeOverlayInfoWindow()
    this.teardownMapPointClickListener()

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize)
      window.removeEventListener('orientationchange', this.onWindowResize)
      this.onWindowResize = null
    }
    if (this.visibilityCheckInterval) {
      clearInterval(this.visibilityCheckInterval)
      this.visibilityCheckInterval = null
    }
    if (this.mapTypeChangedListener && window.google?.maps?.event) {
      window.google.maps.event.removeListener(this.mapTypeChangedListener)
      this.mapTypeChangedListener = null
    }
    this.removeWaybackOverlay()

    this.unbindFullscreenListeners()
    this.map = null
  },
  methods: {
    mountEgyptGis() {
      this.$nextTick(() => {
        const panel = this.$refs.egyptGisPanel
        if (!panel || !this.map) return
        panel.attachGoogle(this.map, {
          isBlocked: () => Boolean(this.polygonDrawActive || this.measureMode || this.sketchMode),
        })
      })
    },
    releaseEgyptGis() {
      const panel = this.$refs.egyptGisPanel
      if (panel && typeof panel.releaseMap === 'function') panel.releaseMap()
    },
    onEgyptGisLoadState({ loading, error } = {}) {
      if (loading) {
        this.beginLayerLoad(['egyptGis'])
        return
      }
      this.finishLayerLoad('egyptGis', {
        success: !error,
        errorMessage: error || null,
        notifyOnError: false,
      })
    },
    async waitForMapContainer(maxAttempts = 80) {
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (this.mapInitAborted) return null
        await this.$nextTick()
        const el = this.$refs.googleMap
        if (el instanceof HTMLElement && el.clientWidth > 0 && el.clientHeight > 0) {
          return el
        }
        await new Promise(resolve => setTimeout(resolve, 50))
      }
      const fallback = this.$refs.googleMap
      return fallback instanceof HTMLElement ? fallback : null
    },
    async takeScreenshot() {
      const container = this.$refs.googleMap
      if (!container || !this.map) {
        throw new Error('Map view is not ready')
      }

      if (window.google?.maps?.event) {
        window.google.maps.event.trigger(this.map, 'resize')
      }
      await waitForGoogleMapIdle(this.map)
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))

      const devicePixelRatio = window.devicePixelRatio || 1
      const scaleFactor = Math.max(2, Math.min(3, devicePixelRatio * 2))

      const restoreMapDom = prepareGoogleMapDomForCapture(container)
      try {
        const canvas = await html2canvas(container, {
          scale: scaleFactor,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#e5e3df',
          ignoreElements: node => node.nodeName === 'IFRAME',
        })

        const normalized = centerCanvasOnMinSize(canvas)
        return normalized.toDataURL('image/png')
      } finally {
        restoreMapDom()
      }
    },
    refreshMapSize() {
      if (!this.map) {
        this.initMap()
        return
      }
      if (!window.google?.maps?.event) return
      window.google.maps.event.trigger(this.map, 'resize')
      this.focusCurrentPolygon()
    },
    whenMapReady(timeoutMs = 12000) {
      if (this.map) return Promise.resolve(this.map)
      this.initMap()
      const started = Date.now()
      return new Promise(resolve => {
        const tick = () => {
          if (this.map) {
            resolve(this.map)
            return
          }
          if (this.mapInitAborted || Date.now() - started >= timeoutMs) {
            resolve(null)
            return
          }
          window.setTimeout(tick, 50)
        }
        tick()
      })
    },
    getFullscreenTargetElement() {
      return this.$el?.closest?.('.map-fullscreen-root')
        || this.$el?.querySelector?.('.map-container-wrapper')
        || this.$refs.googleMap
        || this.$el?.querySelector?.('.coordinates-google-map')
        || null
    },
    isMapFullscreen() {
      const el = this.getFullscreenTargetElement()
      const fsEl = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.mozFullScreenElement
        || document.msFullscreenElement
      return !!(el && fsEl && (fsEl === el || el.contains(fsEl) || fsEl.contains(el)))
    },
    async enterMapFullscreen() {
      if (this.isMapFullscreen()) {
        this.refreshMapSize()
        return true
      }
      const el = this.getFullscreenTargetElement()
      const request = el?.requestFullscreen || el?.webkitRequestFullscreen || el?.msRequestFullscreen
      if (!request) return false
      try {
        const result = request.call(el)
        if (result && typeof result.then === 'function') await result
        this.$nextTick(() => this.refreshMapSize())
        return true
      } catch (error) {
        return false
      }
    },
    async exitMapFullscreen() {
      if (!this.isMapFullscreen()) return true
      const doc = document
      const exit = doc.exitFullscreen
        || doc.webkitExitFullscreen
        || doc.msExitFullscreen
      if (!exit) return false
      try {
        const result = exit.call(doc)
        if (result && typeof result.then === 'function') await result
        this.$nextTick(() => this.refreshMapSize())
        return true
      } catch (error) {
        return false
      }
    },
    async toggleMapFullscreen() {
      if (this.isMapFullscreen()) {
        await this.exitMapFullscreen()
        return
      }
      await this.enterMapFullscreen()
    },
    getFullscreenButtonIconHtml(active = false) {
      if (active) {
        return '<span class="google-fullscreen-button__icon" aria-hidden="true">'
          + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
          + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
          + '<path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>'
          + '<path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>'
          + '</svg></span>'
      }
      return '<span class="google-fullscreen-button__icon" aria-hidden="true">'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>'
        + '<path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>'
        + '</svg></span>'
    },
    updateFullscreenButtonState() {
      if (!this.fullscreenButton) return
      const active = this.isMapFullscreen()
      this.fullscreenButton.classList.toggle('google-fullscreen-button--active', active)
      this.fullscreenButton.title = active ? 'إنهاء ملء الشاشة' : 'ملء الشاشة'
      this.fullscreenButton.setAttribute('aria-pressed', active ? 'true' : 'false')
      this.fullscreenButton.innerHTML = this.getFullscreenButtonIconHtml(active)
    },
    bindFullscreenListeners() {
      if (this._onFullscreenChange) return
      this._onFullscreenChange = () => {
        this.promoteNativeFullscreenToRoot()
        this.$nextTick(() => this.refreshMapSize())
      }
      document.addEventListener('fullscreenchange', this._onFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this._onFullscreenChange)
    },
    promoteNativeFullscreenToRoot() {
      const root = this.getFullscreenTargetElement()
      const canvas = this.$refs.googleMap
        || this.$el?.querySelector?.('.coordinates-google-map')
      const fsEl = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.mozFullScreenElement
        || document.msFullscreenElement
      if (!root || !fsEl || fsEl === root) return
      const canvasIsFullscreen = !!(canvas && (
        fsEl === canvas
        || canvas.contains(fsEl)
        || fsEl.contains(canvas)
      ))
      if (!canvasIsFullscreen) return
      const request = root.requestFullscreen
        || root.webkitRequestFullscreen
        || root.msRequestFullscreen
      if (!request) return
      try {
        request.call(root)
      } catch (error) {
        // Browser may reject nested fullscreen requests.
      }
    },
    unbindFullscreenListeners() {
      this.unbindNativeFullscreenIntercept()
      if (!this._onFullscreenChange) return
      document.removeEventListener('fullscreenchange', this._onFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', this._onFullscreenChange)
      this._onFullscreenChange = null
    },
    isNativeFullscreenControl(target) {
      if (!(target instanceof Element)) return false
      return !!target.closest('.gm-fullscreen-control')
    },
    bindNativeFullscreenIntercept() {
      const container = this.$refs.googleMap
      if (!container || this._onNativeFullscreenClick) return
      this._onNativeFullscreenClick = event => {
        if (!this.isNativeFullscreenControl(event.target)) return
        event.preventDefault()
        event.stopImmediatePropagation()
        this.toggleMapFullscreen()
      }
      container.addEventListener('click', this._onNativeFullscreenClick, true)
    },
    unbindNativeFullscreenIntercept() {
      const container = this.$refs.googleMap
      if (container && this._onNativeFullscreenClick) {
        container.removeEventListener('click', this._onNativeFullscreenClick, true)
      }
      this._onNativeFullscreenClick = null
    },
    addFullscreenControl() {
      if (!this.map || this.fullscreenButton) return
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'google-map-control-button google-fullscreen-button'
      button.setAttribute('aria-label', 'ملء الشاشة')
      button.addEventListener('click', () => this.toggleMapFullscreen())
      this.fullscreenButton = button
      this.updateFullscreenButtonState()
      this.bindFullscreenListeners()
      this.pushMapControlToPosition(button, 'TOP_RIGHT')
    },
    setupMapResizeObserver() {
      const container = this.$refs.googleMap
      if (!container || this.resizeObserver) return

      let rafId = null
      const onResize = () => {
        if (rafId) cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(() => {
          this.refreshMapSize()
          rafId = null
        })
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(onResize)
        this.resizeObserver.observe(container)
        const fullscreenRoot = this.getFullscreenTargetElement()
        if (fullscreenRoot && fullscreenRoot !== container) {
          this.resizeObserver.observe(fullscreenRoot)
        }
      }

      this.onWindowResize = onResize
      window.addEventListener('orientationchange', onResize)
      window.addEventListener('resize', onResize)
    },
    async reloadMapLayers() {
      if (!this.map) return
      this.loadingMapData = true
      try {
        await this.setCurrentToGeolocation()
        await this.loadRequestedDataLayers({ forceMapData: true })
        this.drawOtherProjectLocations()
        this.drawPolygon()
      } catch (error) {
        console.error('reloadMapLayers failed:', error)
        if (this.$toast) {
          this.$toast.error('فشل تحديث طبقات الخريطة')
        }
      } finally {
        this.loadingMapData = false
      }
    },
    async initMap() {
      if (this.map) return

      if (this.mapInitPromise) {
        await this.mapInitPromise
        return
      }

      this.mapInitPromise = (async () => {
        const mapContainer = await this.waitForMapContainer()
        if (!mapContainer || this.mapInitAborted) {
          throw new Error('Map container is not ready yet')
        }

        await loadGoogleMapsScript()
        if (this.mapInitAborted) return

        const { Map, RenderingType } = await importGoogleMapsLibrary('maps')
        const { spherical } = await importGoogleMapsLibrary('geometry')
        if (this.mapInitAborted) return
        this.geometrySpherical = spherical

        await this.setCurrentToGeolocation()
        if (this.mapInitAborted) return

        // Re-resolve container after async work (ref can be lost on re-render / v-if).
        const container = await this.waitForMapContainer()
        if (!container || this.mapInitAborted) {
          throw new Error('Map container is not ready yet')
        }

        let center = this.getInitialMapCenter()

        this.imageryYear = this.normalizeImageryYear(this.defaultImageryYear)

        const ControlPosition = window.google?.maps?.ControlPosition
        const MapTypeControlStyle = window.google?.maps?.MapTypeControlStyle

        this.map = new Map(container, {
          zoom: 18,
          minZoom: 2,
          maxZoom: 22,
          center,
          mapTypeId: 'hybrid',
          mapTypeControl: true,
          mapTypeControlOptions: ControlPosition
            ? {
                position: ControlPosition.TOP_RIGHT,
                ...(MapTypeControlStyle?.DROPDOWN_MENU
                  ? { style: MapTypeControlStyle.DROPDOWN_MENU }
                  : {}),
              }
            : undefined,
          zoomControl: true,
          zoomControlOptions: ControlPosition
            ? { position: ControlPosition.LEFT_TOP }
            : undefined,
          streetViewControl: false,
          fullscreenControl: true,
          fullscreenControlOptions: ControlPosition
            ? { position: ControlPosition.RIGHT_TOP }
            : undefined,
          // Raster tiles are capturable by html2canvas; WebGL/vector tiles are not.
          ...(RenderingType?.RASTER ? { renderingType: RenderingType.RASTER } : {}),
        })

        this.bindImageryMapTypeListener()
        this.setupMapPointClickListener()
        this.addMapControls()
        await this.applyImageryYear(this.imageryYear, { emit: false })

        this.drawPolygon()
        this.drawOtherProjectLocations()
        if (this.mapInitAborted) return

        this.prefetchAdministrativeBoundaryTypes()
        this.setupMapResizeObserver()
        this.bindNativeFullscreenIntercept()
        this.mountEgyptGis()
        this.$nextTick(() => this.refreshMapSize())
        window.setTimeout(() => {
          if (!this.mapInitAborted) this.refreshMapSize()
        }, 250)
      })()

      this.loadingMapData = true
      try {
        await this.mapInitPromise
      } catch (error) {
        if (this.mapInitAborted) return
        console.error('CoordinatesMapGoogle init failed:', error)
        if (this.$toast) {
          const msg = String(error?.message || '')
          if (msg.includes('API key') || msg.includes('InvalidKey')) {
            this.$toast.error('مفتاح Google Maps غير صالح — راجع VUE_APP_GOOGLE_MAPS_API_KEY في .env')
          } else if (msg.includes('Map container')) {
            this.$toast.error('حاوية الخريطة غير جاهزة — أعد فتح الخريطة')
          } else {
            this.$toast.error('تعذر تحميل خريطة Google حالياً')
          }
        }
      } finally {
        this.mapInitPromise = null
        this.loadingMapData = false
      }
    },
    pushMapControl(button) {
      // LEFT_TOP stacks vertically like ArcGIS `top-left` (zoom + action buttons).
      this.pushMapControlToPosition(button, 'LEFT_TOP')
    },
    pushMapControlToPosition(element, positionName = 'LEFT_TOP') {
      if (!this.map || !window.google?.maps?.ControlPosition) return
      const position = window.google.maps.ControlPosition[positionName]
      const slot = this.map.controls?.[position]
      if (slot?.push) slot.push(element)
    },
    insertMapControlAtPosition(element, positionName, index = 0) {
      if (!this.map || !window.google?.maps?.ControlPosition) return
      const position = window.google.maps.ControlPosition[positionName]
      const slot = this.map.controls?.[position]
      if (slot?.insertAt) {
        const length = typeof slot.getLength === 'function' ? slot.getLength() : 0
        const safeIndex = Math.min(Math.max(Number(index) || 0, 0), length)
        slot.insertAt(safeIndex, element)
        return
      }
      if (slot?.push) slot.push(element)
    },
    addMeasureLiveLabelControl() {
      if (this.measureLiveLabelControl) return

      const label = document.createElement('div')
      label.className = 'measure-live-label-control'
      label.setAttribute('role', 'status')
      label.setAttribute('aria-live', 'polite')
      label.style.display = 'none'
      this.measureLiveLabelControl = label
      this.pushMapControlToPosition(label, 'BOTTOM_CENTER')
    },
    addMapControls() {
      if (!this.map) return

      const homeButton = document.createElement('button')
      homeButton.type = 'button'
      homeButton.className = 'google-map-control-button home-location-button'
      homeButton.title = 'الذهاب إلى موقعي الحالي'
      homeButton.innerHTML = this.getHomeButtonIconHtml()
      homeButton.addEventListener('click', () => this.addUserLocation())
      this.homeButton = homeButton
      this.pushMapControl(homeButton)

      const saveButton = document.createElement('button')
      saveButton.type = 'button'
      saveButton.className = 'google-map-control-button save-polygon-button'
      saveButton.title = 'حفظ المضلع المرسوم'
      saveButton.setAttribute('aria-label', 'حفظ المضلع المرسوم')
      saveButton.innerHTML = this.getSavePolygonButtonIconHtml()
      saveButton.addEventListener('click', () => {
        if (!this.canSaveDrawnPolygon) return
        this.saveDrawnCoordinates()
      })
      this.savePolygonButton = saveButton
      this.updateSavePolygonButtonState()
      this.pushMapControl(saveButton)

      if (this.enableSaveAsNewLocation) {
        const saveAsNewButton = document.createElement('button')
        saveAsNewButton.type = 'button'
        saveAsNewButton.className = 'google-map-control-button save-as-new-location-button'
        saveAsNewButton.title = 'حفظ المضلع كقطعة جديدة'
        saveAsNewButton.setAttribute('aria-label', 'حفظ المضلع كقطعة جديدة')
        saveAsNewButton.innerHTML = this.getSaveAsNewLocationButtonIconHtml()
        saveAsNewButton.addEventListener('click', () => {
          if (!this.canSaveDrawnPolygon) return
          this.saveDrawnPolygonAsNewLocation()
        })
        this.saveAsNewLocationButton = saveAsNewButton
        this.updateSaveAsNewLocationButtonState()
        this.pushMapControl(saveAsNewButton)
      }

      this.addAdminBoundariesUploadButtonToView()
      this.addSketchToolbar()
      this.bindFullscreenListeners()
      this.addMeasureLiveLabelControl()
      this.addImageryYearControl()
    },
    normalizeImageryYear(year) {
      const numericYear = Number(year)
      return IMAGERY_YEAR_OPTIONS.includes(numericYear) ? numericYear : DEFAULT_IMAGERY_YEAR
    },
    isSatelliteStyleMapType(mapTypeId) {
      if (!mapTypeId) return false
      const id = String(mapTypeId)
      return id === 'hybrid' || id === 'satellite'
    },
    async buildWaybackOverlayLayer(year) {
      if (!window.google?.maps?.ImageMapType) return null

      const releaseId = await fetchWaybackReleaseForYear(year)
      if (!releaseId) return null

      return new window.google.maps.ImageMapType({
        name: `Imagery ${year}`,
        alt: `Satellite imagery ${year}`,
        tileSize: new window.google.maps.Size(256, 256),
        minZoom: 0,
        maxZoom: 22,
        opacity: 1,
        getTileUrl: (coord, zoom) => buildWaybackTileUrl(releaseId, coord, zoom),
      })
    },
    removeWaybackOverlay() {
      if (!this.map || !this.waybackOverlayLayer) return

      const overlays = this.map.overlayMapTypes
      if (overlays?.getLength) {
        for (let index = overlays.getLength() - 1; index >= 0; index -= 1) {
          if (overlays.getAt(index) === this.waybackOverlayLayer) {
            overlays.removeAt(index)
            break
          }
        }
      }

      this.waybackOverlayLayer = null
      this.waybackOverlayYear = null
    },
    async ensureWaybackOverlay(year = this.imageryYear) {
      if (!this.map) return false

      const normalizedYear = this.normalizeImageryYear(year)
      if (this.waybackOverlayLayer && this.waybackOverlayYear === normalizedYear) {
        return true
      }

      this.removeWaybackOverlay()

      const layer = await this.buildWaybackOverlayLayer(normalizedYear)
      if (!layer) return false

      this.waybackOverlayLayer = layer
      this.waybackOverlayYear = normalizedYear
      this.map.overlayMapTypes.insertAt(0, layer)

      if (window.google?.maps?.event) {
        window.google.maps.event.trigger(this.map, 'resize')
      }
      return true
    },
    bindImageryMapTypeListener() {
      if (!this.map || !window.google?.maps?.event || this.mapTypeChangedListener) return

      this.mapTypeChangedListener = window.google.maps.event.addListener(
        this.map,
        'maptypeid_changed',
        () => this.onMapTypeIdChanged(),
      )
    },
    onMapTypeIdChanged() {
      if (!this.map || this.syncingImageryMapType || this.imageryYear === DEFAULT_IMAGERY_YEAR) {
        return
      }

      const currentType = String(this.map.getMapTypeId() || '')
      if (this.isSatelliteStyleMapType(currentType)) {
        this.ensureWaybackOverlay(this.imageryYear)
      } else {
        this.removeWaybackOverlay()
      }
    },
    async applyImageryYear(year, { emit = true } = {}) {
      const normalizedYear = this.normalizeImageryYear(year)
      this.imageryYear = normalizedYear
      this.updateImageryYearControlState()

      if (!this.map) {
        if (emit) this.$emit('imagery-year-changed', normalizedYear)
        return
      }

      if (this.syncingImageryMapType) {
        if (emit) this.$emit('imagery-year-changed', normalizedYear)
        return
      }

      this.syncingImageryMapType = true
      try {
        if (normalizedYear === DEFAULT_IMAGERY_YEAR) {
          this.removeWaybackOverlay()
          const currentType = String(this.map.getMapTypeId() || '')
          if (!this.isSatelliteStyleMapType(currentType)) {
            this.map.setMapTypeId('hybrid')
          }
        } else {
          const currentType = String(this.map.getMapTypeId() || '')
          if (!this.isSatelliteStyleMapType(currentType)) {
            this.map.setMapTypeId('hybrid')
          }

          const applied = await this.ensureWaybackOverlay(normalizedYear)
          if (!applied && this.$toast) {
            this.$toast.error(`تعذر تحميل صور ${normalizedYear} — تحقق من الاتصال بالإنترنت`)
          }
        }
      } finally {
        this.syncingImageryMapType = false
      }

      if (emit) this.$emit('imagery-year-changed', normalizedYear)
    },
    setImageryYear(year) {
      this.applyImageryYear(year)
    },
    blurMapControlTrigger(event) {
      const target = event?.currentTarget
      if (target instanceof HTMLElement) {
        target.blur()
      }
    },
    addImageryYearControl() {
      if (this.imageryYearControl) return

      const wrapper = document.createElement('div')
      wrapper.className = 'imagery-year-control'
      wrapper.setAttribute('role', 'group')
      wrapper.setAttribute('aria-label', 'تاريخ صور الأقمار الصناعية')

      const label = document.createElement('span')
      label.className = 'imagery-year-control__label'
      label.textContent = 'صور'
      wrapper.appendChild(label)

      IMAGERY_YEAR_OPTIONS.forEach(year => {
        const button = document.createElement('button')
        button.type = 'button'
        button.className = 'imagery-year-control__button'
        button.dataset.year = String(year)
        button.textContent = String(year)
        button.title = year === DEFAULT_IMAGERY_YEAR
          ? 'صور Google الحالية (2026)'
          : `صور أرشيفية تقريباً من ${year}`
        button.addEventListener('click', event => {
          this.applyImageryYear(year)
          this.blurMapControlTrigger(event)
        })
        wrapper.appendChild(button)
      })

      this.imageryYearControl = wrapper
      this.updateImageryYearControlState()
      this.insertMapControlAtPosition(wrapper, 'RIGHT_TOP', 0)
    },
    addAdminBoundariesUploadButtonToView() {
      if (!this.map || this.adminBoundariesUploadButton || !this.canUploadAdministrativeBoundaries) {
        return
      }

      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'google-map-control-button admin-boundaries-upload-button'
      button.title = 'رفع الحدود الإدارية'
      button.setAttribute('aria-label', 'رفع الحدود الإدارية')
      button.innerHTML = this.getAdminBoundariesUploadButtonIconHtml()
      button.addEventListener('click', () => this.openAdminBoundariesUploadModal())
      this.adminBoundariesUploadButton = button
      this.pushMapControl(button)
    },
    getAdminBoundariesUploadButtonIconHtml() {
      return '<span class="admin-boundaries-upload-button__icon" aria-hidden="true">'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>'
        + '<polyline points="17 8 12 3 7 8"/>'
        + '<line x1="12" y1="3" x2="12" y2="15"/>'
        + '</svg></span>'
    },
    async prefetchAdministrativeBoundaryTypes() {
      try {
        this.adminBoundaryTypes = await fetchAdministrativeBoundaryTypes()
      } catch (error) {
        this.adminBoundaryTypes = ADMINISTRATIVE_BOUNDARY_TYPES.map(item => ({ ...item }))
      }
    },
    openAdminBoundariesUploadModal() {
      if (!this.canUploadAdministrativeBoundaries) return
      this.prefetchAdministrativeBoundaryTypes()
      this.adminBoundariesUploadForm = {
        file: null,
        type: this.adminBoundariesUploadForm?.type || 'governorate',
        year: this.administrativeBoundariesYear || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
      }
      this.showAdminBoundariesUploadModal = true
    },
    resetAdminBoundariesUploadForm() {
      this.adminBoundariesUploadForm = {
        file: null,
        type: this.adminBoundariesUploadForm?.type || 'governorate',
        year: this.administrativeBoundariesYear || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
      }
      this.adminBoundariesUploading = false
    },
    async submitAdminBoundariesUpload() {
      if (!this.canUploadAdministrativeBoundaries) return
      if (!this.adminBoundariesUploadForm.file) {
        this.$toast?.error?.('يرجى اختيار ملف ZIP أو KMZ أو KML')
        return
      }
      if (!this.adminBoundariesUploadForm.type) {
        this.$toast?.error?.('يرجى اختيار نوع الطبقة')
        return
      }

      this.adminBoundariesUploading = true
      try {
        const year = this.adminBoundariesUploadForm.year || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR
        const type = this.adminBoundariesUploadForm.type
        const result = await uploadAdministrativeBoundaries(
          this.adminBoundariesUploadForm.file,
          { type, year },
        )
        const payload = result?.data && typeof result.data === 'object' ? result.data : result
        this.showAdminBoundariesUploadModal = false
        this.resetAdminBoundariesUploadForm()
        if (payload?.queued) {
          this.$toast?.success?.(payload.message || 'سيتم إشعارك عند الانتهاء')
          return
        }
        const created = payload?.created ?? 0
        const updated = payload?.updated ?? 0
        const total = payload?.total ?? ((Number(created) || 0) + (Number(updated) || 0))
        const uploadedType = payload?.type || type
        this.administrativeBoundariesYear = Number(payload?.year) || year
        this.$toast?.success?.(
          `تم استيراد ${payload?.type_label_ar || uploadedType} — جديد: ${created} | محدّث: ${updated} | الإجمالي: ${total}`,
        )
        await this.loadAdministrativeBoundaryType(uploadedType, { force: true })
      } catch (error) {
        console.error('Failed to upload administrative boundaries:', error)
        this.$toast?.error?.(
          error?.response?.data?.message || 'فشل رفع ملف الحدود الإدارية',
        )
      } finally {
        this.adminBoundariesUploading = false
      }
    },
    addSketchToolbar() {
      if (!this.map || this.sketchToolbar) return

      const toolbar = document.createElement('div')
      toolbar.className = 'google-sketch-toolbar'
      toolbar.setAttribute('role', 'toolbar')
      toolbar.setAttribute('aria-label', 'أدوات الرسم والطبقات')

      const toolsGroup = document.createElement('div')
      toolsGroup.className = 'google-sketch-toolbar__group'

      const createToolButton = (mode, title, iconHtml) => {
        const button = document.createElement('button')
        button.type = 'button'
        button.className = `google-map-control-button google-sketch-tool google-sketch-tool--${mode}`
        button.title = title
        button.setAttribute('aria-label', title)
        button.innerHTML = iconHtml
        button.addEventListener('click', () => this.toggleSketchTool(mode))
        toolsGroup.appendChild(button)
        this.sketchToolButtons[mode] = button
        return button
      }

      createToolButton(
        'select',
        'تحديد عنصر (انقر على مضلع أو رسم)',
        esriSketchToolIcon('select'),
      )
      createToolButton(
        'rectangle-select',
        'تحديد بمستطيل (انقر زاويتين متقابلتين)',
        esriSketchToolIcon('rectangle-select'),
      )
      createToolButton(
        'lasso-select',
        'تحديد بالوهق (انقر نقاطاً ثم انقر مرتين للإنهاء)',
        esriSketchToolIcon('lasso-select'),
      )

      const deleteButton = document.createElement('button')
      deleteButton.type = 'button'
      deleteButton.className = 'google-map-control-button google-sketch-tool google-sketch-tool--delete'
      deleteButton.title = 'حذف العنصر المحدد ومسح القياسات (Delete)'
      deleteButton.setAttribute('aria-label', 'حذف العنصر المحدد ومسح القياسات')
      deleteButton.innerHTML = this.getDeleteSelectionButtonIconHtml()
      deleteButton.addEventListener('click', () => this.deleteSelectedGraphics())
      this.deleteSelectionButton = deleteButton
      this.sketchToolButtons.delete = deleteButton
      toolsGroup.appendChild(deleteButton)
      this.updateDeleteSelectionButtonState()
      this.bindSelectionKeyboard()

      const selectDivider = document.createElement('div')
      selectDivider.className = 'google-sketch-toolbar__divider'
      toolsGroup.appendChild(selectDivider)

      createToolButton(
        'point',
        'رسم نقطة',
        esriSketchToolIcon('point'),
      )
      createToolButton(
        'polyline',
        'رسم خط (انقر نقاطاً ثم انقر مرتين للإنهاء)',
        esriSketchToolIcon('polyline'),
      )
      const polygonButton = createToolButton(
        'polygon',
        'رسم مضلع (انقر على الخريطة لإضافة النقاط، ثم انقر مرتين لإنهاء الرسم)',
        this.getDrawPolygonButtonIconHtml(),
      )
      this.drawPolygonButton = polygonButton
      createToolButton(
        'rectangle',
        'رسم مستطيل (انقر زاويتين متقابلتين)',
        esriSketchToolIcon('rectangle'),
      )
      createToolButton(
        'circle',
        'رسم دائرة (انقر المركز ثم نقطة على المحيط)',
        esriSketchToolIcon('circle'),
      )

      const divider = document.createElement('div')
      divider.className = 'google-sketch-toolbar__divider'
      toolsGroup.appendChild(divider)

      const measureDistanceButton = document.createElement('button')
      measureDistanceButton.type = 'button'
      measureDistanceButton.className = 'google-map-control-button measure-distance-button'
      measureDistanceButton.title = 'قياس المسافة (انقر نقطتين على الخريطة لإنهاء القياس)'
      measureDistanceButton.setAttribute('aria-label', 'قياس المسافة')
      measureDistanceButton.innerHTML = this.getMeasureDistanceButtonIconHtml()
      measureDistanceButton.addEventListener('click', () => this.toggleMeasureDistanceMode())
      this.measureDistanceButton = measureDistanceButton
      toolsGroup.appendChild(measureDistanceButton)

      const measureAreaButton = document.createElement('button')
      measureAreaButton.type = 'button'
      measureAreaButton.className = 'google-map-control-button measure-area-button'
      measureAreaButton.title = 'قياس المساحة (3 نقاط على الأقل، ثم انقر الزر مرة أخرى أو انقر مرتين على الخريطة)'
      measureAreaButton.setAttribute('aria-label', 'قياس المساحة')
      measureAreaButton.innerHTML = this.getMeasureAreaButtonIconHtml()
      measureAreaButton.addEventListener('click', () => this.toggleMeasureAreaMode())
      this.measureAreaButton = measureAreaButton
      toolsGroup.appendChild(measureAreaButton)

      const dimensionsButton = document.createElement('button')
      dimensionsButton.type = 'button'
      dimensionsButton.className = 'google-map-control-button google-dimensions-button'
      dimensionsButton.title = 'إخفاء أبعاد المضلع'
      dimensionsButton.setAttribute('aria-label', 'إظهار أو إخفاء أبعاد المضلع')
      dimensionsButton.innerHTML = sketchToolSvg(
        '<path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/>'
        + '<path d="m14.5 12.5 2-2"/>'
        + '<path d="m11.5 9.5 2-2"/>'
        + '<path d="m8.5 6.5 2-2"/>'
        + '<path d="m17.5 15.5 2-2"/>',
      )
      dimensionsButton.addEventListener('click', () => this.toggleParcelDimensions())
      this.parcelDimensionsButton = dimensionsButton
      toolsGroup.appendChild(dimensionsButton)
      this.syncParcelDimensionsUi()

      const layersDivider = document.createElement('div')
      layersDivider.className = 'google-sketch-toolbar__divider'
      toolsGroup.appendChild(layersDivider)

      const layersButton = document.createElement('button')
      layersButton.type = 'button'
      layersButton.className = 'google-map-control-button google-sketch-layers-button'
      layersButton.title = 'الطبقات والالتقاط'
      layersButton.setAttribute('aria-label', 'الطبقات والالتقاط')
      layersButton.innerHTML = sketchToolSvg(
        '<polygon points="12 2 2 7 12 12 22 7 12 2"/>'
        + '<polyline points="2 17 12 22 22 17"/>'
        + '<polyline points="2 12 12 17 22 12"/>',
      )
      layersButton.addEventListener('click', () => this.toggleSketchLayersPanel())
      this.sketchToolButtons.layers = layersButton
      toolsGroup.appendChild(layersButton)

      toolbar.appendChild(toolsGroup)

      this.sketchToolbar = toolbar
      toolbar.addEventListener('mousedown', event => event.stopPropagation())
      toolbar.addEventListener('touchstart', event => event.stopPropagation(), { passive: true })
      toolbar.addEventListener('dblclick', event => event.stopPropagation())
      this.pushMapControlToPosition(toolbar, 'TOP_RIGHT')
      this.buildSketchLayersPanel()
      this.emitDrawHistorySync()
    },
    buildSketchLayersPanel() {
      if (this.sketchLayersPanel) return
      const panel = document.createElement('div')
      panel.className = 'google-sketch-layers-panel'
      panel.hidden = true

      const head = document.createElement('div')
      head.className = 'google-sketch-layers-panel__head'
      const title = document.createElement('div')
      title.className = 'google-sketch-layers-panel__title'
      title.textContent = 'الطبقات / الالتقاط'
      const toggle = document.createElement('button')
      toggle.type = 'button'
      toggle.className = 'google-sketch-layers-panel__toggle'
      toggle.title = 'إخفاء'
      toggle.setAttribute('aria-label', 'إخفاء')
      toggle.innerHTML = '<span aria-hidden="true">−</span>'
      head.appendChild(title)
      head.appendChild(toggle)
      panel.appendChild(head)

      const body = document.createElement('div')
      body.className = 'google-sketch-layers-panel__body'
      this.sketchLayerItems.forEach(item => {
        const row = document.createElement('label')
        row.className = 'google-sketch-layers-panel__row'
        const checkbox = document.createElement('input')
        checkbox.type = 'checkbox'
        checkbox.dataset.layerKey = item.key
        checkbox.checked = this.layerVisibility[item.key] === true
        checkbox.addEventListener('change', () => {
          this.onSketchLayerToggle(item.key, checkbox.checked)
        })
        const text = document.createElement('span')
        text.textContent = item.title
        row.appendChild(checkbox)
        row.appendChild(text)
        body.appendChild(row)
      })
      panel.appendChild(body)

      this.sketchLayersPanel = panel
      panel.addEventListener('mousedown', event => event.stopPropagation())
      panel.addEventListener('touchstart', event => event.stopPropagation(), { passive: true })
      this.pushMapControlToPosition(panel, 'RIGHT_TOP')
      this.bindDomFloatPanel(panel, {
        id: 'gis-sketch-layers',
        handle: head,
        toggle,
        body,
      })
    },
    readGisFloatStore() {
      try {
        const raw = window.localStorage.getItem('gis-map-float-panels')
        const parsed = raw ? JSON.parse(raw) : {}
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (error) {
        return {}
      }
    },
    writeGisFloatStore(store) {
      try {
        window.localStorage.setItem('gis-map-float-panels', JSON.stringify(store))
      } catch (error) {
        /* ignore */
      }
    },
    applyDomFloatPosition(panel, x, y) {
      if (!panel) return
      panel.style.transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`
    },
    bindDomFloatPanel(panel, { id, handle, toggle, body }) {
      if (!panel || !handle) return
      const saved = this.readGisFloatStore()[id] || {}
      let collapsed = typeof saved.collapsed === 'boolean' ? saved.collapsed : false
      const persist = (patch = {}) => {
        const store = this.readGisFloatStore()
        store[id] = { ...(store[id] || {}), collapsed, ...patch }
        this.writeGisFloatStore(store)
      }
      const applyCollapsed = () => {
        if (body) body.hidden = collapsed
        panel.classList.toggle('is-collapsed', collapsed)
        if (toggle) {
          toggle.title = collapsed ? 'إظهار' : 'إخفاء'
          toggle.setAttribute('aria-label', collapsed ? 'إظهار' : 'إخفاء')
          toggle.textContent = collapsed ? '+' : '−'
        }
      }
      applyCollapsed()

      if (saved.mode === 'translate' && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
        panel._floatX = saved.x
        panel._floatY = saved.y
        this.applyDomFloatPosition(panel, saved.x, saved.y)
      }

      const onToggle = event => {
        event.preventDefault()
        event.stopPropagation()
        collapsed = !collapsed
        applyCollapsed()
        persist()
      }
      if (toggle) toggle.addEventListener('click', onToggle)

      let session = null
      const boundsEl = () => this.$el?.closest?.('.map-fullscreen-root')
        || this.$el?.querySelector('.map-container-wrapper')
        || this.$refs.googleMap
      const onMove = event => {
        if (!session || event.pointerId !== session.pointerId) return
        const dx = event.clientX - session.startX
        const dy = event.clientY - session.startY
        if (!session.moved && Math.abs(dx) + Math.abs(dy) < 4) return
        event.preventDefault()
        session.moved = true
        panel.classList.add('is-dragging')
        const minDx = session.parentLeft - session.startLeft
        const maxDx = session.parentLeft + session.parentW - session.elW - session.startLeft
        const minDy = session.parentTop - session.startTop
        const maxDy = session.parentTop + session.parentH - session.elH - session.startTop
        const x = session.origX + Math.min(Math.max(dx, minDx), maxDx)
        const y = session.origY + Math.min(Math.max(dy, minDy), maxDy)
        this.applyDomFloatPosition(panel, x, y)
        panel._floatX = x
        panel._floatY = y
        session.x = x
        session.y = y
      }
      const onUp = event => {
        if (session && event.pointerId !== session.pointerId) return
        if (session?.moved) persist({ mode: 'translate', x: session.x, y: session.y })
        panel.classList.remove('is-dragging')
        session = null
        document.removeEventListener('pointermove', onMove, true)
        document.removeEventListener('pointerup', onUp, true)
        document.removeEventListener('pointercancel', onUp, true)
      }
      const onDown = event => {
        if (event.pointerType === 'mouse' && event.button !== 0) return
        if (event.target.closest('button, input, label, a')) return
        event.preventDefault()
        const parent = boundsEl()
        if (!parent) return
        try {
          handle.setPointerCapture(event.pointerId)
        } catch (error) {
          /* ignore */
        }
        const parentRect = parent.getBoundingClientRect()
        const elRect = panel.getBoundingClientRect()
        session = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          origX: panel._floatX || 0,
          origY: panel._floatY || 0,
          startLeft: elRect.left,
          startTop: elRect.top,
          elW: elRect.width,
          elH: elRect.height,
          parentLeft: parentRect.left,
          parentTop: parentRect.top,
          parentW: parentRect.width,
          parentH: parentRect.height,
          moved: false,
        }
        document.addEventListener('pointermove', onMove, { capture: true, passive: false })
        document.addEventListener('pointerup', onUp, true)
        document.addEventListener('pointercancel', onUp, true)
      }
      handle.addEventListener('pointerdown', onDown)
      this._domFloatCleanups = this._domFloatCleanups || []
      this._domFloatCleanups.push(() => {
        if (toggle) toggle.removeEventListener('click', onToggle)
        handle.removeEventListener('pointerdown', onDown)
        document.removeEventListener('pointermove', onMove, true)
        document.removeEventListener('pointerup', onUp, true)
        document.removeEventListener('pointercancel', onUp, true)
      })
    },
    unbindDomFloatPanels() {
      (this._domFloatCleanups || []).forEach(fn => fn())
      this._domFloatCleanups = []
    },
    toggleSketchLayersPanel() {
      this.sketchLayersPanelOpen = !this.sketchLayersPanelOpen
      if (this.sketchLayersPanel) {
        this.sketchLayersPanel.hidden = !this.sketchLayersPanelOpen
      }
      if (this.sketchToolButtons.layers) {
        this.sketchToolButtons.layers.classList.toggle(
          'google-sketch-tool--active',
          this.sketchLayersPanelOpen,
        )
      }
    },
    syncSketchLayersPanel() {
      if (!this.sketchLayersPanel) return
      this.sketchLayersPanel.querySelectorAll('input[data-layer-key]').forEach(checkbox => {
        const key = checkbox.dataset.layerKey
        checkbox.checked = this.layerVisibility[key] === true
      })
    },
    onSketchLayerToggle(layerKey, enabled) {
      this.layerVisibility[layerKey] = enabled
      this.toggleLayer(layerKey)
    },
    isParcelDimensionsVisible() {
      return this.layerVisibility.parcelDimensions !== false
    },
    toggleParcelDimensions() {
      this.layerVisibility.parcelDimensions = !this.isParcelDimensionsVisible()
      this.toggleLayer('parcelDimensions')
    },
    syncParcelDimensionsUi() {
      const visible = this.isParcelDimensionsVisible()
      if (this.parcelDimensionsButton) {
        this.parcelDimensionsButton.classList.toggle('google-sketch-tool--active', visible)
        this.parcelDimensionsButton.title = visible ? 'إخفاء أبعاد المضلع' : 'إظهار أبعاد المضلع'
        this.parcelDimensionsButton.setAttribute('aria-pressed', visible ? 'true' : 'false')
      }
    },
    cloneParcelSnapshot() {
      const fromPolygon = this.parcelPolygon
        ? this.latLngPathToCoordinates(this.parcelPolygon.getPath())
        : []
      const source = fromPolygon.length >= 3 ? fromPolygon : this.pendingPolygonCoordinates
      return this.normalizeCoordinatesList(source).map(coord => ({ ...coord }))
    },
    normalizeDrawPoints(points) {
      return (Array.isArray(points) ? points : []).map(point => ({
        lat: Number(typeof point?.lat === 'function' ? point.lat() : point?.lat),
        lng: Number(typeof point?.lng === 'function' ? point.lng() : point?.lng),
      })).filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    },
    cloneHistoryState() {
      return {
        drawing: !!this.polygonDrawActive,
        sketchMode: this.polygonDrawActive
          ? (this.sketchMode === 'polyline' ? 'polyline' : 'polygon')
          : null,
        drawPoints: this.polygonDrawActive ? this.normalizeDrawPoints(this.polygonDrawPoints) : [],
        parcelCoords: this.cloneParcelSnapshot(),
      }
    },
    drawPointsEqual(left, right) {
      const a = this.normalizeDrawPoints(left)
      const b = this.normalizeDrawPoints(right)
      if (a.length !== b.length) return false
      return a.every((point, index) => (
        Math.abs(point.lat - b[index].lat) < 1e-10
        && Math.abs(point.lng - b[index].lng) < 1e-10
      ))
    },
    snapshotsEqual(left, right) {
      if (!!left?.drawing !== !!right?.drawing) return false
      if (left?.drawing) return this.drawPointsEqual(left.drawPoints, right.drawPoints)
      return this.areCoordinatesEqual(left?.parcelCoords, right?.parcelCoords)
    },
    clearDrawHistoryTimers() {
      if (this.pathHistoryTimer) {
        window.clearTimeout(this.pathHistoryTimer)
        this.pathHistoryTimer = null
      }
    },
    pushDrawHistory(label) {
      if (this.suppressDrawHistory) return
      this.clearDrawHistoryTimers()
      const snapshot = this.cloneHistoryState()
      const current = this.drawHistory[this.drawHistoryIndex]
      if (current && this.snapshotsEqual(current, snapshot)) return

      const next = this.drawHistory.slice(0, this.drawHistoryIndex + 1)
      next.push({
        id: `${Date.now()}-${next.length}`,
        label: label || 'تعديل',
        ...snapshot,
      })
      if (next.length > DRAW_HISTORY_MAX) next.shift()
      this.drawHistory = next
      this.drawHistoryIndex = this.drawHistory.length - 1
      this.emitDrawHistorySync()
    },
    applyDrawSnapshot(entry) {
      this.suppressDrawHistory = true
      this.skipPathHistory = true
      try {
        if (entry?.drawing) {
          this.restoreDrawingSnapshot(entry)
          return
        }
        if (this.polygonDrawActive) this.stopPolygonDrawMode()
        const coords = Array.isArray(entry?.parcelCoords) ? entry.parcelCoords : []
        if (coords.length < 3) {
          this.disableParcelMoveMode()
          this.teardownParcelPolygon()
          this.pendingPolygonCoordinates = []
          this.$emit('coordinates-changed', [])
          this.updateSavePolygonButtonState()
        } else {
          this.drawPolygon(coords)
          this.suppressPropRedrawUntil = Date.now() + 8000
          if (this.isMoveToolActive()) this.enableParcelMoveMode()
          else this.enableParcelEdit()
        }
      } finally {
        this.suppressDrawHistory = false
        this.skipPathHistory = false
      }
      this.emitDrawHistorySync()
    },
    restoreDrawingSnapshot(entry) {
      const parcelCoords = Array.isArray(entry?.parcelCoords) ? entry.parcelCoords : []
      if (parcelCoords.length >= 3) {
        this.drawPolygon(parcelCoords)
        this.suppressPropRedrawUntil = Date.now() + 8000
        this.disableParcelEdit()
      } else {
        this.disableParcelMoveMode()
        this.teardownParcelPolygon()
        this.pendingPolygonCoordinates = []
        this.$emit('coordinates-changed', [])
        this.updateSavePolygonButtonState()
      }
      this.startPolygonDrawMode({
        skipReset: true,
        skipHistory: true,
        sketchMode: entry?.sketchMode,
      })
      this.polygonDrawPoints = this.normalizeDrawPoints(entry?.drawPoints)
      this.rebuildPolygonDrawGraphics()
    },
    undoDrawAction() {
      if (this.drawHistoryIndex <= 0) return
      this.drawHistoryIndex -= 1
      this.applyDrawSnapshot(this.drawHistory[this.drawHistoryIndex])
    },
    redoDrawAction() {
      if (this.drawHistoryIndex >= this.drawHistory.length - 1) return
      this.drawHistoryIndex += 1
      this.applyDrawSnapshot(this.drawHistory[this.drawHistoryIndex])
    },
    jumpDrawHistory(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.drawHistory.length) return
      this.drawHistoryIndex = index
      this.applyDrawSnapshot(this.drawHistory[index])
    },
    emitDrawHistorySync() {
      this.$emit('draw-history-sync', {
        entries: (this.drawHistory || []).map(entry => ({
          id: entry.id,
          label: entry.label,
        })),
        index: this.drawHistoryIndex,
        moveActive: this.isMoveToolActive(),
      })
    },
    updateDrawHistoryButtons() {
      this.emitDrawHistorySync()
    },
    detachDrawHistoryOverlay() {
      this.drawHistoryPanel?.remove?.()
      this.drawHistoryFab?.remove?.()
      this.drawHistoryPanel = null
      this.drawHistoryFab = null
    },
    unbindDrawHistoryMediaQuery() {
      const media = this.drawHistoryMediaQuery
      if (media && this._onDrawHistoryMedia) {
        if (typeof media.removeEventListener === 'function') {
          media.removeEventListener('change', this._onDrawHistoryMedia)
        } else if (typeof media.removeListener === 'function') {
          media.removeListener(this._onDrawHistoryMedia)
        }
      }
      this.drawHistoryMediaQuery = null
      this._onDrawHistoryMedia = null
    },
    renderDrawHistoryPanel() {
      this.emitDrawHistorySync()
    },
    enableParcelMoveMode() {
      if (!this.parcelPolygon || !this.map) return
      this.detachParcelClickListener()
      this.detachParcelDragListeners()
      this.parcelPolygon.setOptions({
        editable: false,
        draggable: true,
        clickable: true,
        zIndex: 4,
      })
      this.parcelDragListeners = [
        this.parcelPolygon.addListener('dragstart', () => {
          this.skipPathHistory = true
          this.moveDragStartCoords = this.cloneParcelSnapshot()
        }),
        this.parcelPolygon.addListener('drag', () => {
          this.syncMoveVertexMarkerPositions()
          this.scheduleParcelDimensionsRefresh()
        }),
        this.parcelPolygon.addListener('dragend', event => {
          this.onParcelMoveEnd(event)
        }),
      ]
      this.syncMoveVertexMarkers()
    },
    disableParcelMoveMode() {
      this.detachParcelDragListeners()
      this.clearMoveVertexMarkers()
      this.moveDragStartCoords = null
      this.moveGrabVertexIndex = -1
      if (!this.parcelPolygon) return
      this.parcelPolygon.setOptions({
        draggable: false,
        editable: false,
        zIndex: 2,
      })
    },
    detachParcelDragListeners() {
      this.parcelDragListeners.forEach(listener => {
        if (listener?.remove) listener.remove()
        else if (listener) window.google.maps.event.removeListener(listener)
      })
      this.parcelDragListeners = []
    },
    clearMoveVertexMarkers() {
      this.moveVertexListeners.forEach(listener => {
        if (listener?.remove) listener.remove()
        else if (listener) window.google.maps.event.removeListener(listener)
      })
      this.moveVertexListeners = []
      this.moveVertexMarkers.forEach(marker => marker?.setMap?.(null))
      this.moveVertexMarkers = []
    },
    syncMoveVertexMarkers() {
      if (!this.parcelPolygon || !this.map || !window.google?.maps) return
      const coords = this.latLngPathToCoordinates(this.parcelPolygon.getPath())
      if (this.moveVertexMarkers.length !== coords.length) {
        this.clearMoveVertexMarkers()
        coords.forEach((coord, index) => {
          const marker = new window.google.maps.Marker({
            position: { lat: coord.latitude, lng: coord.longitude },
            map: this.map,
            draggable: true,
            zIndex: 6,
            cursor: 'move',
            title: 'اسحب الرأس لنقل المضلع بالكامل',
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: '#2563eb',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            },
          })
          this.moveVertexListeners.push(marker.addListener('dragstart', () => {
            this.skipPathHistory = true
            this.moveDragStartCoords = this.cloneParcelSnapshot()
            this.moveGrabVertexIndex = index
          }))
          this.moveVertexListeners.push(marker.addListener('drag', () => {
            this.translateParcelFromGrab(index, marker.getPosition())
          }))
          this.moveVertexListeners.push(marker.addListener('dragend', () => {
            this.onParcelMoveEnd({ latLng: marker.getPosition() }, index)
          }))
          this.moveVertexMarkers.push(marker)
        })
        return
      }
      this.syncMoveVertexMarkerPositions()
    },
    syncMoveVertexMarkerPositions() {
      if (!this.parcelPolygon) return
      const coords = this.latLngPathToCoordinates(this.parcelPolygon.getPath())
      this.moveVertexMarkers.forEach((marker, index) => {
        const coord = coords[index]
        if (!coord || !marker?.setPosition) return
        marker.setPosition({ lat: coord.latitude, lng: coord.longitude })
      })
    },
    translateParcelFromGrab(index, latLng) {
      if (!latLng || !Array.isArray(this.moveDragStartCoords) || !this.moveDragStartCoords[index]) return
      const origin = this.moveDragStartCoords[index]
      const dLat = Number(latLng.lat()) - Number(origin.latitude)
      const dLng = Number(latLng.lng()) - Number(origin.longitude)
      this.applyParcelTranslation(this.moveDragStartCoords, dLat, dLng, { recordHistory: false })
    },
    applyParcelTranslation(startCoords, dLat, dLng, { recordHistory = false, label = 'نقل المضلع' } = {}) {
      if (!this.parcelPolygon || !Array.isArray(startCoords) || startCoords.length < 3) return
      const next = startCoords.map(coord => ({
        ...coord,
        latitude: Number(coord.latitude) + dLat,
        longitude: Number(coord.longitude) + dLng,
        x: Number(coord.longitude) + dLng,
        y: Number(coord.latitude) + dLat,
      }))
      this.skipPathHistory = true
      this.parcelPolygon.setPath(this.coordsToLatLngPath(next))
      this.attachParcelPathListeners(this.parcelPolygon)
      this.capturePendingPolygonFromPolygon(this.parcelPolygon)
      this.skipPathHistory = false
      if (recordHistory) this.pushDrawHistory(label)
      this.syncMoveVertexMarkerPositions()
    },
    onParcelMoveEnd(event, grabIndex = null) {
      this.skipPathHistory = false
      const coords = this.cloneParcelSnapshot()
      if (coords.length < 3) return
      const index = Number.isInteger(grabIndex) && grabIndex >= 0
        ? grabIndex
        : this.nearestVertexIndex(coords, event?.latLng)
      const grab = coords[index] || coords[0]
      const { point, snapped } = this.getSnappedPointResult(
        { lat: grab.latitude, lng: grab.longitude },
        { excludeParcel: true },
      )
      if (snapped) {
        const dLat = point.lat - grab.latitude
        const dLng = point.lng - grab.longitude
        this.applyParcelTranslation(coords, dLat, dLng, { recordHistory: true, label: 'نقل المضلع مع التقاط' })
        this.$toast?.success?.('تم التقاط المضلع على نقطة قريبة')
        return
      }
      this.pushDrawHistory('نقل المضلع')
    },
    nearestVertexIndex(coords, latLng) {
      if (!latLng || !Array.isArray(coords) || !coords.length) return 0
      const target = {
        lat: Number(typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat),
        lng: Number(typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng),
      }
      let bestIndex = 0
      let bestDistance = Number.POSITIVE_INFINITY
      coords.forEach((coord, index) => {
        const distance = this.computeDistanceMetersBetweenPoints(
          { lat: coord.latitude, lng: coord.longitude },
          target,
        )
        if (distance < bestDistance) {
          bestDistance = distance
          bestIndex = index
        }
      })
      return bestIndex
    },
    getLayerLoadTitle(layerKey) {
      const item = MAP_LAYER_LOAD_ITEMS.find(entry => entry.key === layerKey)
      return item?.title || layerKey
    },
    beginLayerLoad(layerKeys) {
      layerKeys.forEach(layerKey => {
        this.$set(this.layerLoadState, layerKey, {
          loading: true,
          loaded: false,
          error: null,
        })
      })
    },
    finishLayerLoad(layerKey, { success = true, errorMessage = null, notifyOnError = true } = {}) {
      const title = this.getLayerLoadTitle(layerKey)
      const message = errorMessage || `فشل تحميل طبقة ${title}`
      this.$set(this.layerLoadState, layerKey, {
        loading: false,
        loaded: success,
        error: success ? null : message,
      })
      if (!success && notifyOnError && this.$toast) {
        this.$toast.error(message)
      }
    },
    finishLayerLoads(layerKeys, { success = true, errorMessage = null, notifyOnError = true } = {}) {
      layerKeys.forEach(layerKey => {
        this.finishLayerLoad(layerKey, { success, errorMessage, notifyOnError: false })
      })
      if (!success && notifyOnError && this.$toast) {
        this.$toast.error(errorMessage || 'فشل تحميل بعض طبقات الخريطة')
      }
    },
    isLayerLoading(layerKey) {
      return !!this.layerLoadState[layerKey]?.loading
    },
    isLayerLoaded(layerKey) {
      const state = this.layerLoadState[layerKey]
      return !!state?.loaded && !state?.error
    },
    isLayerErrored(layerKey) {
      return !!this.layerLoadState[layerKey]?.error
    },
    canRequestLayerLoad(layerKey) {
      if (layerKey === 'egyptGis') {
        return this.canViewEgyptGis && !this.isLayerLoading(layerKey) && this.isLayerErrored(layerKey)
      }
      if (!this.enableAllMapData && layerKey !== 'locationPolygons') return false
      if (isAdministrativeBoundaryLayerKey(layerKey) && !this.canViewAdministrativeBoundaries) return false
      if (this.isLayerLoading(layerKey)) return false
      if (isAdministrativeBoundaryLayerKey(layerKey)) {
        const hasGraphics = (this.overlayGroups?.[layerKey]?.length || 0) > 0
        if (this.isLayerLoaded(layerKey) && hasGraphics) return false
        return true
      }
      if (this.isLayerLoaded(layerKey)) return false
      return true
    },
    onLayerLoadItemClick(item) {
      if (!item?.key || !this.canRequestLayerLoad(item.key)) return
      if (item.key === 'egyptGis') {
        this.$refs.egyptGisPanel?.retry()
        return
      }
      this.enableLayerForLoad(item.key)
      this.ensureLayerDataLoaded(item.key)
    },
    enableLayerForLoad(layerKey) {
      if (Object.prototype.hasOwnProperty.call(this.layerVisibility, layerKey)) {
        this.layerVisibility[layerKey] = true
      }
      this.syncSketchLayersPanel()
      this.toggleLayer(layerKey)
    },
    async ensureLayerDataLoaded(layerKey, { force = false } = {}) {
      if (!layerKey || !this.map) return
      if (!this.enableAllMapData && layerKey !== 'locationPolygons') return

      const mapAllKeys = ['projects', 'mapLibraries', 'fixedPoints']
      if (mapAllKeys.includes(layerKey)) {
        if (mapAllKeys.some(key => this.isLayerLoading(key))) return
        if (mapAllKeys.every(key => this.isLayerLoaded(key))) return
        await this.loadMapAllData()
        return
      }

      if (isAdministrativeBoundaryLayerKey(layerKey)) {
        if (!this.canViewAdministrativeBoundaries) return
        if (this.isLayerLoading(layerKey)) return
        const type = typeFromAdministrativeBoundaryLayerKey(layerKey)
        const hasGraphics = (this.overlayGroups?.[layerKey]?.length || 0) > 0
        if (!force && this.isLayerLoaded(layerKey) && hasGraphics) return
        await this.loadAdministrativeBoundaryType(type, { force: force || !hasGraphics })
        return
      }

      if (GEODESIA_MAP_LAYER_KEYS.includes(layerKey)) {
        if (!force && (this.isLayerLoading(layerKey) || this.isLayerLoaded(layerKey))) return
        await this.loadGeodesiaMapLayer(layerKey, { force })
        return
      }

      if (layerKey === 'locationPolygons') {
        if (this.isLayerLoading(layerKey)) return
        if (!force && this.isLayerLoaded(layerKey)) return
        await this.loadAndRenderLocationPolygons()
      }
    },
    async loadRequestedDataLayers({ forceMapData = false } = {}) {
      if (!this.enableAllMapData) {
        if (this.isLayerLoaded('locationPolygons') || this.layerVisibility.locationPolygons) {
          await this.loadAndRenderLocationPolygons()
        }
        return
      }

      const tasks = []

      if (this.isLayerLoaded('locationPolygons') || this.layerVisibility.locationPolygons) {
        tasks.push(this.loadAndRenderLocationPolygons())
      }

      const mapAllKeys = ['projects', 'mapLibraries', 'fixedPoints']
      if (mapAllKeys.some(key => this.isLayerLoaded(key) || this.layerVisibility[key])) {
        tasks.push(this.loadMapAllData({ force: forceMapData }))
      }

      if (this.canViewAdministrativeBoundaries) {
        getAdministrativeBoundaryLayerLoadItems().forEach(item => {
          if (this.isLayerLoaded(item.key) || this.layerVisibility[item.key]) {
            tasks.push(this.loadAdministrativeBoundaryType(item.administrativeBoundaryType, { force: forceMapData }))
          }
        })
      }

      GEODESIA_MAP_LAYER_KEYS.forEach(key => {
        if (this.isLayerLoaded(key) || this.layerVisibility[key]) {
          tasks.push(this.loadGeodesiaMapLayer(key, { force: forceMapData }))
        }
      })

      await Promise.all(tasks)
    },
    setDataOverlaysClickable(clickable) {
      Object.keys(this.overlayGroups || {}).forEach(groupName => {
        if (groupName === 'sketchGraphics') return
        ;(this.overlayGroups[groupName] || []).forEach(item => {
          if (typeof item?.setClickable === 'function') {
            item.setClickable(clickable)
            return
          }
          item?.setOptions?.({ clickable })
        })
      })
    },
    isSelectToolActive() {
      return SELECT_TOOL_MODES.includes(this.sketchMode)
    },
    isMoveToolActive() {
      return this.sketchMode === 'move'
    },
    isSketchToolActive() {
      return !!this.sketchMode && !this.isSelectToolActive() && !this.isMoveToolActive()
    },
    updateSketchToolActiveState() {
      Object.entries(this.sketchToolButtons || {}).forEach(([mode, button]) => {
        if (mode === 'layers' || mode === 'delete' || mode === 'undo' || mode === 'redo' || mode === 'history' || !button) return
        button.classList.toggle('google-sketch-tool--active', this.sketchMode === mode)
      })
      if (this.drawPolygonButton) {
        this.drawPolygonButton.classList.toggle('draw-polygon-button--active', this.sketchMode === 'polygon')
      }
      this.emitDrawHistorySync()
    },
    toggleSketchTool(mode) {
      if (this.sketchMode === mode) {
        if (mode === 'polygon' && this.polygonDrawActive && this.polygonDrawPoints.length >= 3) {
          this.finishPolygonDrawMode()
          return
        }
        if (mode === 'polyline' && this.polygonDrawActive && this.polygonDrawPoints.length >= 2) {
          this.finishPolygonDrawMode()
          return
        }
        this.stopSketchTool()
        return
      }
      this.startSketchTool(mode)
    },
    startSketchTool(mode) {
      if (!this.map) return
      this.stopMeasureMode()
      this.stopSketchTool()
      this.sketchMode = mode
      this.sketchAnchorPoint = null
      this.selectAnchorPoint = null
      this.selectLassoPath = []
      this.updateSketchToolActiveState()

      if (this.isSelectToolActive()) {
        this.setDataOverlaysClickable(true)
        this.bindSketchGraphicsSelectListeners(true)
        if (mode === 'select') {
          this.restoreParcelEditInteraction()
          this.mapClickListener = this.map.addListener('click', event => {
            this.handleCursorSelectMapClick(event)
          })
          return
        }
        this.disableParcelEdit()
        this.setPolygonDrawMapInteraction(true)
        this.mapClickListener = this.map.addListener('click', event => {
          this.handleSelectShapeClick(event?.latLng)
        })
        this.sketchMoveListener = this.map.addListener('mousemove', event => {
          this.handleSelectMouseMove(event?.latLng)
        })
        if (mode === 'lasso-select') {
          this.polygonDrawDblClickListener = this.map.addListener('dblclick', event => {
            if (event?.stop) event.stop()
            this.finishLassoSelect()
          })
        }
        return
      }

      if (mode === 'move') {
        this.clearSelection()
        this.setDataOverlaysClickable(false)
        if (!this.parcelPolygon) {
          this.$toast?.warning?.('ارسم أو حدّد مضلعاً أولاً لنقله')
          this.sketchMode = null
          this.updateSketchToolActiveState()
          this.setDataOverlaysClickable(true)
          this.restoreParcelEditInteraction()
          return
        }
        this.enableParcelMoveMode()
        this.$toast?.info?.('اسحب المضلع من أي رأس أو من داخله. يلتقط الرأس أقرب نقطة مرسومة أو نقطة على الخريطة.')
        return
      }

      this.clearSelection()
      this.disableParcelEdit()
      if (mode === 'polygon' || mode === 'rectangle' || mode === 'circle') {
        this.preserveCommittedParcelBeforeNewDraw()
      }
      this.setDataOverlaysClickable(false)

      if (mode === 'polygon' || mode === 'polyline') {
        this.startPolygonDrawMode()
        return
      }

      this.setPolygonDrawMapInteraction(true)
      this.mapClickListener = this.map.addListener('click', event => {
        this.handleSketchClick(event?.latLng)
      })
      this.sketchMoveListener = this.map.addListener('mousemove', event => {
        this.handleSketchMouseMove(event?.latLng)
      })
      this.polygonDrawDblClickListener = this.map.addListener('dblclick', event => {
        if (event?.stop) event.stop()
      })
    },
    stopSketchTool() {
      const mode = this.sketchMode
      if (mode === 'move') {
        this.disableParcelMoveMode()
      }
      if (mode === 'polygon' || mode === 'polyline') {
        this.stopPolygonDrawMode()
      }

      this.bindSketchGraphicsSelectListeners(false)
      this.clearSelectPreview()
      this.sketchMode = null
      this.sketchAnchorPoint = null
      this.selectAnchorPoint = null
      this.selectLassoPath = []
      this.clearSketchPreview()

      if (this.mapClickListener && mode !== 'polygon' && mode !== 'polyline') {
        window.google.maps.event.removeListener(this.mapClickListener)
        this.mapClickListener = null
      }
      if (this.sketchMoveListener) {
        window.google.maps.event.removeListener(this.sketchMoveListener)
        this.sketchMoveListener = null
      }
      if (this.polygonDrawDblClickListener && mode !== 'polygon' && mode !== 'polyline') {
        window.google.maps.event.removeListener(this.polygonDrawDblClickListener)
        this.polygonDrawDblClickListener = null
        this.setPolygonDrawMapInteraction(false)
      }

      this.updateSketchToolActiveState()
      this.setDataOverlaysClickable(true)
      this.restoreParcelEditInteraction()
      this.teardownCommittedParcelPolygonIfUnchanged()
    },
    clearSketchPreview() {
      if (this.sketchPreviewOverlay?.setMap) {
        this.sketchPreviewOverlay.setMap(null)
      }
      this.sketchPreviewOverlay = null
    },
    clearSelectPreview() {
      this.clearSketchPreview()
    },
    bindSketchGraphicsSelectListeners(enabled) {
      this.selectGraphicListeners.forEach(listener => {
        if (listener?.remove) listener.remove()
        else if (listener) window.google.maps.event.removeListener(listener)
      })
      this.selectGraphicListeners = []
      if (!enabled) return

      ;(this.overlayGroups.sketchGraphics || []).forEach(item => {
        item?.setOptions?.({ clickable: true })
        if (!item?.addListener) return
        const listener = item.addListener('click', event => {
          if (this.sketchMode !== 'select') return
          if (event?.stop) event.stop()
          this.applySelection([item])
          this._ignoreNextSelectMapClick = true
          window.setTimeout(() => { this._ignoreNextSelectMapClick = false }, 0)
        })
        this.selectGraphicListeners.push(listener)
      })
    },
    handleCursorSelectMapClick(event) {
      if (this.sketchMode !== 'select') return
      if (this._ignoreNextSelectMapClick) {
        this._ignoreNextSelectMapClick = false
        return
      }
      const latLng = event?.latLng
      if (!latLng) {
        this.clearSelection()
        return
      }
      const hit = this.findSelectableOverlayAtLatLng(latLng)
      if (hit) this.applySelection([hit])
      else this.clearSelection()
    },
    handleSelectShapeClick(latLng) {
      if (!latLng) return false
      const { point } = this.getSnappedPointResult({
        lat: latLng.lat(),
        lng: latLng.lng(),
      })

      if (this.sketchMode === 'rectangle-select') {
        if (!this.selectAnchorPoint) {
          this.selectAnchorPoint = point
          return true
        }
        this.finishRectangleSelect(this.selectAnchorPoint, point)
        return true
      }

      if (this.sketchMode === 'lasso-select') {
        this.selectLassoPath.push(point)
        this.updateLassoSelectPreview(this.selectLassoPath, point)
        return true
      }

      return false
    },
    handleSelectMouseMove(latLng) {
      if (!latLng) return
      const cursor = { lat: latLng.lat(), lng: latLng.lng() }
      if (this.sketchMode === 'rectangle-select' && this.selectAnchorPoint) {
        this.updateSelectRectanglePreview(this.selectAnchorPoint, cursor)
        return
      }
      if (this.sketchMode === 'lasso-select' && this.selectLassoPath.length) {
        this.updateLassoSelectPreview(this.selectLassoPath, cursor)
      }
    },
    updateSelectRectanglePreview(start, end) {
      this.clearSketchPreview()
      this.sketchPreviewOverlay = new window.google.maps.Rectangle({
        bounds: this.boundsFromCorners(start, end),
        strokeColor: '#0079C1',
        strokeOpacity: 0.95,
        strokeWeight: 2,
        fillColor: '#0079C1',
        fillOpacity: 0.12,
        clickable: false,
        map: this.map,
      })
    },
    updateLassoSelectPreview(path, cursor) {
      this.clearSketchPreview()
      const previewPath = cursor ? [...path, cursor] : [...path]
      if (previewPath.length < 2) return
      this.sketchPreviewOverlay = new window.google.maps.Polyline({
        path: previewPath,
        strokeColor: '#0079C1',
        strokeOpacity: 0.95,
        strokeWeight: 2,
        clickable: false,
        map: this.map,
      })
    },
    finishRectangleSelect(start, end) {
      const bounds = this.boundsFromCorners(start, end)
      this.applySelection(this.getOverlaysInBounds(bounds))
      this.selectAnchorPoint = null
      this.clearSketchPreview()
    },
    finishLassoSelect() {
      if (this.selectLassoPath.length < 3) {
        this.$toast?.warning?.('أضف ثلاث نقاط على الأقل للوهق.')
        return
      }
      this.applySelection(this.getOverlaysInPolygon(this.selectLassoPath))
      this.selectLassoPath = []
      this.clearSketchPreview()
    },
    getSelectableOverlays() {
      const items = []
      if (this.parcelPolygon) items.push(this.parcelPolygon)
      ;(this.overlayGroups.sketchGraphics || []).forEach(item => {
        if (item) items.push(item)
      })
      return items
    },
    overlayLatLngs(overlay) {
      if (!overlay) return []
      if (typeof overlay.getPosition === 'function') {
        const position = overlay.getPosition()
        return position ? [{ lat: position.lat(), lng: position.lng() }] : []
      }
      if (typeof overlay.getPath === 'function') {
        const path = overlay.getPath()
        const arr = typeof path?.getArray === 'function' ? path.getArray() : []
        return arr.map(point => ({ lat: point.lat(), lng: point.lng() }))
      }
      if (typeof overlay.getBounds === 'function') {
        const bounds = overlay.getBounds()
        if (!bounds) return []
        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()
        return [
          { lat: ne.lat(), lng: ne.lng() },
          { lat: ne.lat(), lng: sw.lng() },
          { lat: sw.lat(), lng: sw.lng() },
          { lat: sw.lat(), lng: ne.lng() },
        ]
      }
      return []
    },
    pointInBounds(point, bounds) {
      return point.lat <= bounds.north
        && point.lat >= bounds.south
        && point.lng <= bounds.east
        && point.lng >= bounds.west
    },
    pointInRing(point, ring) {
      let inside = false
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const yi = ring[i].lat
        const xi = ring[i].lng
        const yj = ring[j].lat
        const xj = ring[j].lng
        const intersect = ((yi > point.lat) !== (yj > point.lat))
          && (point.lng < ((xj - xi) * (point.lat - yi)) / ((yj - yi) || 1e-12) + xi)
        if (intersect) inside = !inside
      }
      return inside
    },
    overlayIntersectsBounds(overlay, bounds) {
      const points = this.overlayLatLngs(overlay)
      if (points.some(point => this.pointInBounds(point, bounds))) return true
      if (typeof overlay.getPath === 'function' && points.length >= 3) {
        const corners = [
          { lat: bounds.north, lng: bounds.west },
          { lat: bounds.north, lng: bounds.east },
          { lat: bounds.south, lng: bounds.east },
          { lat: bounds.south, lng: bounds.west },
        ]
        return corners.some(corner => this.pointInRing(corner, points))
      }
      return false
    },
    overlayIntersectsPolygon(overlay, ring) {
      const points = this.overlayLatLngs(overlay)
      if (points.some(point => this.pointInRing(point, ring))) return true
      if (typeof overlay.getPath === 'function' && points.length >= 3) {
        return ring.some(vertex => this.pointInRing(vertex, points))
      }
      return false
    },
    getOverlaysInBounds(bounds) {
      return this.getSelectableOverlays().filter(overlay => this.overlayIntersectsBounds(overlay, bounds))
    },
    getOverlaysInPolygon(ring) {
      return this.getSelectableOverlays().filter(overlay => this.overlayIntersectsPolygon(overlay, ring))
    },
    findSelectableOverlayAtLatLng(latLng) {
      const point = { lat: latLng.lat(), lng: latLng.lng() }
      const overlays = this.getSelectableOverlays()
      for (let i = overlays.length - 1; i >= 0; i -= 1) {
        const overlay = overlays[i]
        if (typeof overlay.getPosition === 'function') {
          const position = overlay.getPosition()
          if (!position) continue
          const dLat = Math.abs(position.lat() - point.lat)
          const dLng = Math.abs(position.lng() - point.lng)
          if (dLat < 0.00015 && dLng < 0.00015) return overlay
          continue
        }
        const ring = this.overlayLatLngs(overlay)
        if (ring.length >= 3 && this.pointInRing(point, ring)) return overlay
        if (ring.length >= 2 && ring.some(vertex => (
          Math.abs(vertex.lat - point.lat) < 0.00012 && Math.abs(vertex.lng - point.lng) < 0.00012
        ))) return overlay
      }
      return null
    },
    applySelection(overlays) {
      const unique = []
      overlays.forEach(item => {
        if (item && !unique.includes(item)) unique.push(item)
      })
      this.clearSelection()
      this.selectedOverlays = unique
      unique.forEach(item => {
        if (item === this.parcelPolygon) {
          this.enableParcelEdit()
          return
        }
        if (typeof item.getPath === 'function' && typeof item.getPosition !== 'function') {
          item.__selectRestore = {
            strokeWeight: item.strokeWeight,
            editable: item.getEditable?.() || false,
          }
          item.setOptions?.({ editable: true, strokeWeight: 4, clickable: true })
          return
        }
        if (typeof item.getPosition === 'function') {
          item.setAnimation?.(window.google.maps.Animation.BOUNCE)
          window.setTimeout(() => item.setAnimation?.(null), 650)
        }
      })
      this.updateDeleteSelectionButtonState()
    },
    clearSelection() {
      this.selectedOverlays.forEach(item => {
        if (item === this.parcelPolygon) return
        if (item?.__selectRestore) {
          item.setOptions?.(item.__selectRestore)
          item.__selectRestore = null
        }
      })
      this.selectedOverlays = []
      if (this.sketchMode !== 'select') this.disableParcelEdit()
      this.updateDeleteSelectionButtonState()
    },
    hasMeasurements() {
      return this.measureOverlays.length > 0
    },
    hasDeletableSelection() {
      if (this.selectedOverlays.length > 0) return true
      if (this.parcelPolygon?.getEditable?.()) return true
      if (this.isMoveToolActive() && this.parcelPolygon) return true
      return this.hasMeasurements()
    },
    updateDeleteSelectionButtonState() {
      if (!this.deleteSelectionButton) return
      const canDelete = this.hasDeletableSelection()
      this.deleteSelectionButton.disabled = !canDelete
      this.deleteSelectionButton.classList.toggle('save-polygon-button--disabled', !canDelete)
    },
    getDeleteSelectionButtonIconHtml() {
      return '<span class="delete-selection-button__icon" aria-hidden="true">'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>'
        + '<line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>'
        + '</svg></span>'
    },
    bindSelectionKeyboard() {
      if (this._onSelectionKeyDown) return
      this._onSelectionKeyDown = event => {
        const target = event.target
        const tag = target?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
          return
        }
        const key = String(event.key || '')
        const isUndo = (event.ctrlKey || event.metaKey) && key.toLowerCase() === 'z' && !event.shiftKey
        const isRedo = (event.ctrlKey || event.metaKey) && (
          key.toLowerCase() === 'y'
          || (key.toLowerCase() === 'z' && event.shiftKey)
        )
        if (isUndo) {
          event.preventDefault()
          event.stopPropagation()
          this.undoDrawAction()
          return
        }
        if (isRedo) {
          event.preventDefault()
          event.stopPropagation()
          this.redoDrawAction()
          return
        }
        if (event.key !== 'Delete' && event.key !== 'Backspace') return
        if (!this.hasDeletableSelection()) return
        event.preventDefault()
        event.stopPropagation()
        this.deleteSelectedGraphics()
      }
      document.addEventListener('keydown', this._onSelectionKeyDown, true)
    },
    unbindSelectionKeyboard() {
      if (!this._onSelectionKeyDown) return
      document.removeEventListener('keydown', this._onSelectionKeyDown, true)
      this._onSelectionKeyDown = null
    },
    deleteSelectedGraphics() {
      const targets = this.selectedOverlays.length
        ? [...this.selectedOverlays]
        : ((this.parcelPolygon?.getEditable?.() || this.isMoveToolActive()) ? [this.parcelPolygon] : [])
      if (!targets.length && !this.hasMeasurements()) return

      const willDeleteParcel = targets.includes(this.parcelPolygon)

      let deletedParcel = false
      targets.forEach(item => {
        if (item === this.parcelPolygon) {
          deletedParcel = true
          return
        }
        item?.setMap?.(null)
        const graphics = this.overlayGroups.sketchGraphics || []
        const index = graphics.indexOf(item)
        if (index >= 0) graphics.splice(index, 1)
      })

      this.selectedOverlays = []
      if (deletedParcel) {
        this.disableParcelMoveMode()
        this.teardownParcelPolygon()
        this.pendingPolygonCoordinates = []
        this.$emit('coordinates-changed', [])
        this.updateSavePolygonButtonState()
        this.pushDrawHistory('حذف المضلع')
      }
      this.clearAllMeasurements()
      this.updateDeleteSelectionButtonState()
      if (this.sketchMode === 'select') {
        this.bindSketchGraphicsSelectListeners(true)
      }
    },
    handleSketchClick(latLng) {
      if (!latLng || !this.sketchMode) return false
      const { point: snappedPoint, snapped } = this.getSnappedPointResult({
        lat: latLng.lat(),
        lng: latLng.lng(),
      })

      if (this.sketchMode === 'point') {
        this.addSketchPoint(snappedPoint, snapped)
        return true
      }

      if (this.sketchMode === 'rectangle' || this.sketchMode === 'circle') {
        if (!this.sketchAnchorPoint) {
          this.sketchAnchorPoint = snappedPoint
          this.addSketchVertexMarker(snappedPoint, snapped)
          return true
        }
        if (this.sketchMode === 'rectangle') {
          this.finishSketchRectangle(this.sketchAnchorPoint, snappedPoint)
        } else {
          this.finishSketchCircle(this.sketchAnchorPoint, snappedPoint)
        }
        return true
      }

      return false
    },
    handleSketchMouseMove(latLng) {
      if (!latLng || !this.sketchAnchorPoint) return
      if (this.sketchMode !== 'rectangle' && this.sketchMode !== 'circle') return

      const cursor = {
        lat: latLng.lat(),
        lng: latLng.lng(),
      }

      if (this.sketchMode === 'rectangle') {
        this.updateRectanglePreview(this.sketchAnchorPoint, cursor)
        return
      }
      this.updateCirclePreview(this.sketchAnchorPoint, cursor)
    },
    addSketchVertexMarker(point, snapped) {
      const marker = new window.google.maps.Marker({
        position: point,
        map: this.map,
        clickable: false,
        icon: this.buildPointMarkerIcon(snapped ? SNAP_POINT_COLOR : DRAW_POINT_COLOR),
      })
      if (snapped) this.animateSnapFeedback(marker)
      this.overlayGroups.sketchGraphics.push(marker)
    },
    addSketchPoint(point, snapped) {
      const marker = new window.google.maps.Marker({
        position: point,
        map: this.map,
        title: 'نقطة مرسومة',
        icon: this.buildPointMarkerIcon(snapped ? SNAP_POINT_COLOR : '#2563EB', { scale: 6 }),
      })
      if (snapped) this.animateSnapFeedback(marker)
      this.overlayGroups.sketchGraphics.push(marker)
    },
    updateRectanglePreview(start, end) {
      this.clearSketchPreview()
      this.sketchPreviewOverlay = new window.google.maps.Rectangle({
        bounds: this.boundsFromCorners(start, end),
        strokeColor: DRAW_POINT_COLOR,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: DRAW_POINT_COLOR,
        fillOpacity: 0.12,
        clickable: false,
        map: this.map,
      })
    },
    updateCirclePreview(center, edge) {
      this.clearSketchPreview()
      const radius = this.computeDistanceMetersBetweenPoints(center, edge)
      this.sketchPreviewOverlay = new window.google.maps.Circle({
        center,
        radius,
        strokeColor: DRAW_POINT_COLOR,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: DRAW_POINT_COLOR,
        fillOpacity: 0.12,
        clickable: false,
        map: this.map,
      })
    },
    boundsFromCorners(start, end) {
      return {
        north: Math.max(start.lat, end.lat),
        south: Math.min(start.lat, end.lat),
        east: Math.max(start.lng, end.lng),
        west: Math.min(start.lng, end.lng),
      }
    },
    finishSketchRectangle(start, end) {
      const bounds = this.boundsFromCorners(start, end)
      const path = [
        { lat: bounds.north, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east },
        { lat: bounds.south, lng: bounds.east },
        { lat: bounds.south, lng: bounds.west },
      ]
      this.commitSketchPolygon(path)
    },
    finishSketchCircle(center, edge) {
      const radius = this.computeDistanceMetersBetweenPoints(center, edge)
      if (!(radius > 0.2)) {
        this.$toast?.warning?.('ارسم دائرة أكبر.')
        return
      }
      const path = this.buildCirclePath(center, radius)
      this.commitSketchPolygon(path)
    },
    buildCirclePath(center, radiusMeters) {
      const path = []
      if (this.geometrySpherical?.computeOffset && window.google?.maps?.LatLng) {
        const origin = new window.google.maps.LatLng(center.lat, center.lng)
        for (let i = 0; i < SKETCH_CIRCLE_SIDES; i += 1) {
          const heading = (360 * i) / SKETCH_CIRCLE_SIDES
          const point = this.geometrySpherical.computeOffset(origin, radiusMeters, heading)
          path.push({ lat: point.lat(), lng: point.lng() })
        }
        return path
      }

      const latRad = (center.lat * Math.PI) / 180
      const metersPerDegLat = 111320
      const metersPerDegLng = 111320 * Math.cos(latRad) || 111320
      for (let i = 0; i < SKETCH_CIRCLE_SIDES; i += 1) {
        const angle = (2 * Math.PI * i) / SKETCH_CIRCLE_SIDES
        path.push({
          lat: center.lat + ((Math.cos(angle) * radiusMeters) / metersPerDegLat),
          lng: center.lng + ((Math.sin(angle) * radiusMeters) / metersPerDegLng),
        })
      }
      return path
    },
    commitSketchPolygon(path) {
      if (!Array.isArray(path) || path.length < 3) return

      this.clearOverlayGroup('sketchGraphics')
      this.clearSketchPreview()

      const polygon = new window.google.maps.Polygon({
        paths: path,
        ...PARCEL_POLYGON_STYLE,
        editable: false,
        draggable: false,
      })

      this.suppressPropRedrawUntil = Date.now() + 8000
      this.setParcelPolygon(polygon, { editable: true })
      this.pendingPolygonCoordinates = this.latLngPathToCoordinates(polygon.getPath())
      this.updateSavePolygonButtonState()

      const area = this.geometrySpherical?.computeArea?.(polygon.getPath())
      const areaText = Number.isFinite(Number(area))
        ? `المساحة: ${Number(area).toFixed(2)} م²`
        : ''
      this.promptSavePolygonChoice(areaText)
      this.pushDrawHistory('رسم مضلع')

      this.stopSketchTool()
      this.$nextTick(() => this.focusCurrentPolygon())
    },
    finishSketchPolyline() {
      if (this.polygonDrawPoints.length < 2) {
        this.$toast?.warning?.('أضف نقطتين على الأقل لرسم الخط.')
        return
      }

      const polyline = new window.google.maps.Polyline({
        path: [...this.polygonDrawPoints],
        strokeColor: '#2563EB',
        strokeOpacity: 0.95,
        strokeWeight: 3,
        map: this.map,
      })
      this.overlayGroups.sketchGraphics.push(polyline)

      if (this.geometrySpherical) {
        const meters = this.geometrySpherical.computeLength(
          this.polygonDrawPoints.map(point => new window.google.maps.LatLng(point.lat, point.lng)),
        )
        if (this.$toast) {
          this.$toast.success(`تم رسم خط بطول ${this.formatMeasureDistance(meters)}`)
        }
      }

      this.stopSketchTool()
    },
    updateImageryYearControlState() {
      if (!this.imageryYearControl) return

      this.imageryYearControl.querySelectorAll('.imagery-year-control__button').forEach(button => {
        const isActive = Number(button.dataset.year) === this.imageryYear
        button.classList.toggle('imagery-year-control__button--active', isActive)
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      })
    },
    getDrawPolygonButtonIconHtml() {
      return esriSketchToolIcon('polygon')
    },
    togglePolygonDrawMode() {
      this.toggleSketchTool('polygon')
    },
    getMeasureDistanceButtonIconHtml() {
      return '<span class="measure-distance-button__icon" aria-hidden="true">'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M3 21 21 3"/><path d="M6 18 9 15"/><path d="M15 9 18 6"/><path d="M9 9l1 1"/><path d="M14 14l1 1"/>'
        + '</svg></span>'
    },
    getMeasureAreaButtonIconHtml() {
      return '<span class="measure-area-button__icon" aria-hidden="true">'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>'
        + '</svg></span>'
    },
    getSnapThresholdMeters(lat) {
      const zoom = Number(this.map?.getZoom?.() ?? 0)
      const safeZoom = Number.isFinite(zoom) ? zoom : 0
      const latRad = (Number(lat) || 0) * (Math.PI / 180)
      const metersPerPixel = (156543.03392 * Math.cos(latRad)) / (2 ** safeZoom)
      if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return 0
      return metersPerPixel * SNAP_THRESHOLD_PX
    },
    getCloseSnapThresholdPx() {
      return window.matchMedia('(max-width: 767.98px), (pointer: coarse)').matches
        ? CLOSE_SNAP_THRESHOLD_PX_TOUCH
        : CLOSE_SNAP_THRESHOLD_PX
    },
    isLatLngNearFirstDrawVertex(latLng) {
      if (!this.polygonDrawActive || this.sketchMode === 'polyline' || this.polygonDrawPoints.length < 3) {
        return false
      }
      const first = this.polygonDrawPoints[0]
      if (!first || !latLng) return false
      const click = {
        lat: Number(typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat),
        lng: Number(typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng),
      }
      const projection = this.map?.getProjection?.()
      const zoom = Number(this.map?.getZoom?.() ?? 0)
      if (projection?.fromLatLngToPoint && Number.isFinite(zoom)) {
        const a = projection.fromLatLngToPoint(new window.google.maps.LatLng(click.lat, click.lng))
        const b = projection.fromLatLngToPoint(new window.google.maps.LatLng(first.lat, first.lng))
        if (a && b) {
          const scale = 2 ** zoom
          const pixels = Math.hypot((a.x - b.x) * scale, (a.y - b.y) * scale)
          if (pixels <= this.getCloseSnapThresholdPx()) return true
        }
      }
      const closeMeters = this.getSnapThresholdMeters(first.lat) * (
        window.matchMedia('(pointer: coarse)').matches ? 3.2 : 2.2
      )
      return this.computeDistanceMetersBetweenPoints(click, first) <= closeMeters
    },
    canFinishPolygonDraw() {
      if (!this.polygonDrawActive) return false
      return this.sketchMode === 'polyline'
        ? this.polygonDrawPoints.length >= 2
        : this.polygonDrawPoints.length >= 3
    },
    ensureFinishPolygonButton() {
      if (this.finishPolygonButton || !this.map || !window.google?.maps?.ControlPosition) return
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'google-finish-polygon-button'
      button.hidden = true
      button.addEventListener('click', event => {
        event.preventDefault()
        event.stopPropagation()
        this.finishPolygonDrawMode()
      })
      this.finishPolygonButton = button
      this.map.controls[window.google.maps.ControlPosition.BOTTOM_CENTER].push(button)
    },
    syncFinishPolygonButton() {
      this.ensureFinishPolygonButton()
      const button = this.finishPolygonButton
      if (!button) return
      const canFinish = this.canFinishPolygonDraw()
      button.hidden = !canFinish
      button.disabled = !canFinish
      button.textContent = this.sketchMode === 'polyline' ? 'إنهاء الخط' : 'إنهاء المضلع'
    },
    syncClosingVertexMarker() {
      const first = this.polygonDrawMarkers?.[0]
      if (this.polygonDrawFirstVertexListener) {
        window.google.maps.event.removeListener(this.polygonDrawFirstVertexListener)
        this.polygonDrawFirstVertexListener = null
      }
      if (!first) return
      const canClose = this.canFinishPolygonDraw() && this.sketchMode !== 'polyline'
      first.setOptions({
        optimized: false,
        clickable: true,
        zIndex: 40,
        cursor: canClose ? 'pointer' : 'default',
        title: canClose ? 'اضغط هنا لإغلاق المضلع' : '',
        icon: this.buildPointMarkerIcon(canClose ? SNAP_POINT_COLOR : DRAW_POINT_COLOR, {
          scale: canClose ? 11 : 5,
          strokeWeight: canClose ? 3 : 1,
          strokeColor: canClose ? '#0a3345' : '#fff',
        }),
      })
      if (!canClose) return
      this.polygonDrawFirstVertexListener = first.addListener('click', event => {
        if (event?.stop) event.stop()
        this._ignoreNextPolygonDrawClick = true
        window.setTimeout(() => { this._ignoreNextPolygonDrawClick = false }, 400)
        this.finishPolygonDrawMode()
      })
    },
    getSnapCandidatePoints({ excludeParcel = false } = {}) {
      const points = []
      const seen = new Set()
      const pushPoint = candidate => {
        const lat = Number(candidate?.lat)
        const lng = Number(candidate?.lng)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
        const key = `${lat.toFixed(8)}_${lng.toFixed(8)}`
        if (seen.has(key)) return
        seen.add(key)
        points.push({ lat, lng })
      }

      if (!excludeParcel) {
        const parcelPath = this.parcelPolygon?.getPath?.()
        iterateLatLngPath(parcelPath, latLng => {
          pushPoint({ lat: latLng.lat(), lng: latLng.lng() })
        })
      }

      this.polygonDrawPoints.forEach(pushPoint)
      this.measurePoints.forEach(pushPoint)
      this.measureOverlays.forEach(entry => {
        const path = Array.isArray(entry?.labelPath) ? entry.labelPath : []
        path.forEach(pushPoint)
      })

      const snapGroups = [
        'sketchGraphics',
        'importedPoints',
        'otherProjectLocations',
      ]
      if (this.layerVisibility.userLocation) snapGroups.push('userLocation')
      if (this.layerVisibility.projects) snapGroups.push('projects')
      if (this.layerVisibility.mapLibraries) snapGroups.push('mapLibraries')
      if (this.layerVisibility.locationPolygons) snapGroups.push('locationPolygons')
      if (this.layerVisibility.fixedPoints) snapGroups.push('fixedPoints')
      if (this.layerVisibility.vrsCoordinates) snapGroups.push('vrsCoordinates')
      if (this.layerVisibility.jxlCoordinates) snapGroups.push('jxlCoordinates')
      if (this.layerVisibility.jxlRw5Coordinates) snapGroups.push('jxlRw5Coordinates')
      if (this.layerVisibility.jxlRawCoordinates) snapGroups.push('jxlRawCoordinates')
      if (this.layerVisibility.jxlPolygons) snapGroups.push('jxlPolygons')
      if (this.layerVisibility.jxlBaselineLines) snapGroups.push('jxlBaselineLines')
      ADMINISTRATIVE_BOUNDARY_TYPES.forEach(item => {
        const key = administrativeBoundaryLayerKey(item.type)
        if (this.layerVisibility[key]) snapGroups.push(key)
      })

      snapGroups.forEach(groupName => {
        (this.overlayGroups[groupName] || []).forEach(overlay => {
          this.collectOverlayLatLngs(overlay, pushPoint)
        })
      })

      return points
    },
    collectOverlayLatLngs(overlay, pushPoint) {
      if (!overlay || typeof pushPoint !== 'function') return

      if (typeof overlay.getPosition === 'function') {
        const position = overlay.getPosition()
        if (position) {
          pushPoint({
            lat: typeof position.lat === 'function' ? position.lat() : position.lat,
            lng: typeof position.lng === 'function' ? position.lng() : position.lng,
          })
        }
      }

      if (typeof overlay.getCenter === 'function') {
        const center = overlay.getCenter()
        if (center) {
          pushPoint({
            lat: typeof center.lat === 'function' ? center.lat() : center.lat,
            lng: typeof center.lng === 'function' ? center.lng() : center.lng,
          })
        }
      }

      if (typeof overlay.getPath === 'function') {
        iterateLatLngPath(overlay.getPath(), latLng => {
          pushPoint({ lat: latLng.lat(), lng: latLng.lng() })
        })
      }

      if (typeof overlay.getPaths === 'function') {
        const paths = overlay.getPaths()
        if (paths?.getLength) {
          for (let index = 0; index < paths.getLength(); index += 1) {
            iterateLatLngPath(paths.getAt(index), latLng => {
              pushPoint({ lat: latLng.lat(), lng: latLng.lng() })
            })
          }
        }
      }
    },
    computeDistanceMetersBetweenPoints(a, b) {
      if (!a || !b || !window.google?.maps?.LatLng) return Number.POSITIVE_INFINITY

      if (this.geometrySpherical?.computeDistanceBetween) {
        return this.geometrySpherical.computeDistanceBetween(
          new window.google.maps.LatLng(a.lat, a.lng),
          new window.google.maps.LatLng(b.lat, b.lng),
        )
      }

      const toRad = value => (value * Math.PI) / 180
      const earthRadiusMeters = 6371000
      const lat1 = toRad(Number(a.lat) || 0)
      const lng1 = toRad(Number(a.lng) || 0)
      const lat2 = toRad(Number(b.lat) || 0)
      const lng2 = toRad(Number(b.lng) || 0)
      const dLat = lat2 - lat1
      const dLng = lng2 - lng1
      const h = (Math.sin(dLat / 2) ** 2)
        + Math.cos(lat1) * Math.cos(lat2) * (Math.sin(dLng / 2) ** 2)
      return 2 * earthRadiusMeters * Math.asin(Math.min(1, Math.sqrt(h)))
    },
    buildPointMarkerIcon(color, options = {}) {
      const {
        scale = 5,
        fillOpacity = 1,
        strokeColor = '#fff',
        strokeWeight = 1,
      } = options
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale,
        fillColor: color,
        fillOpacity,
        strokeColor,
        strokeWeight,
      }
    },
    animateSnapFeedback(marker) {
      if (!marker?.setAnimation || !window.google?.maps?.Animation) return
      marker.setAnimation(window.google.maps.Animation.BOUNCE)
      window.setTimeout(() => {
        marker?.setAnimation?.(null)
      }, SNAP_EFFECT_BOUNCE_MS)
    },
    getSnappedPointResult(point, options = {}) {
      const fallback = { point, snapped: false }
      if (!point || !this.map) return fallback

      const thresholdMeters = this.getSnapThresholdMeters(point.lat)
      if (!(thresholdMeters > 0)) return fallback

      let nearest = null
      let nearestDistance = Number.POSITIVE_INFINITY
      this.getSnapCandidatePoints(options).forEach(candidate => {
        const distance = this.computeDistanceMetersBetweenPoints(point, candidate)
        if (distance < nearestDistance) {
          nearestDistance = distance
          nearest = candidate
        }
      })

      if (!nearest || nearestDistance > thresholdMeters) return fallback
      return {
        point: { lat: nearest.lat, lng: nearest.lng },
        snapped: true,
      }
    },
    snapPointIfNearby(point) {
      return this.getSnappedPointResult(point).point
    },
    addMeasurePointFromLatLng(latLng) {
      if (!this.measureMode || !latLng || !this.map) return false

      const { point: snappedPoint, snapped } = this.getSnappedPointResult({
        lat: latLng.lat(),
        lng: latLng.lng(),
      })
      this.measurePoints.push(snappedPoint)

      const markerColor = this.measureMode === 'distance' ? '#E65100' : '#6A1B9A'
      const marker = new window.google.maps.Marker({
        position: snappedPoint,
        map: this.map,
        clickable: false,
        icon: this.getMeasureMarkerIcon(snapped ? SNAP_POINT_COLOR : markerColor),
      })
      if (snapped) this.animateSnapFeedback(marker)
      this.measureMarkers.push(marker)
      this.measureCursorPoint = null
      this.updateMeasurePreview()
      this.updateMeasureLiveLabel()

      if (this.measureMode === 'distance' && this.measurePoints.length >= 2) {
        window.setTimeout(() => this.finishMeasureMode(), 0)
      }
      return true
    },
    addPolygonDrawPointFromLatLng(latLng) {
      if (!this.polygonDrawActive || !latLng || !this.map) return false
      if (this._ignoreNextPolygonDrawClick) return false

      if (this.isLatLngNearFirstDrawVertex(latLng)) {
        const firstMarker = this.polygonDrawMarkers[0]
        if (firstMarker) this.animateSnapFeedback(firstMarker)
        window.setTimeout(() => this.finishPolygonDrawMode(), 0)
        return true
      }

      const { point: snappedPoint, snapped } = this.getSnappedPointResult({
        lat: latLng.lat(),
        lng: latLng.lng(),
      })

      this.polygonDrawPoints.push(snappedPoint)

      const marker = new window.google.maps.Marker({
        position: snappedPoint,
        map: this.map,
        optimized: false,
        clickable: true,
        zIndex: 8,
        icon: this.buildPointMarkerIcon(snapped ? SNAP_POINT_COLOR : DRAW_POINT_COLOR),
      })
      if (snapped) this.animateSnapFeedback(marker)
      this.polygonDrawMarkers.push(marker)
      this.updatePolygonDrawPreview()
      this.syncClosingVertexMarker()
      this.syncFinishPolygonButton()
      const pointCount = this.polygonDrawPoints.length
      this.pushDrawHistory(
        this.sketchMode === 'polyline' ? `نقطة خط ${pointCount}` : `نقطة رسم ${pointCount}`,
      )
      if (this.sketchMode !== 'polyline' && pointCount === 3) {
        this.$toast?.info?.('لإغلاق المضلع: اضغط النقطة الأولى الكبيرة، أو زر إنهاء المضلع أسفل الشاشة.')
      }
      return true
    },
    consumeOverlayClickForActiveTool(event) {
      if (!event?.latLng) return false
      if (
        !this.polygonDrawActive
        && !this.measureMode
        && !this.isSketchToolActive()
        && !this.isSelectToolActive()
      ) return false

      if (event?.stop) event.stop()
      if (this.sketchMode === 'select') {
        this.handleCursorSelectMapClick(event)
        this._ignoreNextSelectMapClick = true
        window.setTimeout(() => { this._ignoreNextSelectMapClick = false }, 0)
        return true
      }
      if (this.sketchMode === 'rectangle-select' || this.sketchMode === 'lasso-select') {
        return this.handleSelectShapeClick(event.latLng)
      }
      if (this.sketchMode === 'point' || this.sketchMode === 'rectangle' || this.sketchMode === 'circle') {
        return this.handleSketchClick(event.latLng)
      }
      if (this.polygonDrawActive) return this.addPolygonDrawPointFromLatLng(event.latLng)
      if (this.measureMode) return this.addMeasurePointFromLatLng(event.latLng)
      return false
    },
    setPolygonDrawMapInteraction(active) {
      if (!this.map) return

      if (active) {
        this.polygonDrawPreviousDoubleClickZoom = this.map.get('disableDoubleClickZoom')
        this.map.setOptions({ disableDoubleClickZoom: true })
        return
      }

      if (this.polygonDrawPreviousDoubleClickZoom !== null) {
        this.map.setOptions({ disableDoubleClickZoom: this.polygonDrawPreviousDoubleClickZoom })
        this.polygonDrawPreviousDoubleClickZoom = null
      }
    },
    formatMeasureDistance(meters) {
      const value = Number(meters) || 0
      return `${(value / 1000).toFixed(2)} km`
    },
    formatMeasureArea(squareMeters) {
      const value = Number(squareMeters) || 0
      return `${(value / 1_000_000).toFixed(4)} km²`
    },
    formatDimensionMeters(meters) {
      const value = Math.abs(Number(meters) || 0)
      if (value >= 100) return `${value.toFixed(1)} م`
      return `${value.toFixed(2)} م`
    },
    formatDimensionArea(squareMeters) {
      const value = Math.abs(Number(squareMeters) || 0)
      return `${value.toFixed(2)} م²`
    },
    getDimensionOverlayClass() {
      if (this._DimensionOverlayClass || !window.google?.maps?.OverlayView) {
        return this._DimensionOverlayClass || null
      }
      class DimensionOverlay extends window.google.maps.OverlayView {
        constructor({ position, text, angle = 0, variant = 'edge' }) {
          super()
          this.position = position
          this.text = text
          this.angle = angle
          this.variant = variant
          this.div = null
        }
        onAdd() {
          this.div = document.createElement('div')
          this.div.className = this.variant === 'area'
            ? 'google-dimension-label google-dimension-label--area'
            : 'google-dimension-label'
          this.div.textContent = this.text
          this.getPanes()?.overlayMouseTarget?.appendChild(this.div)
        }
        draw() {
          const projection = this.getProjection()
          if (!projection || !this.div || !this.position) return
          const point = projection.fromLatLngToDivPixel(this.position)
          if (!point) return
          this.div.style.left = `${point.x}px`
          this.div.style.top = `${point.y}px`
          this.div.style.transform = `translate(-50%, -50%) rotate(${this.angle}deg)`
        }
        onRemove() {
          this.div?.parentNode?.removeChild(this.div)
          this.div = null
        }
      }
      this._DimensionOverlayClass = DimensionOverlay
      return this._DimensionOverlayClass
    },
    pathToLatLngPoints(path) {
      if (!path) return []
      const length = typeof path.getLength === 'function' ? path.getLength() : path.length
      const points = []
      for (let i = 0; i < length; i += 1) {
        const item = typeof path.getAt === 'function' ? path.getAt(i) : path[i]
        if (!item) continue
        const lat = Number(typeof item.lat === 'function' ? item.lat() : item.lat ?? item.latitude)
        const lng = Number(typeof item.lng === 'function' ? item.lng() : item.lng ?? item.longitude)
        if (Number.isFinite(lat) && Number.isFinite(lng)) points.push({ lat, lng })
      }
      return points
    },
    getParcelDimensionRings() {
      if (this.polygonDrawActive && this.polygonDrawPoints.length >= 2) {
        return [this.normalizeDrawPoints(this.polygonDrawPoints)]
      }
      if (this.parcelPolygon?.getPath) {
        const ring = this.pathToLatLngPoints(this.parcelPolygon.getPath())
        if (ring.length >= 2) return [ring]
      }
      return []
    },
    computeRingCentroid(ring) {
      if (!Array.isArray(ring) || !ring.length) return null
      const sum = ring.reduce((acc, point) => ({
        lat: acc.lat + point.lat,
        lng: acc.lng + point.lng,
      }), { lat: 0, lng: 0 })
      return {
        lat: sum.lat / ring.length,
        lng: sum.lng / ring.length,
      }
    },
    computeEdgeLabelPlacement(start, end, centroid, lengthMeters) {
      const latRad = (((start.lat + end.lat) / 2) * Math.PI) / 180
      const metersPerDegLng = 111320 * Math.cos(latRad) || 111320
      const metersPerDegLat = 111320
      let dxM = (end.lng - start.lng) * metersPerDegLng
      let dyM = (end.lat - start.lat) * metersPerDegLat
      const edgeLen = Math.hypot(dxM, dyM) || 1
      dxM /= edgeLen
      dyM /= edgeLen
      let nx = -dyM
      let ny = dxM
      if (centroid) {
        const midLng = (start.lng + end.lng) / 2
        const midLat = (start.lat + end.lat) / 2
        const toCentroidX = (centroid.lng - midLng) * metersPerDegLng
        const toCentroidY = (centroid.lat - midLat) * metersPerDegLat
        if ((nx * toCentroidX) + (ny * toCentroidY) > 0) {
          nx = -nx
          ny = -ny
        }
      }

      const along = lengthMeters < 6 ? 0.5 : 0.2
      const offsetM = Math.min(3.2, Math.max(1.1, lengthMeters * 0.05))
      const labelLng = start.lng
        + ((end.lng - start.lng) * along)
        + ((nx * offsetM) / metersPerDegLng)
      const labelLat = start.lat
        + ((end.lat - start.lat) * along)
        + ((ny * offsetM) / metersPerDegLat)

      let angle = -Math.atan2(dyM, dxM) * (180 / Math.PI)
      if (angle > 90) angle -= 180
      if (angle < -90) angle += 180

      return {
        position: { lat: labelLat, lng: labelLng },
        angle,
      }
    },
    clearParcelDimensions() {
      if (this._dimensionRefreshTimer) {
        window.clearTimeout(this._dimensionRefreshTimer)
        this._dimensionRefreshTimer = null
      }
      (this.parcelDimensionOverlays || []).forEach(overlay => overlay?.setMap?.(null))
      this.parcelDimensionOverlays = []
    },
    scheduleParcelDimensionsRefresh() {
      if (this._dimensionRefreshTimer) window.clearTimeout(this._dimensionRefreshTimer)
      this._dimensionRefreshTimer = window.setTimeout(() => {
        this._dimensionRefreshTimer = null
        this.refreshParcelDimensions()
      }, 40)
    },
    refreshParcelDimensions() {
      this.clearParcelDimensions()
      if (!this.isParcelDimensionsVisible()) return
      if (!this.map || !window.google?.maps) return
      const OverlayClass = this.getDimensionOverlayClass()
      if (!OverlayClass) return

      const rings = this.getParcelDimensionRings()
      rings.forEach(ring => {
        const points = ring.filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
        if (points.length < 2) return
        const closed = points.length >= 3
        const vertexCount = closed ? points.length : points.length - 1
        const centroid = closed ? this.computeRingCentroid(points) : null

        for (let i = 0; i < vertexCount; i += 1) {
          const start = points[i]
          const end = closed ? points[(i + 1) % points.length] : points[i + 1]
          if (!end) continue
          let length = 0
          if (this.geometrySpherical?.computeDistanceBetween) {
            length = this.geometrySpherical.computeDistanceBetween(
              new window.google.maps.LatLng(start.lat, start.lng),
              new window.google.maps.LatLng(end.lat, end.lng),
            )
          }
          if (!Number.isFinite(length) || length < 0.05) continue
          const { position, angle } = this.computeEdgeLabelPlacement(start, end, centroid, length)
          const overlay = new OverlayClass({
            position: new window.google.maps.LatLng(position.lat, position.lng),
            text: this.formatDimensionMeters(length),
            angle,
            variant: 'edge',
          })
          overlay.setMap(this.map)
          this.parcelDimensionOverlays.push(overlay)
        }

        if (closed && this.geometrySpherical?.computeArea) {
          const area = Math.abs(this.geometrySpherical.computeArea(
            points.map(point => new window.google.maps.LatLng(point.lat, point.lng)),
          ))
          if (Number.isFinite(area) && area >= 0.05 && centroid) {
            const overlay = new OverlayClass({
              position: new window.google.maps.LatLng(centroid.lat, centroid.lng),
              text: this.formatDimensionArea(area),
              angle: 0,
              variant: 'area',
            })
            overlay.setMap(this.map)
            this.parcelDimensionOverlays.push(overlay)
          }
        }
      })
    },
    refreshMeasureOverlayLabels() {
      this.measureOverlays.forEach(entry => {
        if (!entry?.infoWindow) return
        if (entry.type === 'distance' && entry.lengthMeters != null) {
          entry.infoWindow.setContent(this.buildMeasureInfoContent(
            'المسافة',
            this.formatMeasureDistance(entry.lengthMeters),
          ))
        } else if (entry.type === 'area' && entry.areaSquareMeters != null) {
          entry.infoWindow.setContent(this.buildMeasureInfoContent(
            'المساحة',
            this.formatMeasureArea(entry.areaSquareMeters),
          ))
        }
      })
    },
    buildMeasureInfoContent(label, value) {
      return `<div class="measure-info-window"><strong>${label}:</strong> ${value}</div>`
    },
    getMeasurePreviewPath(includeCursor = true) {
      const path = [...this.measurePoints]
      if (includeCursor && this.measureCursorPoint) {
        path.push(this.measureCursorPoint)
      }
      return path
    },
    computeMeasureValues(path, mode) {
      if (!this.geometrySpherical || !Array.isArray(path) || path.length < 2) {
        return { lengthMeters: 0, areaSquareMeters: 0 }
      }

      const latLngPath = path.map(point => new window.google.maps.LatLng(point.lat, point.lng))

      if (mode === 'distance') {
        return {
          lengthMeters: this.geometrySpherical.computeLength(latLngPath),
          areaSquareMeters: 0,
        }
      }

      if (path.length < 3) {
        return { lengthMeters: 0, areaSquareMeters: 0 }
      }

      return {
        lengthMeters: 0,
        areaSquareMeters: this.geometrySpherical.computeArea(latLngPath),
      }
    },
    getMeasureSegmentMidpoint(start, end) {
      return {
        lat: (start.lat + end.lat) / 2,
        lng: (start.lng + end.lng) / 2,
      }
    },
    offsetMeasurePoint(point, distanceMeters, headingDegrees) {
      if (!point || !this.geometrySpherical) return point
      const offset = this.geometrySpherical.computeOffset(
        new window.google.maps.LatLng(point.lat, point.lng),
        distanceMeters,
        headingDegrees,
      )
      return { lat: offset.lat(), lng: offset.lng() }
    },
    getMeasureLabelPosition(path, mode = this.measureMode) {
      if (!Array.isArray(path) || !path.length) return null

      let anchor = path[path.length - 1]
      let offsetHeading = 0

      if (mode === 'area' && path.length >= 3) {
        anchor = this.getMeasureShapeCenter(path)
        const last = path[path.length - 1]
        if (this.geometrySpherical) {
          offsetHeading = this.geometrySpherical.computeHeading(
            new window.google.maps.LatLng(anchor.lat, anchor.lng),
            new window.google.maps.LatLng(last.lat, last.lng),
          )
        }
      } else if (path.length >= 2) {
        const start = path[path.length - 2]
        const end = path[path.length - 1]
        anchor = this.getMeasureSegmentMidpoint(start, end)
        if (this.geometrySpherical) {
          offsetHeading = this.geometrySpherical.computeHeading(
            new window.google.maps.LatLng(start.lat, start.lng),
            new window.google.maps.LatLng(end.lat, end.lng),
          ) + 90
        }
      }

      return this.offsetMeasurePoint(anchor, MEASURE_LABEL_OFFSET_METERS, offsetHeading)
    },
    getMeasureInfoWindowOptions(content) {
      const options = { content, disableAutoPan: true }
      if (window.google?.maps?.Size) {
        options.pixelOffset = new window.google.maps.Size(0, -5)
      }
      return options
    },
    openMeasureInfoWindow(infoWindow, position, content) {
      if (!infoWindow || !this.map || !position) return
      infoWindow.setOptions(this.getMeasureInfoWindowOptions(content))
      infoWindow.setPosition(position)
      infoWindow.open(this.map)
    },
    hideMeasureLiveLabel() {
      if (this.measureLiveLabelControl) {
        this.measureLiveLabelControl.style.display = 'none'
        this.measureLiveLabelControl.innerHTML = ''
      }
    },
    updateMeasureLiveLabel() {
      if (!this.measureMode || !this.map) return

      this.addMeasureLiveLabelControl()
      if (!this.measureLiveLabelControl) return

      const previewPath = this.getMeasurePreviewPath(true)
      if (!this.measurePoints.length) {
        this.hideMeasureLiveLabel()
        return
      }

      const { lengthMeters, areaSquareMeters } = this.computeMeasureValues(
        previewPath,
        this.measureMode,
      )

      const content = this.measureMode === 'distance'
        ? this.buildMeasureInfoContent('المسافة', this.formatMeasureDistance(lengthMeters))
        : this.buildMeasureInfoContent('المساحة', this.formatMeasureArea(areaSquareMeters))

      this.measureLiveLabelControl.innerHTML = content
      this.measureLiveLabelControl.style.display = 'block'
    },
    trimMeasureDoubleClickPoints() {
      if (this.measurePoints.length < 2 || !this.geometrySpherical) return

      const last = this.measurePoints[this.measurePoints.length - 1]
      const prev = this.measurePoints[this.measurePoints.length - 2]
      const distance = this.geometrySpherical.computeDistanceBetween(
        new window.google.maps.LatLng(last.lat, last.lng),
        new window.google.maps.LatLng(prev.lat, prev.lng),
      )

      if (distance < 1) {
        this.measurePoints.pop()
        const marker = this.measureMarkers.pop()
        if (marker?.setMap) marker.setMap(null)
      }
    },
    setMeasureMapInteraction(active) {
      if (!this.map) return

      if (active) {
        this.measurePreviousDoubleClickZoom = this.map.get('disableDoubleClickZoom')
        this.map.setOptions({ disableDoubleClickZoom: true })
        return
      }

      if (this.measurePreviousDoubleClickZoom !== null) {
        this.map.setOptions({ disableDoubleClickZoom: this.measurePreviousDoubleClickZoom })
        this.measurePreviousDoubleClickZoom = null
      }
    },
    getMeasureMarkerIcon(color = MEASURE_MARKER_ICON.fillColor) {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 5,
        fillColor: color,
        fillOpacity: MEASURE_MARKER_ICON.fillOpacity,
        strokeColor: MEASURE_MARKER_ICON.strokeColor,
        strokeWeight: MEASURE_MARKER_ICON.strokeWeight,
      }
    },
    toggleMeasureDistanceMode() {
      if (this.measureMode === 'distance') {
        this.finishMeasureMode()
        return
      }
      this.startMeasureMode('distance')
    },
    toggleMeasureAreaMode() {
      if (this.measureMode === 'area') {
        this.finishMeasureMode()
        return
      }
      this.startMeasureMode('area')
    },
    startMeasureMode(mode) {
      if (!this.map) return
      this.stopSketchTool()
      this.stopPolygonDrawMode()
      this.stopMeasureMode()
      this.disableParcelEdit()
      this.measureOverlays.forEach(entry => entry.infoWindow?.close())
      this.measureMode = mode
      this.setDataOverlaysClickable(false)
      this.setMeasureMapInteraction(true)

      const activeButton = mode === 'distance' ? this.measureDistanceButton : this.measureAreaButton
      if (activeButton) {
        activeButton.classList.add(`${mode === 'distance' ? 'measure-distance' : 'measure-area'}-button--active`)
        activeButton.title = mode === 'distance'
          ? 'إلغاء قياس المسافة'
          : 'إنهاء قياس المساحة (3 نقاط على الأقل)'
      }

      this.measureClickListener = this.map.addListener('click', event => {
        if (!this.measureMode) return

        this.addMeasurePointFromLatLng(event.latLng)
      })

      this.measureMouseMoveListener = this.map.addListener('mousemove', event => {
        this.measureCursorPoint = this.snapPointIfNearby({
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        })
        this.updateMeasurePreview()
        this.updateMeasureLiveLabel()
      })

      this.measureDblClickListener = this.map.addListener('dblclick', event => {
        if (event?.stop) event.stop()
        if (!this.measureMode) return
        this.trimMeasureDoubleClickPoints()
        if (this.measureMode === 'area') {
          window.setTimeout(() => this.finishMeasureMode(), 0)
        }
      })
    },
    updateMeasurePreview() {
      const previewPath = this.getMeasurePreviewPath(true)

      if (previewPath.length < 2) {
        if (this.measurePreviewOverlay) {
          this.measurePreviewOverlay.setMap(null)
          this.measurePreviewOverlay = null
        }
        return
      }

      if (!this.measurePreviewOverlay) {
        if (this.measureMode === 'distance') {
          this.measurePreviewOverlay = new window.google.maps.Polyline({
            path: previewPath,
            ...MEASURE_DISTANCE_STYLE,
            map: this.map,
          })
        } else {
          this.measurePreviewOverlay = new window.google.maps.Polygon({
            paths: previewPath,
            ...MEASURE_AREA_STYLE,
            map: this.map,
          })
        }
        return
      }

      if (this.measureMode === 'distance') {
        this.measurePreviewOverlay.setPath(previewPath)
      } else {
        this.measurePreviewOverlay.setPaths(previewPath)
      }
    },
    finishMeasureMode() {
      if (!this.measureMode) return

      const mode = this.measureMode
      const minPoints = mode === 'distance' ? 2 : 3
      if (this.measurePoints.length < minPoints) {
        if (this.$toast) {
          this.$toast.warning(mode === 'distance'
            ? 'أضف نقطتين على الأقل لقياس المسافة.'
            : 'أضف 3 نقاط على الأقل لقياس المساحة.')
        }
        return
      }

      let overlay = null
      let lengthMeters = null
      let areaSquareMeters = null
      const finalPath = [...this.measurePoints]
      const labelPosition = this.getMeasureLabelPosition(finalPath, mode)

      if (mode === 'distance') {
        overlay = this.measurePreviewOverlay
        if (!overlay) {
          overlay = new window.google.maps.Polyline({
            path: finalPath,
            ...MEASURE_DISTANCE_STYLE,
            map: this.map,
          })
        } else {
          overlay.setPath(finalPath)
          overlay.setOptions({ clickable: false })
        }
        if (this.geometrySpherical) {
          lengthMeters = this.geometrySpherical.computeLength(
            finalPath.map(point => new window.google.maps.LatLng(point.lat, point.lng)),
          )
        }
      } else {
        overlay = this.measurePreviewOverlay
        if (!overlay) {
          overlay = new window.google.maps.Polygon({
            paths: finalPath,
            ...MEASURE_AREA_STYLE,
            map: this.map,
          })
        } else {
          overlay.setPaths(finalPath)
          overlay.setOptions({ clickable: false })
        }
        if (this.geometrySpherical) {
          areaSquareMeters = this.geometrySpherical.computeArea(
            finalPath.map(point => new window.google.maps.LatLng(point.lat, point.lng)),
          )
        }
      }

      this.hideMeasureLiveLabel()

      const infoWindow = new window.google.maps.InfoWindow()
      const finalContent = mode === 'distance'
        ? this.buildMeasureInfoContent('المسافة', this.formatMeasureDistance(lengthMeters || 0))
        : this.buildMeasureInfoContent('المساحة', this.formatMeasureArea(areaSquareMeters || 0))
      this.openMeasureInfoWindow(infoWindow, labelPosition, finalContent)

      this.measurePreviewOverlay = null

      this.measureOverlays.push({
        type: mode,
        overlay,
        markers: [...this.measureMarkers],
        infoWindow,
        labelPath: finalPath,
        lengthMeters,
        areaSquareMeters,
      })

      overlay?.setOptions?.({ clickable: false })
      this.measureMarkers.forEach(marker => marker.setClickable?.(false))

      this.measureMarkers = []
      this.stopMeasureMode()
      this.updateDeleteSelectionButtonState()
    },
    getMeasureShapeCenter(points) {
      if (!Array.isArray(points) || !points.length) return null
      const sum = points.reduce((acc, point) => ({
        lat: acc.lat + point.lat,
        lng: acc.lng + point.lng,
      }), { lat: 0, lng: 0 })
      return {
        lat: sum.lat / points.length,
        lng: sum.lng / points.length,
      }
    },
    stopMeasureMode() {
      this.measureMode = null
      this.measurePoints = []
      this.measureCursorPoint = null

      if (this.measureClickListener) {
        window.google.maps.event.removeListener(this.measureClickListener)
        this.measureClickListener = null
      }
      if (this.measureDblClickListener) {
        window.google.maps.event.removeListener(this.measureDblClickListener)
        this.measureDblClickListener = null
      }
      if (this.measureMouseMoveListener) {
        window.google.maps.event.removeListener(this.measureMouseMoveListener)
        this.measureMouseMoveListener = null
      }

      this.hideMeasureLiveLabel()

      this.setMeasureMapInteraction(false)

      this.measureMarkers.forEach(marker => marker.setMap(null))
      this.measureMarkers = []

      if (this.measurePreviewOverlay) {
        this.measurePreviewOverlay.setMap(null)
        this.measurePreviewOverlay = null
      }

      if (this.measureDistanceButton) {
        this.measureDistanceButton.classList.remove('measure-distance-button--active')
        this.measureDistanceButton.title = 'قياس المسافة (انقر نقطتين على الخريطة لإنهاء القياس)'
      }
      if (this.measureAreaButton) {
        this.measureAreaButton.classList.remove('measure-area-button--active')
        this.measureAreaButton.title = 'قياس المساحة (3 نقاط على الأقل، ثم انقر الزر مرة أخرى أو انقر مرتين على الخريطة)'
      }

      if (!this.sketchMode && !this.polygonDrawActive) {
        this.setDataOverlaysClickable(true)
      }

      this.restoreParcelEditInteraction()
    },
    clearAllMeasurements() {
      this.stopMeasureMode()
      this.measureOverlays.forEach(entry => {
        if (entry?.overlay?.setMap) entry.overlay.setMap(null)
        if (entry?.infoWindow?.close) entry.infoWindow.close()
        if (Array.isArray(entry?.markers)) {
          entry.markers.forEach(marker => marker.setMap(null))
        }
      })
      this.measureOverlays = []
      this.updateDeleteSelectionButtonState()
    },
    startPolygonDrawMode(options = {}) {
      if (!this.map) return
      const skipReset = !!options.skipReset
      const skipHistory = !!options.skipHistory
      const sketchMode = options.sketchMode === 'polyline' ? 'polyline' : (options.sketchMode || null)

      this.stopMeasureMode()
      if (!skipReset) {
        this.stopPolygonDrawMode()
        this.disableParcelEdit()
        this.sketchMode = this.sketchMode === 'polyline' ? 'polyline' : 'polygon'
        this.polygonDrawPoints = []
        this.clearPolygonDrawGraphics()
      }
      if (sketchMode) this.sketchMode = sketchMode
      else if (this.sketchMode !== 'polyline') this.sketchMode = 'polygon'

      this.polygonDrawActive = true
      this.setPolygonDrawMapInteraction(true)
      this.setDataOverlaysClickable(false)
      this.updateSketchToolActiveState()
      if (this.drawPolygonButton) {
        this.drawPolygonButton.classList.add('draw-polygon-button--active')
        this.drawPolygonButton.title = this.sketchMode === 'polyline'
          ? 'إنهاء رسم الخط (اضغط الزر مرة أخرى أو إنهاء الخط)'
          : 'إنهاء رسم المضلع (3 نقاط ثم اضغط النقطة الأولى أو إنهاء المضلع)'
      }

      if (!this.mapClickListener) {
        this.mapClickListener = this.map.addListener('click', event => {
          this.addPolygonDrawPointFromLatLng(event.latLng)
        })
      }
      if (!this.polygonDrawDblClickListener) {
        this.polygonDrawDblClickListener = this.map.addListener('dblclick', event => {
          if (event?.stop) event.stop()
          if (!this.polygonDrawActive) return
          window.setTimeout(() => this.finishPolygonDrawMode(), 0)
        })
      }

      if (!skipHistory && !skipReset) {
        if (!this.drawHistory.length) this.pushDrawHistory('الحالة الأصلية')
        this.pushDrawHistory('بدء الرسم')
      }
      this.syncClosingVertexMarker()
      this.syncFinishPolygonButton()
    },
    clearPolygonDrawGraphics() {
      if (this.polygonDrawFirstVertexListener) {
        window.google.maps.event.removeListener(this.polygonDrawFirstVertexListener)
        this.polygonDrawFirstVertexListener = null
      }
      (this.polygonDrawMarkers || []).forEach(marker => marker.setMap(null))
      this.polygonDrawMarkers = []
      if (this.polygonDrawPreviewLine) {
        this.polygonDrawPreviewLine.setMap(null)
        this.polygonDrawPreviewLine = null
      }
      if (this.polygonDrawCloseLine) {
        this.polygonDrawCloseLine.setMap(null)
        this.polygonDrawCloseLine = null
      }
    },
    rebuildPolygonDrawGraphics() {
      this.clearPolygonDrawGraphics()
      if (!this.map || !window.google?.maps) return
      this.polygonDrawMarkers = this.polygonDrawPoints.map(point => new window.google.maps.Marker({
        position: point,
        map: this.map,
        optimized: false,
        clickable: true,
        zIndex: 8,
        icon: this.buildPointMarkerIcon(DRAW_POINT_COLOR),
      }))
      this.updatePolygonDrawPreview()
      this.syncClosingVertexMarker()
      this.syncFinishPolygonButton()
    },
    updatePolygonDrawPreview() {
      if (this.polygonDrawPreviewLine) {
        this.polygonDrawPreviewLine.setMap(null)
        this.polygonDrawPreviewLine = null
      }
      if (this.polygonDrawCloseLine) {
        this.polygonDrawCloseLine.setMap(null)
        this.polygonDrawCloseLine = null
      }
      if (this.polygonDrawPoints.length >= 2) {
        this.polygonDrawPreviewLine = new window.google.maps.Polyline({
          path: this.polygonDrawPoints,
          strokeColor: DRAW_POINT_COLOR,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          clickable: false,
          map: this.map,
        })
      }
      if (this.canFinishPolygonDraw() && this.sketchMode !== 'polyline' && this.polygonDrawPoints.length >= 3) {
        const first = this.polygonDrawPoints[0]
        const last = this.polygonDrawPoints[this.polygonDrawPoints.length - 1]
        this.polygonDrawCloseLine = new window.google.maps.Polyline({
          path: [last, first],
          strokeColor: SNAP_POINT_COLOR,
          strokeOpacity: 0,
          strokeWeight: 2,
          clickable: false,
          map: this.map,
          icons: [{
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              strokeColor: SNAP_POINT_COLOR,
              scale: 3,
            },
            offset: '0',
            repeat: '10px',
          }],
        })
      }
      this.scheduleParcelDimensionsRefresh()
    },
    finishPolygonDrawMode() {
      if (!this.polygonDrawActive) return

      if (this.sketchMode === 'polyline') {
        this.finishSketchPolyline()
        return
      }

      if (this.polygonDrawPoints.length < 3) {
        if (this.$toast) {
          this.$toast.warning('أضف 3 نقاط على الأقل لإنشاء المضلع.')
        }
        return
      }

      const polygon = new window.google.maps.Polygon({
        paths: [...this.polygonDrawPoints],
        ...PARCEL_POLYGON_STYLE,
        editable: false,
        draggable: false,
      })

      this.suppressPropRedrawUntil = Date.now() + 8000
      this.setParcelPolygon(polygon, { editable: true })
      this.pendingPolygonCoordinates = this.latLngPathToCoordinates(polygon.getPath())
      this.updateSavePolygonButtonState()

      const area = this.geometrySpherical?.computeArea?.(polygon.getPath())
      const areaText = Number.isFinite(Number(area))
        ? `المساحة: ${Number(area).toFixed(2)} م²`
        : ''
      this.promptSavePolygonChoice(areaText)
      this.pushDrawHistory('رسم مضلع')

      this.stopSketchTool()
      this.$nextTick(() => this.focusCurrentPolygon())
    },
    stopPolygonDrawMode() {
      this.polygonDrawActive = false
      this.polygonDrawPoints = []

      if (this.mapClickListener) {
        window.google.maps.event.removeListener(this.mapClickListener)
        this.mapClickListener = null
      }
      if (this.polygonDrawDblClickListener) {
        window.google.maps.event.removeListener(this.polygonDrawDblClickListener)
        this.polygonDrawDblClickListener = null
      }

      this.clearPolygonDrawGraphics()

      if (this.finishPolygonButton) {
        this.finishPolygonButton.hidden = true
        this.finishPolygonButton.disabled = true
      }

      if (this.drawPolygonButton) {
        this.drawPolygonButton.classList.remove('draw-polygon-button--active')
        this.drawPolygonButton.title = 'رسم مضلع (انقر على الخريطة لإضافة النقاط، ثم انقر مرة أخرى لإنهاء الرسم)'
      }
      this.setPolygonDrawMapInteraction(false)
    },
    coordsToLatLngPath(coords) {
      return this.normalizeCoordinatesList(coords).map(coord => ({
        lat: Number(coord.latitude),
        lng: Number(coord.longitude),
      }))
    },
    latLngPathToCoordinates(path) {
      if (!path) return []
      const len = typeof path.getLength === 'function' ? path.getLength() : path.length
      const points = []
      for (let i = 0; i < len; i += 1) {
        const latLng = typeof path.getAt === 'function' ? path.getAt(i) : path[i]
        if (!latLng) continue
        points.push({
          latitude: Number(latLng.lat()),
          longitude: Number(latLng.lng()),
          type: this.coordinatesType,
          x: Number(latLng.lng()),
          y: Number(latLng.lat()),
        })
      }
      return this.normalizeCoordinatesList(points)
    },
    clearOverlayGroup(groupName) {
      const items = this.overlayGroups[groupName] || []
      items.forEach(item => {
        if (item?.setMap) item.setMap(null)
        if (item?.setVisible) item.setVisible(false)
      })
      this.overlayGroups[groupName] = []
    },
    setupMapPointClickListener() {
      this.teardownMapPointClickListener()
      if (!this.map) return
      this.mapPointClickListener = this.map.addListener('click', event => {
        this.closeOverlayInfoWindow()
        if (this.polygonDrawActive || this.measureMode || this.isSketchToolActive()) return
        const latLng = event?.latLng
        if (!latLng) return
        const latitude = Number(typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat)
        const longitude = Number(typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng)
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return
        if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return
        this.$emit('map-point-click', { latitude, longitude })
      })
    },
    teardownMapPointClickListener() {
      if (this.mapPointClickListener) {
        window.google?.maps?.event?.removeListener?.(this.mapPointClickListener)
        this.mapPointClickListener = null
      }
    },
    clearMatchedAdministrativeBoundary() {
      this.clearOverlayGroup('matchedAdministrativeBoundary')
    },
    highlightMatchedAdministrativeBoundary(geometry) {
      this.clearMatchedAdministrativeBoundary()
      if (!this.map || !geometry || !window.google?.maps) return false

      const rings = ringsFromGeoJsonGeometry(geometry)
      const paths = rings
        .filter(ring => Array.isArray(ring) && ring.length >= 3)
        .map(ring => ring.map(p => ({ lat: Number(p[1]), lng: Number(p[0]) })))

      if (!paths.length) return false

      const polygon = new window.google.maps.Polygon({
        paths,
        ...MATCHED_ADMINISTRATIVE_BOUNDARY_GOOGLE_STYLE,
        map: this.map,
      })
      this.overlayGroups.matchedAdministrativeBoundary.push(polygon)
      return true
    },
    setOverlayGroupVisibility(groupName, visible) {
      const items = this.overlayGroups[groupName] || []
      items.forEach(item => {
        if (item?.setVisible) item.setVisible(visible)
        else if (item?.setMap) item.setMap(visible ? this.map : null)
      })
    },
    teardownParcelPolygon() {
      this.detachParcelPathListeners()
      this.detachParcelClickListener()
      this.detachParcelDragListeners()
      this.clearMoveVertexMarkers()
      if (this.parcelPolygon) {
        this.parcelPolygon.setMap(null)
        this.parcelPolygon = null
      }
      this.clearParcelDimensions()
    },
    teardownCommittedParcelPolygon() {
      if (this.committedParcelPolygon?.setMap) {
        this.committedParcelPolygon.setMap(null)
      }
      this.committedParcelPolygon = null
      this.preservedCommittedParcel = false
    },
    preserveCommittedParcelBeforeNewDraw() {
      if (this.committedParcelPolygon || !this.map || !window.google?.maps) return
      const path = this.parcelPolygon?.getPath?.()
      if (!path) return
      const coords = this.latLngPathToCoordinates(path)
      if (coords.length < 3) return

      this.committedParcelPolygon = new window.google.maps.Polygon({
        paths: this.coordsToLatLngPath(coords),
        strokeColor: '#0080FF',
        strokeOpacity: 0.75,
        strokeWeight: 2,
        fillColor: '#0080FF',
        fillOpacity: 0.08,
        editable: false,
        draggable: false,
        clickable: false,
        zIndex: 1,
        map: this.map,
      })
      this.preservedCommittedParcel = true
    },
    teardownCommittedParcelPolygonIfUnchanged() {
      if (!this.committedParcelPolygon || !this.parcelPolygon) return
      const committed = this.latLngPathToCoordinates(this.committedParcelPolygon.getPath?.())
      const current = this.latLngPathToCoordinates(this.parcelPolygon.getPath?.())
      if (this.areCoordinatesEqual(committed, current)) {
        this.teardownCommittedParcelPolygon()
      }
    },
    detachParcelClickListener() {
      if (this.parcelClickListener) {
        window.google.maps.event.removeListener(this.parcelClickListener)
        this.parcelClickListener = null
      }
    },
    enableParcelEdit() {
      if (!this.parcelPolygon) return
      if (this.measureMode || this.isSketchToolActive() || this.isMoveToolActive()) return
      this.detachParcelClickListener()
      this.parcelPolygon.setOptions({ editable: true })
      this.updateDeleteSelectionButtonState()
    },
    disableParcelEdit() {
      if (!this.parcelPolygon) return
      this.parcelPolygon.setOptions({ editable: false })
      this.detachParcelClickListener()
      this.updateDeleteSelectionButtonState()
    },
    restoreParcelEditInteraction() {
      if (!this.parcelPolygon || this.measureMode || this.polygonDrawActive || this.isSketchToolActive() || this.isMoveToolActive()) return
      if (this.parcelPolygon.getEditable?.()) return
      this.attachParcelClickToEditListener(this.parcelPolygon)
    },
    attachParcelClickToEditListener(polygon) {
      this.detachParcelClickListener()
      if (!polygon?.addListener) return
      this.parcelClickListener = polygon.addListener('click', event => {
        if (this.consumeOverlayClickForActiveTool(event)) return
        if (this.parcelPolygon !== polygon) return
        if (this.measureMode || this.isSketchToolActive()) return
        if (polygon.getEditable?.()) return
        this.enableParcelEdit()
      })
    },
    detachParcelPathListeners() {
      this.parcelPathListeners.forEach(listener => {
        if (listener?.remove) listener.remove()
        else if (listener) window.google.maps.event.removeListener(listener)
      })
      this.parcelPathListeners = []
    },
    attachParcelPathListeners(polygon) {
      this.detachParcelPathListeners()
      const path = polygon?.getPath?.()
      if (!path?.addListener) return
      ;['set_at', 'insert_at', 'remove_at'].forEach(eventName => {
        this.parcelPathListeners.push(
          path.addListener(eventName, () => this.capturePendingPolygonFromPolygon(polygon, eventName)),
        )
      })
    },
    setParcelPolygon(polygon, { editable = false } = {}) {
      this.teardownParcelPolygon()
      this.parcelPolygon = polygon
      polygon.setOptions({
        ...PARCEL_POLYGON_STYLE,
        editable,
        draggable: false,
        zIndex: 2,
      })
      polygon.setMap(this.map)
      this.attachParcelPathListeners(polygon)
      this.capturePendingPolygonFromPolygon(polygon)
      if (!editable) {
        this.attachParcelClickToEditListener(polygon)
      }
    },
    createParcelPolygon(coords) {
      const path = this.coordsToLatLngPath(coords)
      if (path.length < 3) return null

      const polygon = new window.google.maps.Polygon({
        paths: path,
        ...PARCEL_POLYGON_STYLE,
        editable: false,
        draggable: false,
      })
      this.setParcelPolygon(polygon, { editable: false })
      return polygon
    },
    capturePendingPolygonFromPolygon(polygon, pathEvent = null) {
      if (!polygon) return
      const coordinates = this.latLngPathToCoordinates(polygon.getPath())
      if (coordinates.length < 3) {
        this.scheduleParcelDimensionsRefresh()
        return
      }

      this.pendingPolygonCoordinates = coordinates
      this.$emit('coordinates-changed', [...this.pendingPolygonCoordinates])
      this.updateSavePolygonButtonState()
      this.scheduleParcelDimensionsRefresh()
      if (!pathEvent || this.suppressDrawHistory || this.skipPathHistory) return

      const label = pathEvent === 'insert_at'
        ? 'إضافة رأس'
        : pathEvent === 'remove_at'
          ? 'حذف رأس'
          : 'تعديل المضلع'
      const delay = pathEvent === 'set_at' ? 450 : 0
      this.clearDrawHistoryTimers()
      if (delay) {
        this.pathHistoryTimer = window.setTimeout(() => {
          this.pushDrawHistory(label)
        }, delay)
      } else {
        this.pushDrawHistory(label)
      }
    },
    drawPolygon(coordinatesOverride = null) {
      if (!this.map || !window.google?.maps) return

      const coords = coordinatesOverride != null
        ? this.normalizeCoordinatesList(coordinatesOverride)
        : this.normalizeCoordinatesList(this.coordinates)

      this.teardownParcelPolygon()
      this.teardownCommittedParcelPolygon()

      if (coords.length < 3) {
        this.pendingPolygonCoordinates = []
        this.updateSavePolygonButtonState()
        if (!this.suppressDrawHistory && this.drawHistory.length === 0) {
          this.pushDrawHistory('الحالة الأصلية')
        }
        this.$nextTick(() => this.focusCurrentPolygon())
        return
      }

      this.createParcelPolygon(coords)
      this.pendingPolygonCoordinates = coords
      this.updateSavePolygonButtonState()
      if (!this.suppressDrawHistory && this.drawHistory.length === 0) {
        this.pushDrawHistory('الحالة الأصلية')
      }
      this.$nextTick(() => this.focusCurrentPolygon())
    },
    closeOverlayInfoWindow() {
      this.overlayInfoWindow?.close?.()
    },
    openOverlayInfoWindow(contentHtml, position) {
      if (!this.map || !window.google?.maps || !position) return
      if (!this.overlayInfoWindow) {
        this.overlayInfoWindow = new window.google.maps.InfoWindow({
          maxWidth: 260,
        })
      }
      const html = '<div class="google-closable-info">'
        + '<button type="button" class="google-closable-info__close" aria-label="إغلاق">×</button>'
        + `<div class="google-closable-info__body">${contentHtml}</div>`
        + '</div>'
      this.overlayInfoWindow.setContent(html)
      this.overlayInfoWindow.setPosition(position)
      this.overlayInfoWindow.open(this.map)
      window.google.maps.event.addListenerOnce(this.overlayInfoWindow, 'domready', () => {
        const button = document.querySelector('.gm-style-iw-c .google-closable-info__close')
        if (!button) return
        button.addEventListener('click', event => {
          event.preventDefault()
          event.stopPropagation()
          this.closeOverlayInfoWindow()
        })
      })
    },
    drawOtherProjectLocations() {
      if (!this.map) return

      this.closeOverlayInfoWindow()
      this.clearOverlayGroup('otherProjectLocations')

      const entries = Array.isArray(this.otherProjectLocations) ? this.otherProjectLocations : []
      entries.forEach((entry, index) => {
        const coords = this.normalizeCoordinatesList(entry?.coordinates)
        if (coords.length < 3) return

        const color = OTHER_LOCATION_PALETTE[index % OTHER_LOCATION_PALETTE.length]
        const polygon = new window.google.maps.Polygon({
          paths: this.coordsToLatLngPath(coords),
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.2,
          editable: false,
          draggable: false,
          clickable: true,
          map: this.map,
        })

        const label = entry?.label || `موقع ${index + 1}`
        polygon.addListener('click', event => {
          if (this.consumeOverlayClickForActiveTool(event)) return
          this.openOverlayInfoWindow(
            `<div><strong>${label}</strong><br>مضلع موقع آخر في نفس المشروع (للعرض فقط)</div>`,
            event.latLng,
          )
        })

        this.overlayGroups.otherProjectLocations.push(polygon)
      })

      if (!this.parcelPolygon?.getPath) {
        this.$nextTick(() => this.focusCurrentPolygon())
      }
    },
    fitMapToOtherProjectLocations() {
      if (!this.map || !window.google?.maps) return false

      const bounds = new window.google.maps.LatLngBounds()
      const polygons = this.overlayGroups.otherProjectLocations || []
      polygons.forEach(polygon => {
        if (polygon?.getPath) {
          iterateLatLngPath(polygon.getPath(), latLng => bounds.extend(latLng))
        }
      })

      if (bounds.isEmpty()) {
        const entries = Array.isArray(this.otherProjectLocations) ? this.otherProjectLocations : []
        entries.forEach(entry => {
          const coords = this.normalizeCoordinatesList(entry?.coordinates)
          coords.forEach(coord => {
            const lat = Number(coord.latitude)
            const lng = Number(coord.longitude)
            if (this.isValidMapCoordinate(lat, lng)) {
              bounds.extend({ lat, lng })
            }
          })
        })
      }

      if (bounds.isEmpty()) return false

      this.map.fitBounds(bounds, 48)
      return true
    },
    /**
     * Fit the map to the current parcel, sibling locations, or user location (in that order).
     */
    focusCurrentPolygon() {
      if (!this.map || !window.google?.maps) return

      if (this.parcelPolygon?.getPath) {
        const bounds = new window.google.maps.LatLngBounds()
        iterateLatLngPath(this.parcelPolygon.getPath(), latLng => {
          bounds.extend(latLng)
        })
        this.map.fitBounds(bounds, 48)
        return
      }

      if (this.fitMapToOtherProjectLocations()) return

      const lat = Number(this.currentCoordinates.lat)
      const lng = Number(this.currentCoordinates.lng)
      if (this.isValidMapCoordinate(lat, lng)) {
        this.map.setCenter({ lat, lng })
        this.map.setZoom(18)
      }
    },
    toggleLayer(layerName) {
      if (layerName === 'parcelDimensions') {
        if (this.isParcelDimensionsVisible()) this.refreshParcelDimensions()
        else this.clearParcelDimensions()
        this.syncParcelDimensionsUi()
        this.syncSketchLayersPanel()
        return
      }
      if (GEODESIA_MAP_LAYER_KEYS.includes(layerName)) {
        this.setOverlayGroupVisibility(layerName, this.layerVisibility[layerName])
        if (this.layerVisibility[layerName]) {
          this.ensureLayerDataLoaded(layerName)
        } else {
          this.cancelGeodesiaLayerRequest(layerName)
        }
        this.syncSketchLayersPanel()
        return
      }

      const layerMap = {
        projects: 'projects',
        mapLibraries: 'mapLibraries',
        locationPolygons: 'locationPolygons',
        fixedPoints: 'fixedPoints',
        vrsCoordinates: 'vrsCoordinates',
        jxlPolygons: 'jxlPolygons',
        jxlBaselineLines: 'jxlBaselineLines',
        userLocation: 'userLocation',
      }
      ADMINISTRATIVE_BOUNDARY_TYPES.forEach(item => {
        layerMap[administrativeBoundaryLayerKey(item.type)] = administrativeBoundaryLayerKey(item.type)
      })
      const group = layerMap[layerName]
      if (group) {
        this.setOverlayGroupVisibility(group, this.layerVisibility[layerName])
      }

      if (this.layerVisibility[layerName]) {
        this.ensureLayerDataLoaded(layerName)
      } else if (layerName === 'locationPolygons') {
        this.$set(this.layerLoadState, layerName, {
          loading: false,
          loaded: false,
          error: null,
        })
      }

      this.syncSketchLayersPanel()
    },
    getSavePolygonButtonIconHtml() {
      return '<span class="save-polygon-button__icon" aria-hidden="true">'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>'
        + '<polyline points="17 21 17 13 7 13 7 21"/>'
        + '<polyline points="7 3 7 8 15 8"/>'
        + '</svg></span>'
    },
    getSaveAsNewLocationButtonIconHtml() {
      return '<span class="save-as-new-location-button__icon" aria-hidden="true">'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>'
        + '<circle cx="12" cy="10" r="3"/>'
        + '<line x1="12" y1="17" x2="12" y2="22"/>'
        + '<line x1="9" y1="20" x2="15" y2="20"/>'
        + '</svg></span>'
    },
    getHomeButtonIconHtml() {
      return '<span class="home-location-button__icon" aria-hidden="true">'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>'
        + '<polyline points="9 22 9 12 15 12 15 22"/>'
        + '</svg></span>'
    },
    updateSavePolygonButtonState() {
      if (!this.savePolygonButton) return

      const canSave = this.canSaveDrawnPolygon
      this.savePolygonButton.disabled = !canSave
      this.savePolygonButton.classList.toggle('save-polygon-button--disabled', !canSave)

      if (this.savingCoordinates) {
        this.savePolygonButton.classList.add('save-polygon-button--loading')
        this.savePolygonButton.innerHTML = '<span class="home-location-spinner"></span>'
      } else {
        this.savePolygonButton.classList.remove('save-polygon-button--loading')
        this.savePolygonButton.innerHTML = this.getSavePolygonButtonIconHtml()
      }

      this.updateSaveAsNewLocationButtonState()
    },
    updateSaveAsNewLocationButtonState() {
      if (!this.saveAsNewLocationButton) return

      const canSave = this.canSaveDrawnPolygon && !this.savingCoordinates
      this.saveAsNewLocationButton.disabled = !canSave
      this.saveAsNewLocationButton.classList.toggle('save-as-new-location-button--disabled', !canSave)
      this.saveAsNewLocationButton.innerHTML = this.getSaveAsNewLocationButtonIconHtml()
    },
    setHomeButtonLoading(isLoading) {
      if (!this.homeButton) return

      if (isLoading) {
        this.homeButton.classList.add('home-location-button--loading')
        this.homeButton.innerHTML = '<span class="home-location-spinner"></span>'
      } else {
        this.homeButton.classList.remove('home-location-button--loading')
        this.homeButton.innerHTML = this.getHomeButtonIconHtml()
      }
    },
    clearUserLocationMarker() {
      if (this.userLocationMarker) {
        this.userLocationMarker.setMap(null)
        this.userLocationMarker = null
      }
      this.clearOverlayGroup('userLocation')
    },
    async addUserLocation() {
      if (this.locationLoading) return
      this.locationLoading = true
      this.setHomeButtonLoading(true)

      try {
        const location = await this.getUserCurrentLocation()
        this.clearUserLocationMarker()

        this.userLocationMarker = new window.google.maps.Marker({
          position: { lat: location.latitude, lng: location.longitude },
          map: this.layerVisibility.userLocation ? this.map : null,
          title: 'موقعي الحالي',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#0000FF',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          },
        })
        this.overlayGroups.userLocation.push(this.userLocationMarker)

        this.map.panTo({ lat: location.latitude, lng: location.longitude })
        this.map.setZoom(18)
      } catch (error) {
        console.error('Error getting user location:', error)
        if (this.$toast?.error) {
          this.$toast.error('فشل في الحصول على موقعك')
        }
      } finally {
        this.locationLoading = false
        this.setHomeButtonLoading(false)
      }
    },
    getUserCurrentLocation() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'))
          return
        }

        navigator.geolocation.getCurrentPosition(
          position => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
            })
          },
          error => reject(error),
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          },
        )
      })
    },
    isValidMapCoordinate(latitude, longitude) {
      if (latitude == null || longitude == null) return false
      const lat = Number(latitude)
      const lng = Number(longitude)
      if (Number.isNaN(lat) || Number.isNaN(lng)) return false
      if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return false
      return true
    },
    resolveMapCoordinatePair(coord) {
      if (!coord || typeof coord !== 'object') return null
      const latCandidates = [coord.latitude, coord.lat, coord.y]
      const lngCandidates = [coord.longitude, coord.lng, coord.x]
      for (let i = 0; i < latCandidates.length; i += 1) {
        const lat = Number(latCandidates[i])
        const lng = Number(lngCandidates[i])
        if (this.isValidMapCoordinate(lat, lng)) {
          return { latitude: lat, longitude: lng }
        }
      }
      return null
    },
    getOtherProjectLocationsCenter() {
      const entries = Array.isArray(this.otherProjectLocations) ? this.otherProjectLocations : []
      let sumLat = 0
      let sumLng = 0
      let count = 0

      entries.forEach(entry => {
        const coords = Array.isArray(entry?.coordinates) ? entry.coordinates : []
        coords.forEach(coord => {
          const resolved = this.resolveMapCoordinatePair(coord)
          if (!resolved) return
          sumLat += resolved.latitude
          sumLng += resolved.longitude
          count += 1
        })
      })

      if (!count) return null
      return {
        lat: sumLat / count,
        lng: sumLng / count,
      }
    },
    getInitialMapCenter() {
      const currentCoords = this.normalizeCoordinatesList(this.coordinates)
      if (currentCoords.length >= 3) {
        const first = currentCoords[0]
        return { lat: first.latitude, lng: first.longitude }
      }

      const siblingCenter = this.getOtherProjectLocationsCenter()
      if (siblingCenter) return siblingCenter

      return {
        lat: this.currentCoordinates.lat,
        lng: this.currentCoordinates.lng,
      }
    },
    async commitParcelEditToPending() {
      if (this.parcelPolygon) {
        this.capturePendingPolygonFromPolygon(this.parcelPolygon)
      }
      return this.pendingPolygonCoordinates
    },
    bringSavePolygonModalAboveFullscreen() {
      const fsEl = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.mozFullScreenElement
        || document.msFullscreenElement
      if (!fsEl) return

      const modal = document.getElementById(this.savePolygonChoiceModalId)
      if (!modal) return

      document.querySelectorAll('.modal-backdrop').forEach(backdrop => {
        if (!fsEl.contains(backdrop)) fsEl.appendChild(backdrop)
      })
      if (modal.parentElement !== fsEl && !fsEl.contains(modal)) {
        fsEl.appendChild(modal)
      }
    },
    promptSavePolygonChoice(areaText = '') {
      if (this.pendingPolygonCoordinates.length < 3) return
      this.lastDrawnPolygonAreaText = areaText || ''
      this.showSavePolygonChoiceModal = true
      this.$nextTick(() => this.bringSavePolygonModalAboveFullscreen())
    },
    async onSavePolygonToCurrentLocation() {
      await this.saveDrawnCoordinates()
      this.showSavePolygonChoiceModal = false
    },
    async onSavePolygonToNewLocation() {
      await this.saveDrawnPolygonAsNewLocation()
      this.showSavePolygonChoiceModal = false
    },
    async saveDrawnPolygonAsNewLocation() {
      if (!this.canSaveDrawnPolygon || this.savingCoordinates) return

      try {
        const pending = await this.commitParcelEditToPending()
        const savedCoordinates = this.normalizeCoordinatesList(pending)

        if (savedCoordinates.length < 3) {
          if (this.$toast) {
            this.$toast.warning('لا توجد إحداثيات للحفظ (يحتاج المضلع 3 نقاط على الأقل).')
          }
          return
        }

        this.$emit('save-as-new-location', {
          coordinates: savedCoordinates,
          coordinates_type: this.coordinatesType,
        })
      } catch (error) {
        if (this.$toast) {
          this.$toast.error(error.response?.data?.message || 'تعذر تجهيز المضلع للحفظ كقطعة جديدة')
        }
      }
    },
    async saveDrawnCoordinates() {
      this.savingCoordinates = true
      this.updateSavePolygonButtonState()

      try {
        const pending = await this.commitParcelEditToPending()
        const savedCoordinates = this.normalizeCoordinatesList(pending)

        if (savedCoordinates.length < 3) {
          if (this.$toast) {
            this.$toast.warning('لا توجد إحداثيات للحفظ (يحتاج المضلع 3 نقاط على الأقل).')
          }
          return
        }

        const locationId = this.resolvedLocationId
        if (!locationId) {
          this.$emit('coordinates-changed', savedCoordinates)
          this.$emit('coordinates-saved', {
            locationId: null,
            coordinates: savedCoordinates,
            coordinates_type: this.coordinatesType,
          })
          if (this.$toast) {
            this.$toast.success('تم حفظ المضلع محلياً')
          }

          this.pendingPolygonCoordinates = savedCoordinates
          this.suppressPropRedrawUntil = Date.now() + 3000
          this.teardownCommittedParcelPolygon()
          this.drawPolygon(savedCoordinates)
          return
        }

        await this.$store.dispatch('projects/updateCoordinates', {
          id: locationId,
          query: {
            coordinates: savedCoordinates,
            coordinates_type: this.coordinatesType,
          },
        })

        this.$emit('coordinates-changed', savedCoordinates)
        this.$emit('coordinates-saved', {
          locationId,
          coordinates: savedCoordinates,
          coordinates_type: this.coordinatesType,
        })

        this.pendingPolygonCoordinates = savedCoordinates
        this.suppressPropRedrawUntil = Date.now() + 3000
        this.teardownCommittedParcelPolygon()
        this.drawPolygon(savedCoordinates)

        if (this.$toast) {
          this.$toast.success('تم حفظ الإحداثيات بنجاح')
        }
      } catch (error) {
        if (this.$toast) {
          this.$toast.error(error.response?.data?.message || 'تعذر حفظ الإحداثيات')
        }
      } finally {
        this.savingCoordinates = false
        this.updateSavePolygonButtonState()
      }
    },
    normalizeCoordinatesList(coords) {
      if (!Array.isArray(coords)) return []

      const normalized = coords
        .map(coord => {
          const resolved = this.resolveMapCoordinatePair(coord)
          if (!resolved) return null
          return {
            latitude: resolved.latitude,
            longitude: resolved.longitude,
            type: coord?.type || this.coordinatesType,
            x: Number(coord?.x ?? resolved.longitude),
            y: Number(coord?.y ?? resolved.latitude),
          }
        })
        .filter(Boolean)

      return normalized.length >= 3 ? normalized : []
    },
    areCoordinatesEqual(a, b) {
      const left = this.normalizeCoordinatesList(a)
      const right = this.normalizeCoordinatesList(b)
      if (left.length !== right.length || left.length < 3) return false
      return left.every((point, index) => {
        const other = right[index]
        return Math.abs(point.latitude - other.latitude) < 1e-8
          && Math.abs(point.longitude - other.longitude) < 1e-8
      })
    },
    async setCurrentToGeolocation() {
      if (!navigator.geolocation) return
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              enableHighAccuracy: true,
              timeout: 10000,
            },
          )
        })
        this.currentCoordinates.lat = position.coords.latitude
        this.currentCoordinates.lng = position.coords.longitude
      } catch (error) {
        // Keep fallback coordinates
      }
    },
    async loadAndRenderLocationPolygons() {
      const layerKey = 'locationPolygons'
      this.beginLayerLoad([layerKey])
      try {
        const query = {
          currentLocation: {
            latitude: this.currentCoordinates.lat,
            longitude: this.currentCoordinates.lng,
          },
        }

        await this.$store.dispatch('projects/locationPolygons', { query })

        const polygons = Array.isArray(this.storeLocationPolygons) ? this.storeLocationPolygons : []
        this.drawLocationPolygons(polygons)
        this.finishLayerLoad(layerKey, { success: true })
      } catch (error) {
        console.error('Error loading location polygons:', error)
        this.finishLayerLoad(layerKey, {
          success: false,
          errorMessage: error?.response?.data?.message || 'حدث خطأ أثناء تحميل المشاريع القريبة',
        })
      }
    },
    drawLocationPolygons(polygons) {
      if (!this.map) return

      this.clearOverlayGroup('locationPolygons')

      const list = Array.isArray(polygons) ? polygons : []
      list.forEach(location => {
        if (!location || !Array.isArray(location.polygon) || location.polygon.length < 3) {
          return
        }

        const color = location.project_id == this.projectId ? '#0080FF' : '#FF0000'
        const polygon = new window.google.maps.Polygon({
          paths: location.polygon.map(coord => ({
            lat: Number(coord.latitude),
            lng: Number(coord.longitude),
          })),
          strokeColor: color,
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.25,
          editable: false,
          map: this.layerVisibility.locationPolygons ? this.map : null,
        })

        const owners = Array.isArray(location.owners) ? location.owners.join(', ') : ''
        const distance = location.distance_meters != null
          ? `${Number(location.distance_meters).toFixed(2)} م`
          : null

        let html = '<div>'
        html += `<div><strong>المشروع:</strong> ${location.project_name || '-'}</div>`
        if (owners) html += `<div><strong>المالكون:</strong> ${owners}</div>`
        if (distance) html += `<div><strong>المسافة:</strong> ${distance}</div>`
        html += '</div>'

        const infoWindow = new window.google.maps.InfoWindow({ content: html })
        polygon.addListener('click', event => {
          if (this.consumeOverlayClickForActiveTool(event)) return
          infoWindow.setPosition(event.latLng)
          infoWindow.open(this.map)
        })

        this.overlayGroups.locationPolygons.push(polygon)
      })
    },
    async loadMapAllData({ force = false } = {}) {
      if (!this.map) return
      const layerKeys = ['projects', 'mapLibraries', 'fixedPoints']
      this.beginLayerLoad(layerKeys)
      try {
        const data = await fetchMapAllDataShared(
          this.currentCoordinates.lat,
          this.currentCoordinates.lng,
          { force },
        )
        const projects = Array.isArray(data?.projects) ? data.projects : []
        const mapLibraries = Array.isArray(data?.map_libraries) ? data.map_libraries : []
        const fixedPoints = Array.isArray(data?.fixed_points) ? data.fixed_points : []

        this.clearOverlayGroup('projects')
        this.clearOverlayGroup('mapLibraries')
        this.clearOverlayGroup('fixedPoints')

        projects.forEach(project => {
          const coords = project?.geometry?.coordinates
          if (!Array.isArray(coords) || !Array.isArray(coords[0])) return
          const path = coords[0].map(p => ({ lat: Number(p[1]), lng: Number(p[0]) }))
          if (path.length < 3) return

          const polygon = new window.google.maps.Polygon({
            paths: path,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 1.5,
            fillColor: '#FF0000',
            fillOpacity: 0.18,
            map: this.layerVisibility.projects ? this.map : null,
          })
          this.overlayGroups.projects.push(polygon)
        })

        mapLibraries.forEach(item => {
          const coords = item?.geometry?.coordinates || item?.geometry?.rings || item?.polygon
          const ring = Array.isArray(coords) && Array.isArray(coords[0]) && Array.isArray(coords[0][0])
            ? coords[0]
            : coords
          if (!Array.isArray(ring) || ring.length < 3) return

          const polygon = new window.google.maps.Polygon({
            paths: ring.map(p => ({ lat: Number(p[1]), lng: Number(p[0]) })),
            strokeColor: '#FF8C00',
            strokeOpacity: 0.8,
            strokeWeight: 1.5,
            fillColor: '#FFA500',
            fillOpacity: 0.14,
            map: this.layerVisibility.mapLibraries ? this.map : null,
          })
          this.overlayGroups.mapLibraries.push(polygon)
        })

        fixedPoints.forEach(point => {
          const lat = Number(point?.attributes?.latitude || point?.latitude)
          const lng = Number(point?.attributes?.longitude || point?.longitude)
          if (!lat || !lng || Number.isNaN(lat) || Number.isNaN(lng)) return

          const marker = new window.google.maps.Marker({
            position: { lat, lng },
            map: this.layerVisibility.fixedPoints ? this.map : null,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: '#0099FF',
              fillOpacity: 0.85,
              strokeColor: '#FFFFFF',
              strokeWeight: 1.5,
            },
          })
          this.overlayGroups.fixedPoints.push(marker)
        })
        this.finishLayerLoads(layerKeys, { success: true, notifyOnError: false })
      } catch (error) {
        console.error('Failed to load map all-data:', error)
        const errorMessage = error?.response?.data?.message || 'فشل تحميل بيانات المشاريع ومكتبات الخرائط'
        this.finishLayerLoads(layerKeys, { success: false, errorMessage })
      }
    },
    async loadAllAdministrativeBoundaryTypes({ force = false } = {}) {
      if (!this.map || !this.canViewAdministrativeBoundaries) return
      await Promise.all(
        ADMINISTRATIVE_BOUNDARY_TYPES.map(item => (
          this.loadAdministrativeBoundaryType(item.type, { force })
        )),
      )
    },
    async loadAdministrativeBoundaries({ force = false, type = null } = {}) {
      if (type) {
        await this.loadAdministrativeBoundaryType(type, { force })
        return
      }
      await this.loadAllAdministrativeBoundaryTypes({ force })
    },
    async loadAdministrativeBoundaryType(type, { force = false } = {}) {
      if (!this.map || !this.canViewAdministrativeBoundaries || !type) return

      const groupKey = administrativeBoundaryLayerKey(type)
      this.beginLayerLoad([groupKey])
      try {
        const year = this.administrativeBoundariesYear || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR
        const featureCollection = await fetchAdministrativeBoundariesGeoJson(year, { force, type })
        const items = normalizeAdministrativeBoundaryItems(featureCollection)
        const meta = getBoundaryTypeMeta(type)
        const resolveStyle = createAdministrativeBoundaryGoogleStyleResolver(type)

        this.clearOverlayGroup(groupKey)

        items.forEach(item => {
          const rings = ringsFromGeoJsonGeometry(item.geometry)
          const outer = rings[0]
          if (!Array.isArray(outer) || outer.length < 3) return

          const paths = rings
            .filter(ring => Array.isArray(ring) && ring.length >= 3)
            .map(ring => ring.map(p => ({ lat: Number(p[1]), lng: Number(p[0]) })))

          const attributes = flattenAdministrativeBoundaryAttributes(item.attributes || {}, meta)
          const polygon = new window.google.maps.Polygon({
            paths,
            ...resolveStyle(item.attributes),
            map: this.layerVisibility[groupKey] ? this.map : null,
          })

          const infoWindow = new window.google.maps.InfoWindow({
            content: buildAdministrativeBoundaryPopupHtml(attributes, {
              fallbackTitle: meta?.title || type,
            }),
          })
          polygon.addListener('click', event => {
            infoWindow.setPosition(event.latLng)
            infoWindow.open(this.map)
          })

          if (!this.overlayGroups[groupKey]) this.$set(this.overlayGroups, groupKey, [])
          this.overlayGroups[groupKey].push(polygon)
        })
        this.finishLayerLoad(groupKey, { success: true, notifyOnError: false })
      } catch (error) {
        console.error(`Failed to load administrative boundaries (${type}):`, error)
        this.finishLayerLoad(groupKey, {
          success: false,
          errorMessage: error?.response?.data?.message || 'فشل تحميل الحدود الإدارية',
        })
      }
    },
    async loadVrsData() {
      return this.loadGeodesiaMapLayer('vrsCoordinates')
    },
    async loadJxlData() {
      const visibleKeys = GEODESIA_MAP_LAYER_KEYS.filter(key => (
        key !== 'vrsCoordinates' && (this.layerVisibility[key] || this.isLayerLoaded(key))
      ))
      const keys = visibleKeys.length ? visibleKeys : ['jxlCoordinates']
      await Promise.all(keys.map(key => this.loadGeodesiaMapLayer(key, { force: true })))
    },
    cancelGeodesiaLayerRequest(layerKey) {
      const source = this.geodesiaLayerCancels?.[layerKey]
      if (!source) return
      source.cancel('layer-toggled-off')
      this.$delete(this.geodesiaLayerCancels, layerKey)
    },
    cancelAllGeodesiaLayerRequests() {
      Object.keys(this.geodesiaLayerCancels || {}).forEach(key => {
        this.cancelGeodesiaLayerRequest(key)
      })
    },
    notifyGeodesiaLayerTruncated(payload) {
      if (!isGeodesiaLayerTruncated(payload) || !this.$toast?.warning) return
      this.$toast.warning(GEODESIA_LAYER_TRUNCATED_MESSAGE)
    },
    async loadGeodesiaMapLayer(layerKey, { force = false } = {}) {
      if (!this.map || !GEODESIA_MAP_LAYER_KEYS.includes(layerKey)) return
      if (!force && (this.isLayerLoading(layerKey) || this.isLayerLoaded(layerKey))) return

      this.cancelGeodesiaLayerRequest(layerKey)
      this.beginLayerLoad([layerKey])
      this.clearOverlayGroup(layerKey)

      const cancelSource = createGeodesiaLayerCancelSource()
      this.$set(this.geodesiaLayerCancels, layerKey, cancelSource)

      try {
        const response = await geodesiaApi.getGeodesiaMapLayer(layerKey, {}, { cancelToken: cancelSource.token })
        const payload = unwrapGeodesiaLayerPayload(response)
        const features = getGeodesiaLayerFeatures(payload, layerKey)

        if (layerKey === 'jxlPolygons') {
          this.drawGeodesiaGooglePolygons(layerKey, features)
        } else if (layerKey === 'jxlBaselineLines') {
          this.drawGeodesiaGoogleBaselines(layerKey, features)
        } else {
          this.drawGeodesiaGooglePoints(layerKey, features, GEODESIA_LAYER_SOURCE[layerKey] || 'vrs')
        }

        this.notifyGeodesiaLayerTruncated(payload)
        this.finishLayerLoad(layerKey, { success: true, notifyOnError: false })
      } catch (error) {
        if (isGeodesiaLayerRequestCanceled(error)) {
          this.$set(this.layerLoadState, layerKey, {
            loading: false,
            loaded: false,
            error: null,
          })
          return
        }
        console.error(`Failed to load ${layerKey} data:`, error)
        this.finishLayerLoad(layerKey, {
          success: false,
          errorMessage: geodesiaLayerErrorMessage(error, layerKey) || GEODESIA_LAYER_ERROR_MESSAGE[layerKey],
        })
      } finally {
        if (this.geodesiaLayerCancels[layerKey] === cancelSource) {
          this.$delete(this.geodesiaLayerCancels, layerKey)
        }
      }
    },
    drawGeodesiaGooglePoints(groupName, points, sourceKey) {
      if (!this.map || !window.google?.maps || !Array.isArray(points)) return
      if (!Array.isArray(this.overlayGroups[groupName])) {
        this.$set(this.overlayGroups, groupName, [])
      }
      const visible = !!this.layerVisibility[groupName]
      const isVrs = sourceKey === 'vrs'
      points.forEach(point => {
        const coordinates = point?.geometry?.coordinates
        if (!Array.isArray(coordinates) || coordinates.length < 2) return
        const lng = Number(coordinates[0])
        const lat = Number(coordinates[1])
        if (!this.isValidMapCoordinate(lat, lng)) return
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: visible ? this.map : null,
          icon: isVrs
            ? {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: '#E27728',
              fillOpacity: 0.8,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            }
            : getSurveyPointGoogleIcon(window.google.maps, sourceKey),
        })
        this.overlayGroups[groupName].push(marker)
      })
    },
    drawGeodesiaGooglePolygons(groupName, polygons) {
      if (!this.map || !window.google?.maps || !Array.isArray(polygons)) return
      if (!Array.isArray(this.overlayGroups[groupName])) {
        this.$set(this.overlayGroups, groupName, [])
      }
      const visible = !!this.layerVisibility[groupName]
      polygons.forEach(feature => {
        const rings = feature?.geometry?.coordinates
        if (!Array.isArray(rings) || !Array.isArray(rings[0]) || rings[0].length < 3) return
        const path = rings[0]
          .filter(coord => Array.isArray(coord) && coord.length >= 2)
          .map(coord => ({ lat: Number(coord[1]), lng: Number(coord[0]) }))
          .filter(coord => this.isValidMapCoordinate(coord.lat, coord.lng))
        if (path.length < 3) return
        const color = feature?.attributes?.color || '#00C800'
        const polygon = new window.google.maps.Polygon({
          paths: path,
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.25,
          map: visible ? this.map : null,
        })
        this.overlayGroups[groupName].push(polygon)
      })
    },
    drawGeodesiaGoogleBaselines(groupName, lines) {
      if (!this.map || !window.google?.maps || !Array.isArray(lines)) return
      if (!Array.isArray(this.overlayGroups[groupName])) {
        this.$set(this.overlayGroups, groupName, [])
      }
      const visible = !!this.layerVisibility[groupName]
      lines.forEach(feature => {
        const coords = feature?.geometry?.coordinates
        if (!Array.isArray(coords) || coords.length < 2) return
        const path = coords
          .filter(coord => Array.isArray(coord) && coord.length >= 2)
          .map(coord => ({ lat: Number(coord[1]), lng: Number(coord[0]) }))
          .filter(coord => this.isValidMapCoordinate(coord.lat, coord.lng))
        if (path.length < 2) return
        const polyline = new window.google.maps.Polyline({
          path,
          strokeColor: feature?.attributes?.color || '#FF0000',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          map: visible ? this.map : null,
        })
        this.overlayGroups[groupName].push(polyline)
      })
    },
    importedPointGroupKey(type = '') {
      const key = String(type || '').toLowerCase() || 'unknown'
      return `importedPoints:${key}`
    },
    listImportedPointGroups() {
      return Object.keys(this.overlayGroups || {}).filter(name => (
        name === 'importedPoints' || String(name).startsWith('importedPoints:')
      ))
    },
    rebuildImportedPointsIndex() {
      this.overlayGroups.importedPoints = this.listImportedPointGroups()
        .filter(name => name !== 'importedPoints')
        .flatMap(name => this.overlayGroups[name] || [])
    },
    clearImportedPoints(fileType) {
      const type = String(fileType || '').toLowerCase()
      if (type) {
        this.clearOverlayGroup(this.importedPointGroupKey(type))
        this.rebuildImportedPointsIndex()
        return
      }
      this.listImportedPointGroups().forEach(name => this.clearOverlayGroup(name))
    },
    plotImportedPoints(points, fileType, options = {}) {
      const { fitToPoints = true } = options
      if (!this.map || !window.google?.maps) return
      const type = String(fileType || '').toLowerCase()
      const groupName = this.importedPointGroupKey(type)
      this.clearImportedPoints(type)
      if (!Array.isArray(this.overlayGroups[groupName])) {
        this.$set(this.overlayGroups, groupName, [])
      }

      if (!this.geodesiaPointInfoWindow) {
        this.geodesiaPointInfoWindow = new window.google.maps.InfoWindow()
      }

      const validPoints = []
      ;(points || []).forEach((point, index) => {
        const lat = Number(point?.latitude)
        const lng = Number(point?.longitude)
        if (!this.isValidMapCoordinate(lat, lng)) return

        const displayNumber = point.displayNumber || point.label || String(index + 1)
        validPoints.push({ latitude: lat, longitude: lng, displayNumber })

        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: this.map,
          title: `نقطة ${displayNumber}`,
          icon: getSurveyPointGoogleIcon(window.google.maps, type),
        })
        marker.addListener('click', event => {
          if (this.consumeOverlayClickForActiveTool(event)) return
          this.geodesiaPointInfoWindow.setContent(`<strong>نقطة ${displayNumber}</strong>`)
          this.geodesiaPointInfoWindow.open(this.map, marker)
        })
        this.overlayGroups[groupName].push(marker)
      })
      this.rebuildImportedPointsIndex()

      if (fitToPoints) {
        this.fitMapViewToImportedPoints(validPoints)
      }
    },
    fitMapViewToImportedPoints(points) {
      if (!this.map || !window.google?.maps || !points?.length) return

      if (points.length === 1) {
        this.map.setCenter({
          lat: Number(points[0].latitude),
          lng: Number(points[0].longitude),
        })
        this.map.setZoom(18)
        return
      }

      const bounds = new window.google.maps.LatLngBounds()
      points.forEach(point => {
        bounds.extend({
          lat: Number(point.latitude),
          lng: Number(point.longitude),
        })
      })
      this.map.fitBounds(bounds, 48)
    },
    setImportedPointsVisible(visible) {
      this.setOverlayGroupVisibility('importedPoints', !!visible)
    },
    createPolygonFromImportedPoints(points) {
      const normalizedInput = (points || []).map(point => ({
        latitude: point?.latitude,
        longitude: point?.longitude,
        type: point?.type || this.coordinatesType,
        x: point?.x ?? point?.longitude,
        y: point?.y ?? point?.latitude,
      }))
      const coords = this.normalizeCoordinatesList(normalizedInput)
      if (coords.length < 3 || coords.length !== normalizedInput.length) return false

      this.suppressPropRedrawUntil = Date.now() + 8000
      this.pendingPolygonCoordinates = coords
      this.drawPolygon(coords)
      this.$nextTick(() => {
        this.focusCurrentPolygon()
        this.enableParcelEdit()
      })
      this.$emit('coordinates-changed', [...coords])
      return true
    },
  },
}
</script>

<style scoped>
.coordinates-map-wrapper {
  position: relative;
  width: 100%;
}

.map-container-wrapper {
  position: relative;
}

.coordinates-google-map {
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 8px;
}

.coordinates-google-map:fullscreen,
.coordinates-google-map:-webkit-full-screen,
.map-container-wrapper:fullscreen,
.map-container-wrapper:-webkit-full-screen {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  overflow: visible;
}

.coordinates-google-map:fullscreen,
.coordinates-google-map:-webkit-full-screen {
  aspect-ratio: auto;
  border-radius: 0;
}

:fullscreen .map-container-wrapper,
:-webkit-full-screen .map-container-wrapper,
:fullscreen .coordinates-map-wrapper,
:-webkit-full-screen .coordinates-map-wrapper {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

:fullscreen .coordinates-google-map,
:-webkit-full-screen .coordinates-google-map {
  flex: 1;
  width: 100% !important;
  height: 100% !important;
  min-height: 0 !important;
  max-height: none !important;
  aspect-ratio: auto;
  border-radius: 0;
}

@media (max-width: 767.98px) {
  .coordinates-map-wrapper {
    max-width: 100%;
    overflow: hidden;
  }

  .map-container-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .coordinates-google-map {
    aspect-ratio: auto;
    width: 100%;
    max-width: 100%;
    height: clamp(320px, 72dvh, 640px);
    min-height: 320px;
    border-radius: 4px;
  }

  .coordinates-map-wrapper--popup {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .coordinates-map-wrapper--popup .map-container-wrapper {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .coordinates-map-wrapper--popup .coordinates-google-map {
    flex: 1;
    height: calc(100dvh - 10rem);
    min-height: calc(100dvh - 10rem);
    max-height: none;
  }
}

@media (max-width: 575.98px) {
  .coordinates-map-wrapper:not(.coordinates-map-wrapper--popup) .coordinates-google-map {
    height: clamp(300px, 68dvh, 540px);
    min-height: 300px;
  }

  .coordinates-map-wrapper--popup .coordinates-google-map {
    height: calc(100dvh - 9rem);
    min-height: calc(100dvh - 9rem);
  }
}

.map-layer-status-panel {
  position: absolute;
  left: 8px;
  bottom: 8px;
  top: auto;
  z-index: 4;
  min-width: 180px;
  max-width: min(260px, calc(100% - 16px));
  padding: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.map-layer-status-panel__list {
  padding: 4px 10px 8px;
}

.map-layer-status-panel__list {
  padding: 4px 10px 8px;
}

.map-layer-status-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: #374151;
  padding: 2px 0;
}

.map-layer-status-item__icon {
  width: 14px;
  text-align: center;
  flex-shrink: 0;
}

.map-layer-status-item__icon--success {
  color: #16a34a;
  font-weight: 700;
}

.map-layer-status-item__icon--error {
  color: #dc2626;
  font-weight: 700;
}

.map-layer-status-item__icon--pending {
  color: #9ca3af;
}

.map-layer-status-item--error .map-layer-status-item__label {
  color: #b91c1c;
}

.map-layer-status-item--clickable {
  cursor: pointer;
}

.map-layer-status-item--clickable:hover .map-layer-status-item__label {
  color: #2563eb;
  text-decoration: underline;
}

.map-layer-status-item__label {
  line-height: 1.2;
}
</style>

<style>
.google-map-control-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 4px;
  padding: 0;
  border: none;
  border-radius: 2px;
  background: #fff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
  cursor: pointer;
}

.google-map-control-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.google-sketch-toolbar {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin: 10px;
  overflow: visible;
  z-index: 5;
  zoom: 0.75;
}

@supports not (zoom: 1) {
  .google-sketch-toolbar {
    transform: scale(0.75);
    transform-origin: top right;
  }
}

.google-sketch-toolbar__group {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
}

.google-sketch-toolbar__divider {
  width: 1px;
  align-self: stretch;
  margin: 4px 2px;
  background: #d1d5db;
}

.google-sketch-tool__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #323232;
  line-height: 0;
}

.google-sketch-tool--active,
.google-sketch-layers-button.google-sketch-tool--active {
  background: #e8f1ff;
}

.google-sketch-layers-panel {
  z-index: 6;
  min-width: 210px;
  max-width: min(280px, calc(100vw - 24px));
  max-height: min(70dvh, 420px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 10px;
  padding: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/*! rtl:begin:ignore */
.google-sketch-layers-panel.is-moved {
  position: absolute !important;
  margin: 0;
  left: 0;
  top: 0;
  right: auto !important;
  bottom: auto !important;
  inset-inline-start: auto !important;
  inset-inline-end: auto !important;
}
/*! rtl:end:ignore */

.google-sketch-layers-panel.is-dragging {
  box-shadow: 0 14px 32px rgba(10, 51, 69, 0.22);
}

.google-sketch-layers-panel[hidden] {
  display: none !important;
}

.google-sketch-layers-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  flex-shrink: 0;
  padding: 8px 10px;
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.google-sketch-layers-panel.is-dragging .google-sketch-layers-panel__head {
  cursor: grabbing;
}

.google-sketch-layers-panel__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  padding: 0;
  border: 1px solid rgba(10, 51, 69, 0.16);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.8);
  color: #0a3345;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}

.google-sketch-layers-panel__body {
  min-height: 0;
  overflow-y: auto;
  padding: 0 10px 8px;
}

.google-sketch-layers-panel.is-collapsed {
  max-height: none;
}

.google-draw-history-panel {
  position: absolute;
  z-index: 45;
  /*! rtl:begin:ignore */
  left: 0.5rem;
  right: auto;
  /*! rtl:end:ignore */
  bottom: 0.75rem;
  width: 11.75rem;
  max-width: min(12rem, calc(100% - 1rem));
  max-height: 10.75rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 6px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.16);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  pointer-events: auto;
}

.google-draw-history-panel--docked,
.google-draw-history-panel.google-draw-history-panel--docked {
  position: relative;
  left: auto;
  right: auto;
  bottom: auto;
  z-index: 1;
  width: 100%;
  max-width: none;
  max-height: 9rem;
  margin: 0;
  border-radius: 0;
  box-shadow: none;
  border: 0;
  border-bottom: 1px solid rgba(10, 51, 69, 0.08);
  background: rgba(255, 255, 255, 0.28);
}

.google-draw-history-fab--docked,
.google-draw-history-fab.google-draw-history-fab--docked {
  position: relative;
  left: auto;
  right: auto;
  bottom: auto;
  z-index: 1;
  margin: 0.3rem;
  width: calc(100% - 0.6rem);
}

.google-draw-history-panel.is-hidden {
  display: none !important;
}

.google-draw-history-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  margin-bottom: 4px;
}

.google-draw-history-panel__title {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  color: #4b5563;
}

.google-draw-history-panel__toggle {
  display: none;
  flex-shrink: 0;
  padding: 0.12rem 0.4rem;
  border: 1px solid rgba(10, 51, 69, 0.16);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.8);
  color: #0a3345;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
}

.google-draw-history-panel__body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.google-draw-history-fab {
  display: none;
  position: absolute;
  z-index: 45;
  /*! rtl:begin:ignore */
  left: 0.5rem;
  right: auto;
  /*! rtl:end:ignore */
  bottom: 0.7rem;
  padding: 0.38rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: #0a3345;
  font-size: 0.78rem;
  font-weight: 700;
  box-shadow: 0 6px 18px rgba(10, 51, 69, 0.18);
  cursor: pointer;
  pointer-events: auto;
}

.google-draw-history-fab.is-visible {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.google-draw-history-panel__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 6px;
}

.google-draw-history-panel__actions .google-map-control-button {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  min-height: 2rem;
  margin: 0;
  flex: 1 1 0;
}

.google-draw-history-panel__list {
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

@media (max-width: 1199.98px) {
  .google-draw-history-panel {
    width: 10.6rem;
    max-height: 9.75rem;
    bottom: 0.7rem;
  }
}

@media (max-width: 767.98px) {
  .google-draw-history-panel {
    width: min(11.5rem, calc(100% - 1rem));
    max-height: min(32dvh, 11.5rem);
    bottom: 0.7rem;
  }

  .google-draw-history-panel__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .google-draw-history-panel__actions .google-map-control-button {
    width: 1.85rem;
    height: 1.85rem;
    min-width: 1.85rem;
    min-height: 1.85rem;
  }
}

.google-draw-history-panel__empty,
.google-draw-history-panel__item {
  width: 100%;
  padding: 0.28rem 0.4rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #1f2937;
  font-size: 0.72rem;
  text-align: start;
}

.google-draw-history-panel__item {
  cursor: pointer;
  background: rgba(255, 255, 255, 0.45);
}

.google-draw-history-panel__item.is-current {
  background: rgba(37, 99, 235, 0.16);
  font-weight: 700;
}

.google-sketch-tool--undo:disabled,
.google-sketch-tool--redo:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.google-sketch-layers-panel__title {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #4b5563;
}

.google-sketch-layers-panel__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 3px 0;
  font-size: 12px;
  color: #1f2937;
  cursor: pointer;
}

.google-fullscreen-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #323232;
  line-height: 0;
}

.google-fullscreen-button--active {
  background: #e8f1ff;
}

.admin-boundaries-upload-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #005aa0;
  line-height: 0;
}

@media (max-width: 767.98px) {
  .google-map-control-button {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
  }
}

.draw-polygon-button--active {
  background: #e8f1ff;
}

.measure-distance-button--active {
  background: #fff3e0;
}

.measure-area-button--active {
  background: #f3e5f5;
}

.measure-distance-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #e65100;
  line-height: 0;
}

.measure-area-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6a1b9a;
  line-height: 0;
}

.delete-selection-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #b91c1c;
  line-height: 0;
}

.google-sketch-tool--delete:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.measure-info-window {
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
  padding: 2px 4px;
}

.measure-live-label-control {
  margin: 10px;
  padding: 8px 14px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
  font-size: 14px;
  line-height: 1.4;
  pointer-events: none;
}

.gm-style-iw:has(.measure-info-window) .gm-style-iw-chr,
.gm-style-iw-c:has(.measure-info-window) .gm-style-iw-chr,
.gm-style-iw:has(.google-closable-info) .gm-style-iw-chr,
.gm-style-iw-c:has(.google-closable-info) .gm-style-iw-chr {
  display: none !important;
}

.google-finish-polygon-button {
  margin: 0 0 12px;
  padding: 0.7rem 1.35rem;
  min-height: 44px;
  min-width: 9.5rem;
  border: 0;
  border-radius: 999px;
  background: #0a3345;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  box-shadow: 0 8px 22px rgba(10, 51, 69, 0.28);
  cursor: pointer;
  pointer-events: auto;
}

.google-finish-polygon-button[hidden] {
  display: none !important;
}

.google-finish-polygon-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.google-dimension-label {
  position: absolute;
  z-index: 3;
  transform-origin: center center;
  pointer-events: none;
  white-space: nowrap;
  font: bold 11px Arial, sans-serif;
  color: #141414;
  text-shadow:
    -1px -1px 0 #fff,
    1px -1px 0 #fff,
    -1px 1px 0 #fff,
    1px 1px 0 #fff,
    0 0 3px #fff;
}

.google-dimension-label--area {
  color: #0846a0;
  font-size: 12px;
}

.google-closable-info {
  position: relative;
  min-width: 9rem;
  padding: 0.1rem 1.35rem 0.1rem 0.1rem;
  pointer-events: auto;
}

.google-closable-info__close {
  position: absolute;
  top: -0.15rem;
  /*! rtl:begin:ignore */
  right: -0.2rem;
  left: auto;
  /*! rtl:end:ignore */
  width: 1.35rem;
  height: 1.35rem;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #4b5563;
  font-size: 1.15rem;
  line-height: 1;
  cursor: pointer;
}

.google-closable-info__close:hover {
  color: #111827;
  background: rgba(0, 0, 0, 0.06);
}

.gm-style-iw-d {
  overflow: hidden !important;
}

.draw-polygon-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0d9488;
  line-height: 0;
}

.save-polygon-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  line-height: 0;
}

.save-as-new-location-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
  line-height: 0;
}

.save-as-new-location-button--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.save-polygon-button--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.save-polygon-button--loading {
  pointer-events: none;
  opacity: 0.8;
}

.home-location-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #323232;
  line-height: 0;
}

.home-location-button--loading {
  pointer-events: none;
  opacity: 0.8;
}

.home-location-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #3498db;
  border-top-color: transparent;
  border-radius: 50%;
  box-sizing: border-box;
  animation: home-location-spin 1s linear infinite;
}

@keyframes home-location-spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.imagery-year-control {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  margin: 10px;
  margin-top: 4px;
  padding: 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
}

.imagery-year-control__label {
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: #374151;
  white-space: nowrap;
}

.imagery-year-control__button {
  min-width: 44px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  background: #fff;
  color: #374151;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}

.imagery-year-control__button--active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}

.imagery-year-control__button:hover {
  background: #f9fafb;
}

.imagery-year-control__button--active:hover {
  background: #dbeafe;
}

@media (max-width: 767.98px) {
  .imagery-year-control {
    margin: 6px;
    padding: 3px;
  }

  .imagery-year-control__label {
    display: none;
  }

  .imagery-year-control__button {
    min-width: 36px;
    height: 24px;
    padding: 0 6px;
    font-size: 10px;
  }
}

.google-admin-boundary-layers {
  margin: 10px;
  padding: 8px 10px;
  max-width: 260px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
  font-size: 12px;
  color: #1f2937;
}

.google-admin-boundary-layers__title {
  margin-bottom: 6px;
  font-weight: 700;
}

.google-admin-boundary-layers__row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  cursor: pointer;
}

.google-admin-boundary-layers__row + .google-admin-boundary-layers__row {
  margin-top: 4px;
}

.coordinates-map-wrapper ::v-deep .save-polygon-choice-modal {
  z-index: 1000010;
}

.coordinates-map-wrapper ::v-deep .save-polygon-choice-modal .modal-dialog {
  max-width: 260px;
  margin: 0.5rem auto;
}

.coordinates-map-wrapper ::v-deep .save-polygon-choice-modal .modal-content {
  border-radius: 8px;
}

.coordinates-map-wrapper ::v-deep .save-polygon-choice-modal .modal-body {
  padding: 0.6rem 0.65rem;
}

.coordinates-map-wrapper ::v-deep .save-polygon-choice-modal__msg {
  margin: 0 0 0.3rem;
  font-size: 0.8125rem;
  line-height: 1.35;
}

.coordinates-map-wrapper ::v-deep .save-polygon-choice-modal__area {
  margin: 0 0 0.45rem;
  font-size: 0.7rem;
  color: #6c757d;
  line-height: 1.3;
}

.coordinates-map-wrapper ::v-deep .save-polygon-choice-modal__actions {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.coordinates-map-wrapper ::v-deep .save-polygon-choice-modal__actions .btn {
  width: 100%;
  font-size: 0.75rem;
  padding: 0.2rem 0.45rem;
}
</style>
